import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { useQuery, useMutation, useQueryClient, type QueryClient, type UseMutationOptions, type UseQueryOptions, type MutationFunction, type UseMutationResult, type UseQueryResult } from "react-query";
export type AbsenceNoticeResponse = {
    entityId: string;
    start: string;
    end: string;
    employee?: EmployeeResponse;
    reason: string;
    createdOn: string;
    status: AbsenceNoticeStatus;
};
export type AbsenceNoticeStatus = "RECEIVED" | "PROCESSED";
export type AbsenceResponse = {
    start: string;
    end: string;
    type: "UNSPECIFIED" | "LEAVE" | "ILLNESS";
};
export type ActivityGroupType = "OTHER" | "SUGGESTED";
export type ActivityResponse = {
    entityId: string;
    name: string;
    code: string;
    description?: string;
    requiredQualifications: QualificationResponse[];
    schedulingLabel: SchedulingLabel;
    clientRelatedType?: ClientRelatedType;
    isFavorite?: boolean;
    validFrom?: string;
    validTo?: string;
    allowedFunctionIds?: string[];
};
export type ActivitySummary = {
    name?: string;
    code?: string;
    id?: string;
};
export type AdditionalCompensationResponse = {
    employment: EmploymentResponse;
    dutyStart?: string;
    dutyEnd?: string;
    additionalCompensationType: AdditionalCompensationType;
    byWorkPattern: boolean;
    markedAsAdditionalCompensation?: boolean;
    mutatedOn: string;
    mutatedBy: string;
};
export type AdditionalCompensationType = "CREATED" | "UPDATED";
export type AddressRequest = {
    streetName?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    countryCode?: string;
};
export type AddressResponse = {
    streetName?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
};
export type AppointmentContext = {
    appointmentSummary?: AppointmentSummary;
};
export type AppointmentExceptionChangeType = "DELETED" | "UPDATED";
export type AppointmentExceptionResponse = {
    appointmentExceptionId?: string;
    originalAppointmentId?: string;
    date?: string;
    changeType?: AppointmentExceptionChangeType;
    updatedAppointment?: AppointmentResponse;
};
export type AppointmentGroupingResponse = {
    entityId: string;
    code: string;
    description: string;
    start: string;
    end: string;
    durationInSeconds: number;
    organizationalUnit?: OrganizationalUnitOnDateResponse;
};
export type AppointmentGroupingTransportationModeRequest = {
    transportationMode?: TransportationMode;
};
export type AppointmentImportSyncStatusResponse = {
    appointmentEntityId?: string;
    status?: SynchronizationStatus;
    lastSyncedAt?: string | null;
    validationMessages?: string | null;
    externalId?: string | null;
    appointmentContext?: AppointmentContext;
};
export type AppointmentImportSyncStatusSummaryResponse = {
    status?: SynchronizationStatus;
    lastSyncedAt?: string | null;
    validationMessages?: string | null;
};
export type AppointmentOccurrenceRequest = {
    entityId?: string;
    originalOccurrenceStart?: string;
    schedulingLabel?: SchedulingLabel;
};
export type AppointmentResponse = {
    entityId?: string;
    description?: string;
    start?: string;
    durationInSeconds?: number;
    recurrenceRule?: RecurrenceRuleResponse;
    recurrenceRuleEnd?: string;
    originalAppointmentId?: string;
    previousAppointmentId?: string;
    timeWindow?: TimeWindowResponse;
    workPlace?: WorkPlaceResponse;
    project?: ProjectResponse;
    activity?: ActivityResponse;
    category?: EventCategory;
    clients?: ClientResponse[];
    employeeType?: EmployeeTypeResponse;
    employees?: EmployeeResponse[];
    resources?: ResourceResponse[];
    organizationalUnit?: OrganizationalUnitOnDateResponse;
    appointmentExceptions?: AppointmentExceptionResponse[];
    createdById?: number;
    createdByName?: string;
    createdOn?: string;
    lastUpdatedById?: number;
    lastUpdatedByName?: string;
    lastUpdatedOn?: string;
};
export type AppointmentSeriesResponse = {
    appointments?: AppointmentResponse[];
};
export type AppointmentSummary = {
    id?: string;
    employees?: ParticipantSummary[];
    clients?: ParticipantSummary[];
    activity?: ActivitySummary;
    startDateTime?: string;
    endDateTime?: string;
    createdById?: number;
    createdByName?: string;
    createdOn?: string;
    lastUpdatedById?: number;
    lastUpdatedByName?: string;
    lastUpdatedOn?: string;
    deleted?: boolean;
};
export type AppointmentType = "SERIES_INSTANCE" | "SERIES_EXCEPTION" | "SINGLE_OCCURRENCE" | "DEFAULT";
export type ApproveAppointmentsRequest = {
    appointmentEntityIds?: string[];
};
export type ApproveExpenseClaimsRequest = {
    expenseClaimIds?: string[];
};
export type AskEmployeeForDutyRequest = {
    dutyId: string;
    employeeId: string;
    responseActionType?: ResponseActionType;
};
export type AskEmployeesForDutyRequest = {
    dutyId: string;
    employeeIds: string[];
    responseActionType?: ResponseActionType;
};
export type AskRoundSettingsResponse = {
    minutesBeforeStart: number;
    minimalMinutesOfAdvantageBeforeNext: number;
    includeOwn: boolean;
    includeShared: boolean;
    includePreference1: boolean;
    includePreference2: boolean;
    includeFlexWithPreference1: boolean;
    includeFlexWithPreference2: boolean;
    includeFlex: boolean;
    includeDeployableAsFlexWithPreference1: boolean;
    includeDeployableAsFlexWithPreference2: boolean;
    includeDeployableAsFlex: boolean;
    includeFunctionPreference1: boolean;
    includeFunctionPreference2: boolean;
    overlapMargin: number;
    responseActionType: ResponseActionType;
};
export type AskedDutyResponse = {
    duty: DutyResponse;
    rounds?: AskedDutyRoundResponse[];
    numberOfAskedEmployees: number;
    numberOfNotifiedEmployees: number;
    numberOfRespondedEmployees: number;
    triggeredAt?: string;
    status: AskedDutyStatus;
};
export type AskedDutyRoundResponse = {
    id: string;
    askedEmployees: AskedEmployeeResponse[];
    numberOfAskedEmployees: number;
    numberOfNotifiedEmployees: number;
    numberOfRespondedEmployees: number;
    createdAt: string;
    dueAt: string;
    finalDueAt: string;
    processedAt: string;
    settings: AskedDutyRoundSettingsResponse;
    status: AskedDutyRoundStatus;
};
export type AskedDutyRoundSettingsResponse = {
    minutesBeforeStart: number;
    minimalMinutesOfAdvantageBeforeNext: number;
    overlapMargin: number;
    responseActionType: ResponseActionType;
    includeOwn: boolean;
    includeShared: boolean;
    includePreference1: boolean;
    includePreference2: boolean;
    includeFlexWithPreference1: boolean;
    includeFlexWithPreference2: boolean;
    includeFlex: boolean;
    includeDeployableAsFlexWithPreference1: boolean;
    includeDeployableAsFlexWithPreference2: boolean;
    includeDeployableAsFlex: boolean;
    includeFunctionPreference1: boolean;
    includeFunctionPreference2: boolean;
};
export type AskedDutyRoundStatus = "PLANNED" | "PROCESSED" | "CANCELLED";
export type AskedDutyStatus = "OPEN" | "ASKED" | "PLANNED" | "ONE_REPLIED_AVAILABLE" | "ALL_REPLIED_NOT_AVAILABLE" | "FAILED" | "PARKED";
export type AskedEmployeeResponse = {
    id: string;
    employee: EmployeeResponse;
    responseActionType: ResponseActionType;
    status: AskedEmployeeStatus;
    askedAt: string;
    remark?: string;
    notifiedAt?: string;
    respondedAt?: string;
    response?: AskedEmployeeResponseType;
    responseRemark?: string;
};
export type AskedEmployeeResponseType = "AVAILABLE" | "NOT_AVAILABLE";
export type AskedEmployeeStatus = "ASKED" | "NOTIFIED" | "RESPONDED_AVAILABLE" | "RESPONDED_NOT_AVAILABLE" | "CANCELLED";
export type AssignEmploymentToAppointmentGroupingRequest = {
    date: string;
    employeeNumber: number;
    employmentNumber: number;
};
export type AssignEmploymentToAppointmentRequest = {
    correlationToken?: string;
    appointmentOccurrence: AppointmentOccurrenceRequest;
    planningEntityIdentifier: PlanningEntityIdentifier;
    editAppointmentType?: EditAppointmentTypeResponse;
    suppressWarnings?: boolean;
};
export type AuditChangeEventResponse = {
    eventId?: string;
    entityId?: string;
    type?: "APPOINTMENT" | "APPOINTMENT_SERIES";
    crudMode?: "CREATE" | "UPDATE" | "DELETE";
    afterState?: {
        [key: string]: any;
    };
    beforeState?: {
        [key: string]: any;
    };
    effectiveChanges?: {
        [key: string]: any;
    };
    createdAt?: string;
};
export type AuditedCommandChangeEventDetailsResponse = {
    commandId?: string;
    accountId?: string;
    type?: string;
    commandDetails?: {
        [key: string]: any;
    };
    createdAt?: string;
    changeEvents?: AuditChangeEventResponse[];
};
export type AuditedCommandSeriesResponse = {
    commands?: AuditedCommandChangeEventDetailsResponse[];
};
export type AvailabilityResponse = {
    entityId?: string;
    start: string;
    end: string;
    type: AvailabilityType;
    participantId?: string;
};
export type AvailabilityType = "AVAILABLE" | "NOT_AVAILABLE" | "POSSIBLE" | "UNSPECIFIED";
export type BulkConnectSchedulesRequest = {
    startDate: string;
    endDate: string;
    organizationalUnitPlanningEntityPerspective: PlanningEntityType;
    planningEntityIdentifiers: PlanningEntityIdentifier[];
    editAppointmentType: EditAppointmentTypeResponse;
    suppressWarnings?: boolean;
    correlationToken?: string;
};
export type BulkDeletionActionAppointmentRequest = {
    appointmentOccurrenceIdentifiers?: AppointmentOccurrenceRequest[];
    editAppointmentType?: EditAppointmentTypeResponse;
    correlationToken?: string;
};
export type BulkEditParticipantsActionAppointmentRequest = {
    appointmentOccurrenceIdentifiers?: AppointmentOccurrenceRequest[];
    addParticipants?: PlanningEntityIdentifier[];
    removeParticipants?: PlanningEntityIdentifier[];
    editAppointmentType?: EditAppointmentTypeResponse;
    correlationToken?: string;
};
export type CalculateFunctionType = "DUTY" | "EMPLOYEE";
export type ClaResponse = {
    claId: number;
    claCode: string;
    claExt?: string;
};
export type ClientRelatedType = "DIRECT_CLIENT_RELATED" | "INDIRECT_CLIENT_RELATED" | "NON_CLIENT_RELATED";
export type ClientResponse = {
    id: string;
    number?: number;
    initials?: string;
    firstName?: string;
    lastName?: string;
    nameWithInitials?: string;
    fullName?: string;
    displayName?: string;
    sourceDisplayName?: string;
    givenName?: string;
    dateOfBirth?: string;
    age?: number;
    base64Image?: string;
    address?: AddressResponse;
    phoneNumbers?: string[];
    keyArrangements?: string[];
    planningFeatures?: string[];
    treatments?: TreatmentResponse[];
};
export type ClosureYearResponse = {
    closureYear?: number;
};
export type CommandResultResponse = {
    notifications?: NotificationResponse[];
    success?: boolean;
};
export type ConfigurationAddPlanningEntitiesRequest = {
    configurationId?: string;
    planningEntities?: ConfigurationPlanningEntity[];
};
export type ConfigurationAddSettingsRequest = {
    configuration: string;
    settings?: ConfigurationSettingRequest[];
};
export type ConfigurationDeletePlanningEntitiesRequest = {
    configuration: string;
    planningEntities?: string[];
};
export type ConfigurationDeleteSettingsRequest = {
    configuration: string;
    settings?: string[];
};
export type ConfigurationPlanningEntity = {
    id?: string;
    type?: PlanningEntityType;
};
export type ConfigurationResponse = {
    id?: string;
    name?: string;
    configurationSettings?: ConfigurationSettingsResponse[];
    planningEntities?: ConfigurationPlanningEntity[];
    isDefault?: boolean;
};
export type ConfigurationSettingRequest = {
    schedulingSetting?: string;
    value?: string;
};
export type ConfigurationSettingsResponse = {
    schedulingSetting?: SchedulingSettingResponse;
    value?: string;
};
export type ConnectPeriodAssignmentRequest = {
    periodAssignmentEntityId?: string;
    employmentEntityId?: string;
};
export type ConnectScheduleRequest = {
    planningEntityIdentifier: PlanningEntityIdentifier;
    scheduleStart: string;
    editAppointmentType?: EditAppointmentTypeResponse;
    suppressWarnings?: boolean;
};
export type ConnectSelfMatchDutiesRequest = {
    organizationalUnitIds?: number[];
    startDate?: string;
    endDate?: string;
};
export type CopyAppointmentsInPeriodRequest = {
    organizationalUnitId?: number;
    employeeEntityId?: string;
    source: Period;
    destination: Period;
    includeEmployees?: boolean;
    weekInterval?: number;
};
export type CorrectTravelMovementPerDayRequest = {
    employmentId: number;
    date: string;
    travelMovementCorrections: TravelMovementCorrectionRequest[];
};
export type CostCalculationExternal = {
    month: number;
    hours: CostCalculationExternalHours[];
    totalHours: number;
    fees: CostCalculationExternalFee[];
    totalFees: number;
    subTotalFees: number;
    travelExpenses: CostCalculationExternalTravelExpenses[];
    totalKm: number;
    totalTravelExpenses: number;
    vat: CostCalculationExternalVat[];
    totalVat: number;
    total: number;
};
export type CostCalculationExternalFee = {
    feePercentage: number;
    feeOfTotal: number;
    total: number;
};
export type CostCalculationExternalHours = {
    description: string;
    hours: number;
    baseRate: number;
    percentage: number;
    feePercentage: number;
    vatPercentage: number;
    totalHours: number;
    totalFee: number;
    totalVat: number;
    total: number;
};
export type CostCalculationExternalRecalculate = {
    employmentId?: number;
    organizationalUnitId?: number;
    startDate: string;
    endDate: string;
};
export type CostCalculationExternalSurcharge = {
    id: number;
    employmentId?: number;
    organizationalUnitId?: number;
    functionId?: number;
    validFrom: string;
    validTo: string;
    surchargeType: CostCalculationExternalSurchargeType;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
    timeFrom: string;
    timeTo: string;
    percentage?: number;
    amount?: number;
    description: string;
    function?: FunctionResponse;
};
export type CostCalculationExternalSurchargeSetting = {
    id: number;
    employmentId?: number;
    organizationalUnitId?: number;
    validFrom: string;
    validTo: string;
    functionId?: number;
    calculateFunctionType?: CalculateFunctionType;
    function?: FunctionResponse;
    amount?: number;
    fee?: number;
    vatType: VatType;
    vat?: Vat;
};
export type CostCalculationExternalSurchargeType = "PERCENTAGE" | "AMOUNT";
export type CostCalculationExternalTravelExpenses = {
    dutyLineId?: number;
    km: number;
    travelExpenses: number;
};
export type CostCalculationExternalVat = {
    vatPercentage: number;
    vatOfTotal: number;
    total: number;
};
export type CreateAbsenceNoticeRequest = {
    start: string;
    end?: string;
    reason: string;
};
export type CreateActivityRequest = {
    name: string;
    description?: string;
    code: string;
    requiredQualificationEntityIds: string[];
    schedulingLabel: SchedulingLabel;
    clientRelatedType?: ClientRelatedType;
    validFrom?: string;
    validTo?: string;
    allowedFunctionIds?: string[];
};
export type CreateAppointmentGroupingRequest = {
    code: string;
    description: string;
    start: string;
    durationInSeconds: number;
    organizationalUnitId?: number;
};
export type CreateAppointmentRequest = {
    start: string;
    durationInSeconds: number;
    organizationalUnitId?: number;
    workPlaceEntityId?: string;
    schedulingLabel?: SchedulingLabel;
    projectEntityId?: string;
    activityEntityId?: string;
    clientEntityIds?: string[];
    resourceEntityIds?: string[];
    employeeEntityIds?: string[];
    recurrenceRule?: RecurrenceRuleRequest;
    noteInternal?: string;
    notePublic?: string;
    locationType?: LocationType;
    employeeTypeEntityId?: string;
    suppressWarnings?: boolean;
};
export type CreateAvailabilityParticipantRequest = {
    start?: string;
    end?: string;
    type?: AvailabilityType;
    participantId?: string;
    participantType?: ParticipantType;
};
export type CreateConfigurationRequest = {
    name: string;
    settings?: ConfigurationSettingRequest[];
    planningEntities: ConfigurationPlanningEntity[];
};
export type CreateDayLimitArrangementForEmployeeRequest = {
    validFrom: string;
    validTo: string;
    employeeId: number;
    employmentId?: string;
    maxNumberOfDays?: number;
    dayLimitArrangementNotApplicableForEmployee?: boolean;
    alternativeTimeUnit?: TimeUnit;
    alternativeNrOfPeriods?: number;
    startDateForAlternativePeriod?: string;
};
export type CreateDayLimitArrangementRequest = {
    validFrom: string;
    validTo?: string;
    claId?: number;
    records: DayLimitRecordRequest[];
};
export type CreateDutyCodeRequest = {
    dutyCodeId?: number;
    code: string;
    description?: string;
    colorCode?: string;
    organizationalUnitId?: number | null;
    validFrom?: string;
    validTo?: string;
    periods?: DutyCodePeriodRequest[];
    flags?: DutyCodeFlag[];
};
export type CreateExpenseClaimRequest = {
    employmentId: number;
    date: string;
    description?: string;
    subtractCommuteDistance?: boolean;
    registerPrivateKilometers?: boolean;
    transportationTypeCode?: string;
    declarationTypeId?: string;
    routes: ExpenseClaimRouteRequest[];
};
export type CreatePeriodAssignmentRequest = {
    startDate?: string;
    endDate?: string;
    description?: string;
    organizationalUnitId?: number;
    functionId?: number;
    hoursPerWeek?: number;
};
export type CreateProjectRequest = {
    name: string;
    description?: string;
    validFrom: string;
    validTo?: string;
};
export type CreateResourceRequest = {
    name: string;
    description?: string;
    code: string;
    active?: boolean;
};
export type CreateTravelSetRequest = {
    code: string;
    externalCode?: string;
    description: string;
    compensationTypeId?: number;
    isExternal?: boolean;
    travelSetLine: TravelSetLineRequest;
    transportationMode?: TransportationMode;
};
export type CreateWorkPlaceAtOrganizationalUnitAssignmentRequest = {
    organizationalUnitId?: number;
    workPlaceEntityId?: string;
    startDate?: string;
    endDate?: string;
};
export type CreateWorkPlaceRequest = {
    name?: string;
    description?: string;
    organizationalUnitId?: number;
    validFrom?: string;
    validTo?: string;
    address?: AddressRequest;
    feature?: string;
};
export type CreditAppointmentsRequest = {
    appointmentEntityIds?: string[];
};
export type DayInWeekRequest = "SUNDAY" | "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY";
export type DayLimitArrangementForEmployeeResponse = {
    id: string;
    validFrom: string;
    validTo: string;
    employee: EmployeeResponse;
    employment?: EmploymentResponse;
    maxNumberOfDays?: number;
    dayLimitArrangementNotApplicableForEmployee?: boolean;
    alternativeTimeUnit?: TimeUnit;
    alternativeNrOfPeriods?: number;
    startDateForAlternativePeriod?: string;
    periodDescription?: string;
};
export type DayLimitArrangementResponse = {
    id: string;
    validFrom: string;
    validTo: string;
    cla?: ClaResponse;
    records: DayLimitRecordResponse[];
};
export type DayLimitRecordRequest = {
    fromHours: number;
    toHoursInclusive?: number;
    maxNumberOfDays: number;
};
export type DayLimitRecordResponse = {
    id: string;
    fromHours: number;
    toHoursInclusive: number;
    maxNumberOfDays: number;
};
export type DayLimitViolationPayOutRequest = {
    employmentId: number;
    startPeriod?: string;
    payOutDate: string;
    numberOfDaysPaidOut: number;
};
export type DayLimitViolationPayOutRequestArray = {
    requests?: DayLimitViolationPayOutRequest[];
};
export type DayPart = "MORNING" | "AFTERNOON" | "EVENING" | "NIGHT";
export type DeclarationTypeRequest = {
    id?: string;
    name: string;
    isDefault: boolean;
    validFrom: string;
    validTo: string;
};
export type DeclarationTypeResponse = {
    id: string;
    name: string;
    isDefault: boolean;
    isDeletable: boolean;
    validFrom: string;
    validTo: string;
};
export type DeleteAppointmentRequest = {
    appointmentOccurrenceIdentifier: AppointmentOccurrenceRequest;
    editAppointmentType: EditAppointmentTypeResponse;
    recurrenceExceptionUpdateType?: RecurrenceExceptionUpdateType;
};
export type DeleteAvailabilityParticipantRequest = {
    id: string;
};
export type DeleteDayLimitArrangementForEmployeeRequest = {
    id: string;
};
export type DeleteDayLimitArrangementRequest = {
    id: string;
};
export type DeleteDutyCodeRequest = {
    dutyCodeId: number;
    organizationalUnitId?: number;
};
export type DeleteEmploymentAdditionalProperty = {
    employmentAdditionalPropertyId: number;
    employmentId: number;
};
export type DummyResponse = {
    employeeInPeriod?: EmployeeInPeriodResponse;
    requirement?: Requirement;
};
export type DutyAskRoundSettingsResponse = {
    nonFlexEnabled: boolean;
    flexEnabled: boolean;
    minutesBeforeStartLimit: number;
    rounds: AskRoundSettingsResponse[];
};
export type DutyCalculationValueSleepDutyType = {
    dutyCalculationValueSleepDutyId?: number;
    validFrom: string;
    validTo: string;
    claId?: number;
    hourlyWageValue: number;
    hourlyWageValue20: number;
    hourlyWageValue19: number;
    hourlyWageValue18: number;
    hourlyWageValue17: number;
    hourlyWageValue16: number;
    hourlyWageValue15: number;
};
export type DutyCode = {
    dutyCodeId: number;
    dutyCode?: string;
    description?: string;
    code?: string;
    colorCode?: string;
    startTime?: string;
    endTime?: string;
    organizationalUnitId?: number | null;
    validFrom: string;
    validTo: string;
    periods: DutyCodePeriod[];
    flags?: DutyCodeFlag[];
    periodsCanBeEdited: boolean;
    canBeDeleted: boolean;
};
export type DutyCodeFlag = "REQUEST_EXCLUDE" | "MANDATORY_MANUAL_RATE" | "BUH" | "BLOCK_RATE_MANDATORY" | "ROUTE" | "ORGANIZATIONAL_UNIT_SPECIFIC";
export type DutyCodePeriod = {
    dutyCodePeriodId: number;
    startTime: string;
    endTime: string;
    dutyHourType?: DutyHourType;
};
export type DutyCodePeriodRequest = {
    dutyCodePeriodId?: number;
    startTime: string;
    endTime: string;
    dutyHourTypeId?: number;
};
export type DutyHourType = {
    dutyHourTypeId: number;
    code: string;
    colorCode?: string;
    description?: string;
    dutyCalculationCode?: number;
    isDefault?: boolean;
    showInTeamRoster?: boolean;
    showAbsenceTypeInTeamRoster?: boolean;
    dutyHourTypeFlags?: DutyHourTypeFlag[];
    dutyHourTypeDynFlags?: DutyHourTypeDynFlag[];
};
export type DutyHourTypeDynFlag = "REQUEST_DISCONNECT_CUST_ALTER" | "EXCLUDE_FROM_JUS" | "TRAVEL_EXPENSES" | "ILLNES_EXTERNAL_CAUSE" | "EXS_DUTY_TIMES_EDIT" | "NOT_ON_DAY_ROSTER" | "BUH_BLOCKED_FLAG" | "SHORTLIST" | "HOURS_PERC_SEPARATE" | "BONUS_CRISIS" | "EXS_DUTY_TIMES_CREATE" | "REQ_DISCONNECT_AUTO" | "SAL_PERC_SEPARATE" | "RESTRICTED_DUTY_TYPE" | "EXS_ABSENCE_REQ" | "BLOCK_OUTSIDE_ABSENCE" | "BLOCK_INSIDE_ABSENCE" | "INCLUDE_IN_KIDS_RATIO" | "ALLOW_SWAP" | "ROSTER_FREE" | "BONUS_CRISIS_100" | "EXTERNAL_DUTY_MARK" | "WORK_FROM_HOME" | "SHOW_IN_TEAMROSTER" | "HOURS_PERC_SEPARATE_BUH" | "HOURS_PERC_SEPARATE_BONUS" | "PLB_BLOCKED_FLAG" | "SHOW_ABSENCE_TYPE_IN_TEAMROSTER" | "AVAILABLE_DECENTRALIZED" | "TRAVEL_MOVEMENTS" | "COUNT_FOR_DAY_LIMIT" | "ADDITIONAL_COMPENSATION_STANDARD" | "ADDITIONAL_COMPENSATION_SLEEP" | "ADDITIONAL_COMPENSATION_DEVIANT" | "OVERSTAFFED";
export type DutyHourTypeFlag = "DEFAULT";
export type DutyOrganizationalUnitRequestResponse = {
    id: number;
    requestedOn?: string;
    isRetracted: boolean;
    retractedOn?: string;
};
export type DutyPeriodResponse = {
    id: number;
    start: string;
    end: string;
    dutyHourType?: DutyHourType;
};
export type DutyPeriodsBlockWithDtrViolationsResponse = {
    id: string;
    startDate?: string;
    endDate?: string;
    isNightBlock: boolean;
    totalWorkHours?: number;
    totalPresenceHours?: number;
    restHoursBeforeBlock?: number;
    restHoursAfterBlock?: number;
    restHoursDuringBlock?: number;
    blokDuration?: number;
    periods: RosterDutyPeriodResponse[];
    violations: DutyTimeRegulationViolationResponse[];
    minRestHoursBetweenExceptionAndDefault: boolean;
};
export type DutyResponse = {
    id?: string;
    date: string;
    start: string;
    end: string;
    dutyCode?: DutyCode;
    assignedEmployee?: EmployeeResponse;
    assignedEmployment?: EmploymentResponse;
    assignedOrganizationalUnit?: OrganizationalUnitOnDateResponse;
    periods?: DutyPeriodResponse[];
    function?: FunctionResponse;
    requestId?: number;
    dutyLineId?: number;
    status: DutyStatus;
    qualifications?: QualificationResponse[];
    notes?: NoteResponse[];
    dutyOrganizationalUnitRequests?: DutyOrganizationalUnitRequestResponse[];
    organisation?: OrganizationResponse;
    travelDistance?: number;
    travelDistanceCompensation?: number;
    reach?: number;
    connectedOn?: string;
    isIncludedInKidsRatio?: boolean;
};
export type DutyStatus = "OPEN" | "PLANNED" | "PENDING" | "APPROVED" | "REJECTED" | "REALIZED" | "INVOICED";
export type DutyTimeRegulationViolationResponse = {
    ruleName: string;
    periodStartDate: string;
    periodEndDate: string;
    description: string;
};
export type EditAppointmentTypeResponse = "OVERRIDE" | "ALL" | "THIS_AND_ALL_SUBSEQUENT" | "IN_PERIOD" | "ONLY_THIS";
export type EmployeeInPeriodResponse = {
    periodStartDate?: string;
    periodEndDate?: string;
    employee?: EmployeeResponse;
    activeFunctions?: FunctionResponse[];
};
export type EmployeeOrganizationalUnitResponse = {
    types: EmployeeOrganizationalUnitType[];
    organizationalUnit: OrganizationalUnitOnDateResponse;
    publishDate: string;
};
export type EmployeeOrganizationalUnitType = "OWN" | "SHARED" | "GRANT" | "PLANNED" | "PREFERENCE";
export type EmployeeResponse = {
    id?: number;
    number?: number;
    firstName?: string;
    lastName?: string;
    nameCompound?: string;
    nameInformal?: string;
    phoneNumber?: string;
    mobilePhoneNumber?: string;
    email?: string;
    entityId?: string;
    planningFeatures?: string[];
    transportationMode?: TransportationMode;
};
export type EmployeeTypeResponse = {
    entityId?: string;
    name: string;
    code: string;
    description?: string;
};
export type EmployeeWithEmploymentsResponse = {
    id?: number;
    number?: number;
    firstName?: string;
    lastName?: string;
    nameCompound?: string;
    nameInformal?: string;
    entityId?: string;
    employments?: EmploymentResponse[];
};
export type EmploymentAdditionalProperty = {
    employmentAdditionalPropertyId?: number;
    employmentId: number;
    validFrom?: string;
    validTo?: string;
    propertyType: EmploymentAdditionalPropertyType;
    propertyValue: EmploymentAdditionalPropertyValue;
    lastUpdatedBy?: number;
    lastUpdatedOn?: string;
};
export type EmploymentAdditionalPropertyType = "CHAMBER_OF_COMMERCE" | "IDENTITY" | "VAT_NUMBER" | "DECLARATION_OF_CONDUCT" | "COMPANY_DETAILS" | "CHILDCARE_REGISTRATION" | "BIG_REGISTRATION";
export type EmploymentAdditionalPropertyValue = {
    value?: string;
    propertyValueIDType?: EmploymentAdditionalPropertyValueIDType;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    addressLine4?: string;
    addressLine5?: string;
};
export type EmploymentAdditionalPropertyValueIDType = "PASSPORT" | "IDENTITY_CARD" | "DRIVER_LICENSE";
export type EmploymentDayViolationResponse = {
    employment: EmploymentResponse;
    periodStart?: string;
    periodEnd?: string;
    periodDescription?: string;
    numberOfWorkingDays: number;
    maxAllowedNumberOfDays: number;
    numberOfViolationDays: number;
    canBePaidOut: boolean;
    isUnjustifiedPayOut: boolean;
    numberOfPaidOutDays: number;
};
export type EmploymentResponse = {
    id: number;
    entityId?: string;
    employeeId: number;
    startDate?: string;
    endDate?: string;
    employeeName?: string;
    organizationalUnitName?: string;
    functionName?: string;
    number?: number;
    employeeNumber?: number;
};
export type EndClientAbsenceForAppointmentRequest = {
    appointmentOccurrence?: AppointmentOccurrenceRequest;
};
export type EndOfEmploymentSettlementResponse = {
    employment: EmploymentResponse;
    endOfEmploymentSettlementDate?: string;
    extraHours?: number;
    leaveHours?: number;
    plbHours?: number;
    transferableEmployments?: EmploymentResponse[];
    defaultSettlementType?: EndOfEmploymentSettlementType;
    payOutExtraHoursType?: DutyHourType;
    payOutLeaveHoursType?: DutyHourType;
    payOutPlbHoursType?: DutyHourType;
    payOutBalanceHoursType?: DutyHourType;
    settleExtraHoursType?: DutyHourType;
    settleLeaveHoursType?: DutyHourType;
    settlePlbHoursType?: DutyHourType;
    settleBalanceHoursType?: DutyHourType;
    transferExtraHoursType?: DutyHourType;
    transferLeaveHoursType?: DutyHourType;
    transferPlbHoursType?: DutyHourType;
    transferBalanceHoursType?: DutyHourType;
    hourManagementPresentationMode?: string;
};
export type EndOfEmploymentSettlementType = "NONE" | "PAY_OUT" | "SETTLE" | "TRANSFER";
export type ErrorResponse = {
    code: number;
    message: string;
};
export type EventCategory = "CLIENT" | "NON_CLIENT" | "TRAVELTIME" | "FREE";
export type EventLocationResponse = {
    locationType: LocationType;
    latitude?: number;
    longitude?: number;
    address?: AddressResponse;
    points?: string;
    distanceInMeters?: number;
};
export type EventResponse = {
    id: string;
    date: string;
    start: string;
    end: string;
    durationInSeconds: number;
    timeWindow?: TimeWindowResponse;
    recurrenceRule?: RecurrenceRuleResponse;
    recurrenceRuleRepresentation?: string;
    noteInternal?: string;
    notePublic?: string;
    isRecurring: boolean;
    isRecurringException: boolean;
    appointmentType?: AppointmentType;
    isOpen: boolean;
    isAssigned?: boolean;
    isAbsent: boolean;
    absenceReason?: string;
    category: EventCategory;
    activity: ActivityResponse;
    organizationalUnit: OrganizationalUnitOnDateResponse;
    workPlace?: WorkPlaceResponse;
    project?: ProjectResponse;
    status: EventStatus;
    clients: ClientResponse[];
    employeeType?: EmployeeTypeResponse;
    employees: EmployeeResponse[];
    resources?: ResourceResponse[];
    appointmentGrouping?: AppointmentGroupingResponse;
    planningEntityIdentifiers?: PlanningEntityIdentifier[];
    location: EventLocationResponse;
    warnings: EventWarningsResponse;
    schedulingLabel: SchedulingLabel;
    isSubmittable: boolean;
    isApprovable: boolean;
    isEditable: boolean;
    isCreditable: boolean;
    isCredit: boolean;
    linkedDutyCode?: string;
    appointmentExceptions?: AppointmentExceptionResponse[];
    originalAppointmentId?: string;
    externalId?: string;
    synchronizationStatusSummary?: SynchronizationStatusSummaryResponse;
    importSyncStatus?: AppointmentImportSyncStatusSummaryResponse;
    createdById?: number;
    createdByName?: string;
    createdOn?: string;
    lastUpdatedById?: number;
    lastUpdatedByName?: string;
    lastUpdatedOn?: string;
    allowedToEditRecurrenceRule?: boolean;
    endDateAppointmentSeries?: string;
    hasFutureSubSeriesOrExceptions?: boolean;
};
export type EventStatus = "PLANNED" | "SUBMITTED" | "APPROVED";
export type EventWarningsResponse = {
    startsTooEarly: boolean;
    startsTooLate: boolean;
    isInSync: boolean;
    hasOverlap: boolean;
    isOverQualified: boolean;
    isUnderQualified: boolean;
    excludedEmployeeClientCombination: boolean;
    excludedEmployeeClientCombinationDescription: string;
};
export type ExpenseClaimCreateSummaryResponse = {
    nrOfCreated: number;
    nrOfUpdated?: number;
    nrOfIgnored: number;
};
export type ExpenseClaimResponse = {
    id: string;
    employment: EmploymentResponse;
    date: string;
    description?: string;
    subtractCommuteDistance: boolean;
    registerPrivateKilometers?: boolean;
    transportationType: TransportationTypeResponse;
    declarationType?: DeclarationTypeResponse;
    status: ExpenseClaimStatus;
    statusNote?: string;
    isRevokable?: boolean;
    canBeApproved?: boolean;
    routes: ExpenseClaimRouteResponse[];
};
export type ExpenseClaimRouteRequest = {
    from: string;
    to: string;
    distanceInMeters: number;
    type?: TravelMovementType;
};
export type ExpenseClaimRouteResponse = {
    id: string;
    from: string;
    to: string;
    latitudeFrom?: number;
    longitudeFrom?: number;
    latitudeTo?: number;
    longitudeTo?: number;
    distanceInMeters: number;
    points?: string;
    correctedDistanceInMeters?: number;
    type?: TravelMovementType;
};
export type ExpenseClaimStatus = "SUBMITTED" | "APPROVED" | "REVOKED" | "REALIZED" | "REJECTED";
export type ExpenseClaimSummaryResponse = {
    expenseClaimsUnapprovedLastMonth: number;
    expenseClaimsUnapprovedCurrentMonth: number;
};
export type ExportCalculatedAppointmentOccurrencesBySeriesRequest = {
    originalAppointmentId: string;
    startDate: string;
    endDate: string;
};
export type ExportCalculatedAppointmentOccurrencesRequest = {
    startDate: string;
    endDate: string;
};
export type ExtendAppointmentSeriesHorizonRequest = {
    updateHorizon?: boolean;
};
export type FunctionResponse = {
    id?: number;
    code?: string;
    description?: string;
    entityId?: string;
};
export type GetAppointmentsFiltered = {
    startDate: string;
    endDate: string;
    planningEntityIdentifiers?: PlanningEntityIdentifier[];
    activityIds?: string[];
};
export type GetAvailabilityParticipantRequest = {
    startDate?: string;
    endDate?: string;
    planningEntityIdentifiers?: PlanningEntityIdentifier[];
};
export type GetPlanningRequest = {
    startDate?: string;
    endDate?: string;
    organizationalUnitPlanningEntityPerspective?: PlanningEntityType;
    planningEntityIdentifiers?: PlanningEntityIdentifier[];
};
export type GetWorkPlacesRequest = {
    startDate?: string;
    endDate?: string;
    organizationalUnitIds?: number[];
};
export type IdentityAvatarResponse = {
    data?: string;
    mimeType?: string;
};
export type ImportAppointmentsRequest = {
    startDate: string;
    endDate: string;
    deleteOrphans?: boolean;
};
export type InProgressPeriodAssignmentRequest = {
    periodAssignmentEntityId: string;
};
export type KidDistributionResponse = {
    numberOfAge0?: number;
    numberOfAge1?: number;
    numberOfAge2?: number;
    numberOfAge3?: number;
    numberOfAge4To7?: number;
    numberOfAge7OrAbove?: number;
    total?: number;
};
export type KidsRatioOrganizationalUnitOverviewResponse = {
    organizationalUnitId?: number;
    name: string;
    ratios: KidsRatioResponse[];
};
export type KidsRatioOverviewResponse = {
    organizationalUnitRatios?: KidsRatioOrganizationalUnitOverviewResponse[];
    totalRatios?: KidsRatioResponse[];
};
export type KidsRatioResponse = {
    organizationalUnitId?: number;
    date?: string;
    time?: string;
    numberOfEmployees?: number;
    numberOfRequiredEmployees?: number;
    employeeBalance?: number;
    kidDistribution?: KidDistributionResponse;
};
export type LastLockedMonthResponse = {
    lastLockedMonth?: string;
};
export type LocationType = "NONE" | "CLIENTADDRESS" | "ROOM" | "LOCATION" | "TELECARE" | "ROUTE";
export type LogicalOperator = "AND" | "OR";
export type MatchCandidateResponse = {
    employmentId: number;
    employeeEntityId: string;
    employeeNumber: number;
    employeeName: string;
    employmentEntityId: string;
    employmentNumber: number;
    isFlexEmployee?: boolean;
    function?: FunctionResponse;
    organizationalUnit?: OrganizationalUnitOnDateResponse;
    overlapWithDutiesPercentage: number;
    overlapWithAppointmentsPercentage: number;
    contractMatchPercentage: number;
    functionMatchPercentage: number;
    responseMatchPercentage?: number;
    responseRemark?: string;
    missingQualifications: QualificationResponse[];
    matchPercentage: number;
};
export type NoteResponse = {
    id: number;
    content?: string;
    createdOn: string;
    isPrivate: boolean;
    isExternal: boolean;
};
export type NotificationLevelResponse = "ERROR" | "WARNING";
export type NotificationResponse = {
    text?: string;
    referenceId?: string;
    notificationLevel?: NotificationLevelResponse;
    notificationType?: NotificationType;
};
export type NotificationType = "UNAVAILABILITY" | "DUTY_CONTAINMENT" | "ABSENCE";
export type OrganizationResponse = {
    id?: string;
    name?: string;
};
export type OrganizationalUnitOnDateResponse = {
    id: number;
    code: string;
    name: string;
    address?: AddressResponse;
    remarks?: string;
    issues?: string;
    phone?: string;
};
export type ParticipantSummary = {
    displayName?: string;
    number?: number;
};
export type ParticipantType = "EMPLOYEE" | "CLIENT" | "RESOURCE" | "EMPLOYMENT";
export type Period = {
    startDate: string;
    endDate: string;
};
export type PeriodAssignmentResponse = {
    entityId: string;
    startDate: string;
    endDate: string;
    function: FunctionResponse;
    organizationalUnit: OrganizationalUnitOnDateResponse;
    employment?: EmploymentResponse;
    employee?: EmployeeResponse;
    hoursPerWeek: number;
    description: string;
    status: PeriodAssignmentStatus;
    requestedOn: string;
    inProgressOn?: string;
    completedOn?: string;
    returnedOn?: string;
    requestedBy?: string;
    inProgressBy?: string;
    completedBy?: string;
    returnedBy?: string;
    reason?: string;
    numberOfSubscribedEmployees: number;
    numberOfRejectedEmployees: number;
};
export type PeriodAssignmentStatus = "REQUESTED" | "IN_PROGRESS" | "COMPLETED" | "RETURNED";
export type PeriodTypeRequest = "YEAR" | "MONTH" | "WEEK" | "DAY";
export type PlanAppointmentsRequest = {
    start?: string;
    date?: string;
    appointmentOccurrences?: AppointmentOccurrenceRequest[];
    planningEntity: PlanningEntityIdentifier;
    editAppointmentType: EditAppointmentTypeResponse;
    suppressWarnings?: boolean;
    correlationToken?: string;
    viewport?: ViewportContext;
};
export type PlannedSubscribedHourResponse = {
    year?: number;
    month?: number;
    plannedHours?: number;
    subscribedHours?: number;
    contractHours?: number;
};
export type PlanningEntitiesSearchResponse = {
    id?: string;
    number?: number;
    label?: string;
    birthDate?: string;
    type?: PlanningEntityType;
    abbreviation?: string;
};
export type PlanningEntityDataType = "CLIENT" | "EMPLOYEEINPERIOD";
export type PlanningEntityIdentifier = {
    entityId?: string;
    id?: number;
    externalEntityId?: string;
    externalId?: number;
    number?: number;
    contractNumber?: number;
    planningEntityType: PlanningEntityType;
    start?: string;
};
export type PlanningEntityMetricsResponse = {
    contractDurationInSeconds?: number;
    plannedDurationInSeconds?: number;
};
export type PlanningEntityResponse = {
    id: string;
    type: PlanningEntityType;
    planningEntityIdentifier: PlanningEntityIdentifier;
    planningEntity?: {};
    planningEntityDataType?: PlanningEntityDataType;
    name: string;
    description?: string;
    schedules: ScheduleResponse[];
    events: EventResponse[];
    absences?: AbsenceResponse[];
    availabilities?: AvailabilityResponse[];
    planningTasks?: PlanningTaskResponse[];
    metrics?: PlanningEntityMetricsResponse;
};
export type PlanningEntityType = "CLIENT" | "EMPLOYEE" | "WORKPLACE" | "ROUTE" | "PROJECT" | "ORGANIZATIONAL_UNIT" | "EMPLOYMENT" | "ORGANIZATIONAL_UNIT_TO_PLAN" | "RESOURCE";
export type PlanningRequest = {
    startDate: string;
    endDate: string;
    organizationalUnitIds: number[];
    planningEntityType: PlanningEntityType;
    includeEvents?: boolean;
};
export type PlanningResponse = {
    kidsRatio?: KidsRatioOverviewResponse;
    planningEntities: PlanningEntityResponse[];
    organizationalUnitId?: number;
    date?: string;
};
export type PlanningTaskCategoryResponse = "DEFAULT" | "APPOINTMENT_TO_PLAN" | "APPOINTMENT_WARNING" | "SCHEDULE_TO_ASSIGN" | "SCHEDULE_WITH_MULTIPLE_DUTIES";
export type PlanningTaskResponse = {
    type: PlanningTaskTypeResponse;
    category: PlanningTaskCategoryResponse;
    data?: {};
};
export type PlanningTaskTypeResponse = "DEFAULT" | "APPOINTMENT" | "SCHEDULE";
export type PrescriptionResponse = {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    urgency: Urgency;
    status: PrescriptionStatus;
    prescriptionRules: PrescriptionRuleResponse[];
};
export type PrescriptionRulePeriodResponse = {
    id: string;
    frequency: number;
    start: string;
    end: string;
    startPeriodNumber: number;
    endPeriodNumber: number;
    rangeType: RangeType;
    interval: number;
    recurrenceType?: RecurrenceType;
    requirement?: Requirement;
};
export type PrescriptionRuleResponse = {
    id: string;
    start: string;
    end: string;
    description: string;
    activity: ActivityResponse;
    prescriptionRulePeriods: PrescriptionRulePeriodResponse[];
};
export type PrescriptionStatus = "ACTIVE" | "EXPIRED" | "CONCEPT";
export type ProcessAbsenceNoticeRequest = {
    absenceNoticeId: string;
};
export type ProcessEndOfEmploymentSettlementRequest = {
    employmentId: number;
    endOfEmploymentSettlementDate: string;
    type: EndOfEmploymentSettlementType;
    transferEmploymentId?: number;
};
export type ProjectParticipantResponse = {
    entityId?: string;
    participantEntityId?: string;
    name?: string;
    role?: ProjectParticipantRole;
    validFrom?: string;
    validTo?: string;
};
export type ProjectParticipantRole = "EMPLOYEE" | "TEAM_LEAD" | "MANAGER";
export type ProjectResponse = {
    entityId?: string;
    name?: string;
    description?: string;
    validFrom?: string;
    validTo?: string;
    participants?: ProjectParticipantResponse[];
    isFavorite?: boolean;
    isEditable?: boolean;
};
export type QualificationResponse = {
    id: string;
    name?: string;
    code?: string;
};
export type RangeType = "DAILY" | "WEEKLY" | "MONTHLY";
export type RecurrenceExceptionUpdateType = "EXCLUDE" | "OVERRIDE" | "UNION";
export type RecurrenceRangeRequest = {
    end?: string;
    numberOfOccurrences?: number;
    type?: RecurrenceRangeTypeRequest;
};
export type RecurrenceRangeResponse = {
    end?: string;
    numberOfOccurrences?: number;
    type?: RecurrenceRangeTypeRequest;
};
export type RecurrenceRangeTypeRequest = "NO_END" | "END_ON_DATE" | "END_ON_OCCURRENCE";
export type RecurrenceRuleRequest = {
    interval?: number;
    dayOfMonth?: number;
    setPosition?: number;
    referenceId?: string;
    periodType?: PeriodTypeRequest;
    recurrenceRange?: RecurrenceRangeRequest;
    dayInWeekSet?: DayInWeekRequest[];
};
export type RecurrenceRuleResponse = {
    start?: string;
    interval?: number;
    dayOfMonth?: number;
    setPosition?: number;
    periodType?: PeriodTypeRequest;
    dayInWeekSet?: DayInWeekRequest[];
    recurrenceRange?: RecurrenceRangeRequest;
    recurrenceRuleRepresentation?: string;
};
export type RecurrenceType = "TOTAL" | "WEEKLY";
export type RegionalPoolEmployeeTenantAgreementRequest = {
    id?: number;
    employeeId: number;
    employmentId: number;
    tenantId: string;
    validFrom: string;
    validTo: string;
    allowDay: boolean;
    allowNight: boolean;
};
export type RegionalPoolEmployeeTenantAgreementResponse = {
    id: number;
    employee: EmployeeResponse;
    employmentId?: number;
    tenant: RegionalPoolTenantResponse;
    validFrom: string;
    validTo: string;
    allowDay: boolean;
    allowNight: boolean;
};
export type RegionalPoolFunctionMappingRequest = {
    regionalPoolFunctionId?: number;
};
export type RegionalPoolFunctionMappingResponse = {
    regionalPoolFunctionId?: number;
    regionalPoolFunctionName?: string;
    tenantFunctionId: number;
    tenantFunctionName: string;
};
export type RegionalPoolQualificationMappingRequest = {
    regionalPoolQualificationId?: string;
};
export type RegionalPoolQualificationMappingResponse = {
    regionalPoolQualificationId?: string;
    regionalPoolQualificationName?: string;
    tenantQualificationId: string;
    tenantQualificationName: string;
};
export type RegionalPoolTenantResponse = {
    id?: number;
    tenantName?: string;
    displayName?: string;
};
export type RejectExpenseClaimsRequest = {
    expenseClaimIds?: string[];
    statusNote?: string;
};
export type RemoveAssignmentEmploymentToAppointmentGroupingRequest = {
    date: string;
};
export type RemoveAssignmentFromAppointmentsRequest = {
    appointmentOccurrences: AppointmentOccurrenceRequest[];
    editAppointmentType: EditAppointmentTypeResponse;
    correlationToken?: string;
};
export type Requirement = {
    id?: string;
    requirementAssignments?: RequirementAssignment[];
};
export type RequirementAssignment = {
    id?: string;
    requirementGroups?: RequirementCriteriaGroup[];
};
export type RequirementCriteriaGroup = {
    id?: string;
    operator?: LogicalOperator;
    requirementCriteria?: RequirementCriterion[];
};
export type RequirementCriterion = {
    id?: string;
    entityId?: string;
    name?: string;
    code?: string;
    requirementCriterionType?: RequirementCriterionType;
};
export type RequirementCriterionType = "FUNCTION" | "QUALIFICATION";
export type ResourceResponse = {
    id: string;
    externalId?: string;
    name: string;
    description?: string;
    code: string;
    active: boolean;
};
export type RespondSelfMatchPeriodAssignmentRequest = {
    periodAssignmentEntityId?: string;
    responseType?: SelfMatchResponseType;
    remark?: string;
};
export type ResponseActionType = "SUBSCRIBE" | "CONNECT";
export type ReturnPeriodAssignmentRequest = {
    periodAssignmentEntityId: string;
    reason: string;
};
export type RosterDutyPeriodResponse = {
    start: string;
    end: string;
    dutyHourType?: DutyHourType;
};
export type ScheduleMetricsResponse = {
    productivity?: number;
    travelTimeInSeconds?: number;
    travelKilometers?: number;
};
export type ScheduleResponse = {
    planningEntityId?: string;
    planningEntityType?: PlanningEntityType;
    type?: ScheduleType;
    date: string;
    start: string;
    end: string;
    durationInSeconds: number;
    dayPart?: DayPart;
    name?: string;
    isOpen: boolean;
    isInSync: boolean;
    hasDuty: boolean;
    startsTooEarly?: boolean;
    endsTooLate?: boolean;
    transportationMode?: TransportationMode;
    organizationalUnit: OrganizationalUnitOnDateResponse;
    function?: FunctionResponse;
    employeeType?: EmployeeTypeResponse;
    appointmentGrouping?: AppointmentGroupingResponse;
    mainAvailability?: AvailabilityResponse;
    duty?: DutyResponse;
    employee?: EmployeeResponse;
    metrics: ScheduleMetricsResponse;
    events: EventResponse[];
    isAssignableToEmployee?: boolean;
    isOverrulingExternalDuty?: boolean;
    hasScheduleWithMultipleDuties?: boolean;
};
export type ScheduleType = "DUTY" | "APPOINTMENT_GROUPING" | "AVAILABILITY" | "ABSENCE" | "OTHER";
export type SchedulingActionConflictResponse = {
    notifications?: NotificationResponse[];
};
export type SchedulingActionResultResponse = {
    notifications?: NotificationResponse[];
    planningEntityIdentifiers?: PlanningEntityIdentifier[];
    planningEntities?: PlanningEntityResponse[];
    success?: boolean;
};
export type SchedulingLabel = "DEFAULT" | "VV" | "HH" | "BB" | "WORKPLACE" | "PROJECT" | "MRP";
export type SchedulingSettingResponse = {
    id?: string;
    key?: string;
};
export type SelfMatchPeriodAssignmentRequest = {
    startDate?: string;
    endDate?: string;
    organizationalUnitIds?: number[];
};
export type SelfMatchPeriodAssignmentResponse = {
    entityId: string;
    startDate: string;
    endDate: string;
    function: FunctionResponse;
    organizationalUnit: OrganizationalUnitOnDateResponse;
    employment?: EmploymentResponse;
    employee?: EmployeeResponse;
    hoursPerWeek: number;
    description: string;
    status: PeriodAssignmentStatus;
    urgency: number;
    complexity: number;
    subscribedEmployees: EmployeeResponse[];
    rejectedEmployees: EmployeeResponse[];
};
export type SelfMatchResponseType = "APPROVED" | "REJECTED" | "ASK_LATER";
export type SortWorkPlaceAtOrganizationalUnitAssignmentRequest = {
    organizationalUnitId: number;
    sortedWorkPlaceEntityIds: string[];
};
export type SubmitAppointmentsRequest = {
    appointmentEntityIds?: string[];
};
export type SuggestedActivitiesGroupResponse = {
    activityGroupType?: ActivityGroupType;
    activities?: ActivityResponse[];
};
export type SuggestedActivitiesRequest = {
    activityId?: string;
    employeeIds?: string[];
};
export type SyncAbsenceReasonsForTenantsRequest = {
    tenants: string[];
};
export type SyncAppointmentGroupingsWithDutiesRequest = {
    startDate: string;
    endDate: string;
    organizationalUnitIds?: number[];
};
export type SyncAppointmentGroupingsWithDutiesResponse = {
    message?: string;
};
export type SyncEcdOrganizationalUnitsForTenantsRequest = {
    tenants: string[];
};
export type SyncExternalFlexRequestsRequest = {
    startDate: string;
    endDate: string;
};
export type SyncExternalFlexRequestsResponse = {
    message?: string;
};
export type SyncStatusResponse = {
    syncDateTime?: string | null;
    destination?: SynchronizationDestination;
    status?: SynchronizationStatus;
    stale?: boolean;
    missing?: boolean;
    missingExport?: boolean;
    missingCalculation?: boolean;
    wontSync?: boolean;
    errorCode?: string;
    errorMessage?: string;
    externalId?: string;
    appointmentContext?: AppointmentContext;
};
export type SynchronizationDestination = "REFLEX" | "ECARIS";
export type SynchronizationStatus = "SUCCESS" | "PARTIALLY_COMPLETE" | "FAILED";
export type SynchronizationStatusSummaryResponse = {
    success?: boolean;
    hasErrors?: boolean;
    synchronizationStatuses?: SyncStatusResponse[];
};
export type SynchronizeAndExportCalculatedAppointmentOccurrencesRequest = {
    startDate: string;
    endDate: string;
};
export type SynchronizeCalculatedAppointmentOccurrencesRequest = {
    startDate: string;
    endDate: string;
};
export type TimeUnit = "DAY" | "WEEK" | "MONTH" | "QUARTER" | "YEAR" | "CUSTOM";
export type TimeWindowResponse = {
    preferred: string;
    start: string;
    end: string;
    isUrgent: boolean;
};
export type TransportationMode = "CAR" | "BICYCLE" | "PEDESTRIAN";
export type TransportationTypeResponse = {
    id: number;
    code: string;
    name: string;
    isExport?: boolean;
    exportCode1?: string;
    exportCode2?: string;
};
export type TravelMovementCorrectionRequest = {
    correctedDistanceInMeters: number;
    departureEventId?: string;
    destinationEventId?: string;
};
export type TravelMovementPerDayResponse = {
    id?: string;
    employment: EmploymentResponse;
    date: string;
    description?: string;
    subtractCommuteDistance?: boolean;
    registerPrivateKilometers?: boolean;
    transportationMode?: TransportationMode;
    declarationType?: DeclarationTypeResponse;
    status: TravelMovementStatus;
    statusNote?: string;
    commuteDistanceInMeters?: number;
    isManual?: boolean;
    canBeApproved?: boolean;
    isCorrected?: boolean;
    isMismatch?: boolean;
    hasFixedDistances?: boolean;
    travelMovements: TravelMovementResponse[];
};
export type TravelMovementResponse = {
    id?: string;
    fromAddress?: string;
    toAddress?: string;
    fromDescription?: string;
    toDescription?: string;
    latitudeFrom?: number;
    longitudeFrom?: number;
    latitudeTo?: number;
    longitudeTo?: number;
    distanceInMeters: number;
    points?: string;
    type?: TravelMovementType;
    transportationMode?: TransportationMode;
    departureEventId?: string;
    destinationEventId?: string;
    compensationInEuros?: number;
    expenseClaimRoute?: ExpenseClaimRouteResponse;
    correctedDistanceInMeters?: number;
};
export type TravelMovementStatus = "UNKNOWN" | "REALIZED" | "CALCULATED" | "SUBMITTED" | "APPROVED" | "REVOKED" | "REJECTED" | "CREDITED";
export type TravelMovementType = "PRIVATE" | "BUSINESS";
export type TravelSetLineRequest = {
    id?: number;
    distanceRangeFrom: number;
    distanceRangeTo: number;
    expensesMax?: number;
    expensesNominal?: number;
    expensesPerDistance?: number;
    validFrom?: string;
    validTo?: string;
};
export type TreatmentResponse = {
    actualStartDate?: string;
    expectedStartDate?: string;
    plannedStartDate?: string;
    actualOrPlannedOrExpectedStartDate?: string;
    actualEndDate?: string;
    plannedEndDate?: string;
    expectedEndDate?: string;
    actualOrPlannedOrExpectedEndDate?: string;
    creationDate?: string;
    cancelDate?: string;
    programName?: string;
    groupName?: string;
    departmentName?: string;
    status?: TreatmentStatusTypeResponse;
    urgency?: string;
};
export type TreatmentStatusTypeResponse = "DEFAULT" | "STARTED" | "CANCELLED" | "ENDED" | "REGISTERED";
export type UndoCommandRequest = {
    commandId: string;
};
export type UpdateActivityRequest = {
    entityId: string;
    name: string;
    description?: string;
    code: string;
    requiredQualificationEntityIds: string[];
    schedulingLabel: SchedulingLabel;
    clientRelatedType?: ClientRelatedType;
    validFrom?: string;
    validTo?: string;
    allowedFunctionIds?: string[];
};
export type UpdateAppointmentGroupingRequest = {
    entityId: string;
    code: string;
    description: string;
    start: string;
    durationInSeconds: number;
    organizationalUnitId?: number;
};
export type UpdateAppointmentRequest = {
    entityId: string;
    start: string;
    durationInSeconds: number;
    organizationalUnitId?: number;
    workPlaceEntityId?: string;
    projectEntityId?: string;
    activityEntityId?: string;
    employeeEntityIds?: string[];
    clientEntityIds?: string[];
    resourceEntityIds?: string[];
    noteInternal?: string;
    notePublic?: string;
    schedulingLabel?: SchedulingLabel;
    originalOccurrenceStart?: string;
    appointmentGroupingEntityId?: string;
    appointmentGroupingStart?: string;
    editAppointmentType?: EditAppointmentTypeResponse;
    suppressWarnings?: boolean;
    locationType?: LocationType;
    employeeTypeEntityId?: string;
    employmentPlanningEntityIdentifier?: PlanningEntityIdentifier;
    correlationToken?: string;
    recurrenceExceptionUpdateType?: RecurrenceExceptionUpdateType;
    recurrenceRule?: RecurrenceRuleRequest;
};
export type UpdateAvailabilityParticipantRequest = {
    availabilityId?: string;
    start?: string;
    end?: string;
    type?: AvailabilityType;
    participantId?: string;
};
export type UpdateConfigurationRequest = {
    id: string;
    name: string;
};
export type UpdateDayLimitArrangementForEmployeeRequest = {
    id: string;
    validFrom: string;
    validTo: string;
    employeeId: number;
    employmentId?: string;
    maxNumberOfDays?: number;
    dayLimitArrangementNotApplicableForEmployee?: boolean;
    alternativeTimeUnit?: TimeUnit;
    alternativeNrOfPeriods?: number;
    startDateForAlternativePeriod?: string;
};
export type UpdateDayLimitArrangementRequest = {
    id: string;
    validFrom: string;
    validTo?: string;
    claId?: number;
    records: DayLimitRecordRequest[];
};
export type UpdateExpenseClaimRequest = {
    id: string;
    date: string;
    description?: string;
    subtractCommuteDistance?: boolean;
    registerPrivateKilometers?: boolean;
    transportationTypeCode?: string;
    declarationTypeId?: string;
    routes: ExpenseClaimRouteRequest[];
};
export type UpdatePeriodAssignmentRequest = {
    periodAssignmentEntityId?: string;
    startDate?: string;
    endDate?: string;
    description?: string;
    organizationalUnitId?: number;
    functionId?: number;
    hoursPerWeek?: number;
};
export type UpdateProjectRequest = {
    entityId?: string;
    name?: string;
    description?: string;
    validFrom?: string;
    validTo?: string;
    participants?: ProjectParticipantResponse[];
};
export type UpdateResourceRequest = {
    id: string;
    name: string;
    description?: string;
    code: string;
    active?: boolean;
};
export type UpdateTransportationTypeExportCodesRequest = {
    id?: number;
    exportCode1?: string;
    exportCode2?: string;
};
export type UpdateTransportationTypeRequest = {
    code?: string;
};
export type UpdateTravelSetRequest = {
    id: number;
    code: string;
    externalCode?: string;
    description: string;
    compensationTypeId?: number;
    isExternal?: boolean;
    travelSetLines?: TravelSetLineRequest[];
    transportationMode?: TransportationMode;
};
export type UpdateWorkPlaceAtOrganizationalUnitAssignmentRequest = {
    entityId?: string;
    organizationalUnitId?: number;
    workPlaceEntityId?: string;
    startDate?: string;
    endDate?: string;
};
export type UpdateWorkPlaceRequest = {
    entityId?: string;
    name?: string;
    description?: string;
    validFrom?: string;
    validTo?: string;
    address?: AddressRequest;
    feature?: string;
};
export type Urgency = "STANDARD" | "URGENT";
export type UserFavoriteReferenceType = "ACTIVITY" | "PROJECT" | "WORKPLACE";
export type UserFavoriteRequest = {
    referenceEntityId?: string;
    referenceType?: UserFavoriteReferenceType;
};
export type Vat = {
    vatType: VatType;
    percentage: number;
};
export type VatType = "NIL" | "LOW" | "HIGH";
export type ViewportContext = {
    startDate?: string;
    endDate?: string;
    planningEntityIdentifiers?: PlanningEntityIdentifier[];
    sourcePlanningEntityIdentifiers?: PlanningEntityIdentifier[];
};
export type WorkPlaceResponse = {
    entityId?: string;
    name?: string;
    description?: string;
    validFrom?: string;
    validTo?: string;
    isFavorite?: boolean;
    address?: AddressResponse;
    feature?: string;
};
export type AxiosConfig = {
    paramsSerializer?: AxiosRequestConfig["paramsSerializer"];
};
export type Config = {
    mutations?: MutationConfigs;
    axios?: AxiosConfig;
};
export function initialize(axios: AxiosInstance, config?: Config) {
    const requests = makeRequests(axios, config?.axios);
    return {
        requests,
        queries: makeQueries(requests),
        mutations: makeMutations(requests, config?.mutations)
    };
}
function useRapiniMutation<TData = unknown, TError = unknown, TVariables = void, TOnMutateResult = unknown>(mutationFn: MutationFunction<TData, TVariables>, config?: (queryClient: QueryClient) => Pick<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, "onSuccess" | "onSettled" | "onError">, options?: Omit<UseMutationOptions<TData, TError, TVariables, TOnMutateResult>, "mutationFn">): UseMutationResult<TData, TError, TVariables, TOnMutateResult> {
    const { onSuccess, onError, onSettled, ...rest } = options ?? {};
    const queryClient = useQueryClient();
    const conf = config?.(queryClient);
    const mutationOptions: typeof options = {
        onSuccess: (...args) => {
            conf?.onSuccess?.(...args);
            onSuccess?.(...args);
        },
        onError: (...args) => {
            conf?.onError?.(...args);
            onError?.(...args);
        },
        onSettled: (...args) => {
            conf?.onSettled?.(...args);
            onSettled?.(...args);
        },
        ...rest
    };
    return useMutation({ mutationFn, ...mutationOptions });
}
function nullIfUndefined<T>(value: T): NonNullable<T> | null {
    return typeof value === "undefined" ? null : value as NonNullable<T> | null;
}
export const queryKeys = {
    getAbsenceNotices: (startDate: string, endDate: string) => ["getAbsenceNotices", startDate, endDate] as const,
    getActivities: (schedulingLabels?: SchedulingLabel[], clientRelatedTypes?: ClientRelatedType[], withRequirements?: boolean) => ["getActivities", nullIfUndefined(schedulingLabels), nullIfUndefined(clientRelatedTypes), nullIfUndefined(withRequirements)] as const,
    secureAdminProceduresClosureYear: () => ["secureAdminProceduresClosureYear"] as const,
    secureAdminProceduresLastLockedMonth: () => ["secureAdminProceduresLastLockedMonth"] as const,
    findAdditionalCompensationsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[]) => ["findAdditionalCompensationsByOrganizationalUnits", startDate, endDate, nullIfUndefined(organizationalUnitIds)] as const,
    getAppointmentExceptions: (appointmentId: string, start: string) => ["getAppointmentExceptions", appointmentId, start] as const,
    importChangedAppointments: (start?: string, end?: string) => ["importChangedAppointments", nullIfUndefined(start), nullIfUndefined(end)] as const,
    getImportAppointmentsSyncStatuses: (from?: string, until?: string, status?: SynchronizationStatus) => ["getImportAppointmentsSyncStatuses", nullIfUndefined(from), nullIfUndefined(until), nullIfUndefined(status)] as const,
    getAppointmentSeries: (originalAppointmentId: string) => ["getAppointmentSeries", originalAppointmentId] as const,
    findMatchCandidatesForAppointments: (appointmentEntityId: string, date: string, organizationalUnitIds?: number[], searchCriterium?: string, checkEmploymentValidity?: boolean) => ["findMatchCandidatesForAppointments", appointmentEntityId, date, nullIfUndefined(organizationalUnitIds), nullIfUndefined(searchCriterium), nullIfUndefined(checkEmploymentValidity)] as const,
    getAppointmentGroupings: (organizationalUnitIds?: number[]) => ["getAppointmentGroupings", nullIfUndefined(organizationalUnitIds)] as const,
    findMatchCandidatesForAppointmentGrouping: (appointmentGroupingId: string, date: string, organizationalUnitIds?: number[], searchCriterium?: string, checkEmploymentValidity?: boolean) => ["findMatchCandidatesForAppointmentGrouping", appointmentGroupingId, date, nullIfUndefined(organizationalUnitIds), nullIfUndefined(searchCriterium), nullIfUndefined(checkEmploymentValidity)] as const,
    findAuditedCommands: (startDate?: string, endDate?: string) => ["findAuditedCommands", nullIfUndefined(startDate), nullIfUndefined(endDate)] as const,
    getCalculatedAppointmentOccurrencesSyncStatuses: (from?: string, until?: string, status?: SynchronizationStatus) => ["getCalculatedAppointmentOccurrencesSyncStatuses", nullIfUndefined(from), nullIfUndefined(until), nullIfUndefined(status)] as const,
    getClients: (organizationalUnitIds?: number[], searchCriterium?: string, schedulingLabels?: SchedulingLabel[]) => ["getClients", nullIfUndefined(organizationalUnitIds), nullIfUndefined(searchCriterium), nullIfUndefined(schedulingLabels)] as const,
    findConfigurationSettings: () => ["findConfigurationSettings"] as const,
    findConfigurationById: (id: string) => ["findConfigurationById", id] as const,
    getDayLimitArrangements: () => ["getDayLimitArrangements"] as const,
    getDayLimitArrangementsForEmployee: (employeeId: number) => ["getDayLimitArrangementsForEmployee", employeeId] as const,
    findDayLimitViolationsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[]) => ["findDayLimitViolationsByOrganizationalUnits", startDate, endDate, nullIfUndefined(organizationalUnitIds)] as const,
    getDeclarationTypes: (startDate?: string, endDate?: string) => ["getDeclarationTypes", nullIfUndefined(startDate), nullIfUndefined(endDate)] as const,
    getAskedDuties: (startDate: string, endDate: string) => ["getAskedDuties", startDate, endDate] as const,
    dummyReference: () => ["dummyReference"] as const,
    getDuties: (startDate: string, endDate: string, employeeNumbers?: number[], includeAssignedDuties?: boolean, includeRequestedDuties?: boolean, includeOpenDuties?: boolean, includePlannedDuties?: boolean, includeRetractedDuties?: boolean, includeTravelDistance?: boolean, includeOptionalEmployeesForPlannedDuties?: boolean) => ["getDuties", startDate, endDate, nullIfUndefined(employeeNumbers), nullIfUndefined(includeAssignedDuties), nullIfUndefined(includeRequestedDuties), nullIfUndefined(includeOpenDuties), nullIfUndefined(includePlannedDuties), nullIfUndefined(includeRetractedDuties), nullIfUndefined(includeTravelDistance), nullIfUndefined(includeOptionalEmployeesForPlannedDuties)] as const,
    getDutyAskRoundSettings: () => ["getDutyAskRoundSettings"] as const,
    getDutyCalculationValueSleepDuties: (validFrom?: string, validTo?: string) => ["getDutyCalculationValueSleepDuties", nullIfUndefined(validFrom), nullIfUndefined(validTo)] as const,
    getDutyCodes: (referenceDate?: string, organizationalUnitId?: number, includeRequestExcluded?: boolean) => ["getDutyCodes", nullIfUndefined(referenceDate), nullIfUndefined(organizationalUnitId), nullIfUndefined(includeRequestExcluded)] as const,
    getDutyCode: (dutyCodeId?: number) => ["getDutyCode", nullIfUndefined(dutyCodeId)] as const,
    getDutyHourTypes: (referenceDate?: string, checkRestrictedAuthorization?: boolean) => ["getDutyHourTypes", nullIfUndefined(referenceDate), nullIfUndefined(checkRestrictedAuthorization)] as const,
    getDutyPeriodsBlocksWithDtrViolations: (employeeId: number, startDate: string, endDate: string) => ["getDutyPeriodsBlocksWithDtrViolations", employeeId, startDate, endDate] as const,
    getCostCalculationExternalSurcharges: (employmentId?: number, organizationalUnitId?: number, validFrom?: string, validTo?: string) => ["getCostCalculationExternalSurcharges", nullIfUndefined(employmentId), nullIfUndefined(organizationalUnitId), nullIfUndefined(validFrom), nullIfUndefined(validTo)] as const,
    getCostCalculationExternalSurchargeSettings: (employmentId?: number, organizationalUnitId?: number, validFrom?: string, validTo?: string) => ["getCostCalculationExternalSurchargeSettings", nullIfUndefined(employmentId), nullIfUndefined(organizationalUnitId), nullIfUndefined(validFrom), nullIfUndefined(validTo)] as const,
    getEmployeeTypes: () => ["getEmployeeTypes"] as const,
    getEmployees: (startDate?: string, endDate?: string, searchCriterium?: string) => ["getEmployees", nullIfUndefined(startDate), nullIfUndefined(endDate), nullIfUndefined(searchCriterium)] as const,
    findColleagues: (startDate?: string, endDate?: string, organizationalUnitIds?: number[], searchCriterium?: string) => ["findColleagues", nullIfUndefined(startDate), nullIfUndefined(endDate), nullIfUndefined(organizationalUnitIds), nullIfUndefined(searchCriterium)] as const,
    findEmployeeWithEmployments: (employeeId: number) => ["findEmployeeWithEmployments", employeeId] as const,
    getEmployeeCostCalculationExternal: (employmentId: number, year: number) => ["getEmployeeCostCalculationExternal", employmentId, year] as const,
    getEmployeeExpenseClaims: (startDate: string, endDate: string) => ["getEmployeeExpenseClaims", startDate, endDate] as const,
    getEmployeeOrganizationalUnits: (referenceDate?: string) => ["getEmployeeOrganizationalUnits", nullIfUndefined(referenceDate)] as const,
    getEmployeeTransportationType: () => ["getEmployeeTransportationType"] as const,
    getEmploymentAdditionalProperties: (employmentId: number) => ["getEmploymentAdditionalProperties", employmentId] as const,
    getEmploymentClaYearsValid: (employmentId: number) => ["getEmploymentClaYearsValid", employmentId] as const,
    getEndOfEmploymentByEmploymentId: (employmentId: number, endOfEmploymentSettlementDate: string) => ["getEndOfEmploymentByEmploymentId", employmentId, endOfEmploymentSettlementDate] as const,
    getEndOfEmploymentSettlementOverview: (organizationalUnitIds?: number[]) => ["getEndOfEmploymentSettlementOverview", nullIfUndefined(organizationalUnitIds)] as const,
    findExpenseClaimById: (id: string) => ["findExpenseClaimById", id] as const,
    findExpenseClaimsByEmployee: (startDate: string, endDate: string, employeeId: number) => ["findExpenseClaimsByEmployee", startDate, endDate, employeeId] as const,
    findExpenseClaimsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[]) => ["findExpenseClaimsByOrganizationalUnits", startDate, endDate, nullIfUndefined(organizationalUnitIds)] as const,
    findExpenseClaimsByEmployments: (startDate: string, endDate: string, employmentId: number) => ["findExpenseClaimsByEmployments", startDate, endDate, employmentId] as const,
    getSummary: (organizationalUnitIds: number[]) => ["getSummary", organizationalUnitIds] as const,
    getAvatar: (employeeNumber?: number) => ["getAvatar", nullIfUndefined(employeeNumber)] as const,
    getFunctions: (startDate: string, endDate: string, onlyPreferred?: boolean, organizationalUnit?: number, onlySelectable?: boolean) => ["getFunctions", startDate, endDate, nullIfUndefined(onlyPreferred), nullIfUndefined(organizationalUnit), nullIfUndefined(onlySelectable)] as const,
    getOrganizationalUnitChildren: (organizationalUnitId: number, referenceDate: string, includeAllChildren: boolean) => ["getOrganizationalUnitChildren", organizationalUnitId, referenceDate, includeAllChildren] as const,
    getPeriodAssignments: (startDate?: string, endDate?: string, organizationalUnitId?: number, statuses?: PeriodAssignmentStatus[]) => ["getPeriodAssignments", nullIfUndefined(startDate), nullIfUndefined(endDate), nullIfUndefined(organizationalUnitId), nullIfUndefined(statuses)] as const,
    findMatchCandidatesForPeriodAssignment: (periodAssignmentId: string, searchCriterium?: string, includeFlex?: boolean, includeDeployableAsFlex?: boolean, checkEmploymentValidity?: boolean) => ["findMatchCandidatesForPeriodAssignment", periodAssignmentId, nullIfUndefined(searchCriterium), nullIfUndefined(includeFlex), nullIfUndefined(includeDeployableAsFlex), nullIfUndefined(checkEmploymentValidity)] as const,
    getPlannedSubscribedHours: () => ["getPlannedSubscribedHours"] as const,
    findPlanningEntities: (searchCriterium: string, types?: PlanningEntityType[], maxResultSize?: number, schedulingLabels?: SchedulingLabel[]) => ["findPlanningEntities", searchCriterium, nullIfUndefined(types), nullIfUndefined(maxResultSize), nullIfUndefined(schedulingLabels)] as const,
    getPrescriptions: (careReceiverId: string) => ["getPrescriptions", careReceiverId] as const,
    getProjects: (startDate?: string, endDate?: string) => ["getProjects", nullIfUndefined(startDate), nullIfUndefined(endDate)] as const,
    getRegionalPoolEmployeeTenantAgreements: (employmentId?: number, tenantId?: string) => ["getRegionalPoolEmployeeTenantAgreements", nullIfUndefined(employmentId), nullIfUndefined(tenantId)] as const,
    getRegionalPoolTenants: () => ["getRegionalPoolTenants"] as const,
    getRegionalPools: () => ["getRegionalPools"] as const,
    getRegionalPoolFunctions: (regionalPoolTenantName: string) => ["getRegionalPoolFunctions", regionalPoolTenantName] as const,
    getRegionalPoolFunctionMappings: (regionalPoolTenantName: string) => ["getRegionalPoolFunctionMappings", regionalPoolTenantName] as const,
    getRegionalPoolQualifications: (regionalPoolTenantName: string) => ["getRegionalPoolQualifications", regionalPoolTenantName] as const,
    getRegionalPoolQualificationMappings: (regionalPoolTenantName: string) => ["getRegionalPoolQualificationMappings", regionalPoolTenantName] as const,
    findResources: () => ["findResources"] as const,
    findResourceById: (id: string) => ["findResourceById", id] as const,
    getSelfMatchPeriodAssignments: (startDate?: string, endDate?: string, organizationalUnitIds?: number[]) => ["getSelfMatchPeriodAssignments", nullIfUndefined(startDate), nullIfUndefined(endDate), nullIfUndefined(organizationalUnitIds)] as const,
    getTransportationTypes: () => ["getTransportationTypes"] as const,
    findTravelMovementsByEmployee: (startDate: string, endDate: string, employeeId: number) => ["findTravelMovementsByEmployee", startDate, endDate, employeeId] as const,
    findTravelMovementsByOrganizationalUnits: (startDate: string, endDate: string, onlyApprovableTravelMovements?: boolean, organizationalUnitIds?: number[]) => ["findTravelMovementsByOrganizationalUnits", startDate, endDate, nullIfUndefined(onlyApprovableTravelMovements), nullIfUndefined(organizationalUnitIds)] as const,
    getWorkPlaces: (startDate?: string, endDate?: string, organizationalUnitIds?: number[]) => ["getWorkPlaces", nullIfUndefined(startDate), nullIfUndefined(endDate), nullIfUndefined(organizationalUnitIds)] as const
} as const;
export type QueryKeys = typeof queryKeys;
function makeRequests(axios: AxiosInstance, config?: AxiosConfig) {
    return {
        getAbsenceNotices: (startDate: string, endDate: string) => axios.request<AbsenceNoticeResponse[]>({
            method: "get",
            url: `/secure/absence-notices`,
            params: {
                startDate,
                endDate
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createAbsenceNotice: (payload: CreateAbsenceNoticeRequest) => axios.request<string>({
            method: "post",
            url: `/secure/absence-notices/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        processAbsenceNotice: (payload: ProcessAbsenceNoticeRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/absence-notices/process`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getActivities: (schedulingLabels?: SchedulingLabel[], clientRelatedTypes?: ClientRelatedType[], withRequirements?: boolean) => axios.request<ActivityResponse[]>({
            method: "get",
            url: `/secure/activities`,
            params: {
                ...(schedulingLabels !== undefined ? { schedulingLabels } : undefined),
                ...(clientRelatedTypes !== undefined ? { clientRelatedTypes } : undefined),
                ...(withRequirements !== undefined ? { withRequirements } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        suggestedActivities: (payload: SuggestedActivitiesRequest) => axios.request<SuggestedActivitiesGroupResponse[]>({
            method: "post",
            url: `/secure/activities/suggested`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createActivity: (payload: CreateActivityRequest) => axios.request<string>({
            method: "post",
            url: `/secure/activities/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateActivity: (payload: UpdateActivityRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/activities/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        secureAdminProceduresAbsenceReasonsSynchronize: (payload: SyncAbsenceReasonsForTenantsRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/admin-procedures/absence-reasons/synchronize`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        secureAdminProceduresOrganizationalUnitsSynchronize: (payload: SyncEcdOrganizationalUnitsForTenantsRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/admin-procedures/organizational-units/synchronize`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        secureAdminProceduresBalanceBudgetGift: () => axios.request<unknown>({
            method: "post",
            url: `/secure/admin-procedures/balance-budget/gift`
        }).then(res => res.data),
        secureAdminProceduresClosureYear: () => axios.request<ClosureYearResponse>({
            method: "get",
            url: `/secure/admin-procedures/closure-year`
        }).then(res => res.data),
        secureAdminProceduresLastLockedMonth: () => axios.request<LastLockedMonthResponse>({
            method: "get",
            url: `/secure/admin-procedures/last-locked-month`
        }).then(res => res.data),
        findAdditionalCompensationsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[]) => axios.request<AdditionalCompensationResponse[]>({
            method: "get",
            url: `/secure/additional-compensations/find-by-organizational-units`,
            params: {
                startDate,
                endDate,
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        approveAppointments: (payload: ApproveAppointmentsRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/appointments/approve`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        creditAppointments: (payload: CreditAppointmentsRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/appointments/credit`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        endClientAbsenceForAppointment: (payload: EndClientAbsenceForAppointmentRequest) => axios.request<SchedulingActionResultResponse>({
            method: "put",
            url: `/secure/appointments/end-client-absence`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        submitAppointments: (payload: SubmitAppointmentsRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/appointments/submit`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        copyAppointmentsInPeriod: (payload: CopyAppointmentsInPeriodRequest) => axios.request<CommandResultResponse>({
            method: "post",
            url: `/secure/appointments/copy-period`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createAppointment: (payload: CreateAppointmentRequest) => axios.request<SchedulingActionResultResponse>({
            method: "post",
            url: `/secure/appointments/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        appointmentBulkDeleteAction: (payload: BulkDeletionActionAppointmentRequest) => axios.request<SchedulingActionResultResponse>({
            method: "post",
            url: `/secure/appointments/bulk/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        appointmentBulkEditParticipantsAction: (payload: BulkEditParticipantsActionAppointmentRequest) => axios.request<SchedulingActionResultResponse>({
            method: "post",
            url: `/secure/appointments/bulk/edit`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getAppointmentExceptions: (appointmentId: string, start: string) => axios.request<AppointmentResponse[]>({
            method: "get",
            url: `/secure/appointments/exceptions`,
            params: {
                appointmentId,
                start
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        importAppointments: (payload: ImportAppointmentsRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/appointments/import`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        importChangedAppointments: (start?: string, end?: string) => axios.request<unknown>({
            method: "get",
            url: `/secure/appointments/import/changes`,
            params: {
                ...(start !== undefined ? { start } : undefined),
                ...(end !== undefined ? { end } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getImportAppointmentsSyncStatuses: (from?: string, until?: string, status?: SynchronizationStatus) => axios.request<AppointmentImportSyncStatusResponse[]>({
            method: "get",
            url: `/secure/appointments/import/status`,
            params: {
                ...(from !== undefined ? { from } : undefined),
                ...(until !== undefined ? { until } : undefined),
                ...(status !== undefined ? { status } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        updateAppointment: (payload: UpdateAppointmentRequest) => axios.request<SchedulingActionResultResponse>({
            method: "put",
            url: `/secure/appointments/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteAppointment: (payload: DeleteAppointmentRequest) => axios.request<string>({
            method: "delete",
            url: `/secure/appointments/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getAppointmentSeries: (originalAppointmentId: string) => axios.request<AppointmentSeriesResponse>({
            method: "get",
            url: `/secure/appointments/get-appointment-series`,
            params: {
                originalAppointmentId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        planAppointments: (payload: PlanAppointmentsRequest) => axios.request<SchedulingActionResultResponse>({
            method: "put",
            url: `/secure/appointments/plan`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        assignEmploymentToAppointment: (payload: AssignEmploymentToAppointmentRequest) => axios.request<SchedulingActionResultResponse>({
            method: "post",
            url: `/secure/appointments/assign-employment`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getAppointmentFiltered: (payload: GetAppointmentsFiltered) => axios.request<EventResponse[]>({
            method: "post",
            url: `/secure/appointments/filtered`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findMatchCandidatesForAppointments: (appointmentEntityId: string, date: string, organizationalUnitIds?: number[], searchCriterium?: string, checkEmploymentValidity?: boolean) => axios.request<MatchCandidateResponse[]>({
            method: "get",
            url: `/secure/appointments/find-match-candidates`,
            params: {
                appointmentEntityId,
                date,
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined),
                ...(searchCriterium !== undefined ? { searchCriterium } : undefined),
                ...(checkEmploymentValidity !== undefined ? { checkEmploymentValidity } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        removeAssignmentFromAppointments: (payload: RemoveAssignmentFromAppointmentsRequest) => axios.request<SchedulingActionResultResponse>({
            method: "post",
            url: `/secure/appointments/remove-assignment`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getAppointmentGroupings: (organizationalUnitIds?: number[]) => axios.request<AppointmentGroupingResponse[]>({
            method: "get",
            url: `/secure/appointment-groupings`,
            params: {
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createAppointmentGrouping: (payload: CreateAppointmentGroupingRequest) => axios.request<string>({
            method: "post",
            url: `/secure/appointment-groupings/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateAppointmentGrouping: (payload: UpdateAppointmentGroupingRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/appointment-groupings/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findMatchCandidatesForAppointmentGrouping: (appointmentGroupingId: string, date: string, organizationalUnitIds?: number[], searchCriterium?: string, checkEmploymentValidity?: boolean) => axios.request<MatchCandidateResponse[]>({
            method: "get",
            url: `/secure/appointment-groupings/find-match-candidates`,
            params: {
                appointmentGroupingId,
                date,
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined),
                ...(searchCriterium !== undefined ? { searchCriterium } : undefined),
                ...(checkEmploymentValidity !== undefined ? { checkEmploymentValidity } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        syncAppointmentGroupingsWithDuties: (payload: SyncAppointmentGroupingsWithDutiesRequest) => axios.request<SyncAppointmentGroupingsWithDutiesResponse>({
            method: "post",
            url: `/secure/appointment-groupings/sync-duties`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        assignEmploymentToAppointmentGrouping: (payload: AssignEmploymentToAppointmentGroupingRequest, appointmentGroupingEntityId: string) => axios.request<unknown>({
            method: "put",
            url: `/secure/appointment-groupings/${appointmentGroupingEntityId}/assign`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        removeAssignmentEmploymentToAppointmentGrouping: (payload: RemoveAssignmentEmploymentToAppointmentGroupingRequest, appointmentGroupingEntityId: string) => axios.request<unknown>({
            method: "post",
            url: `/secure/appointment-groupings/${appointmentGroupingEntityId}/remove-assignment`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateAppointmentGroupingTransportationMode: (payload: AppointmentGroupingTransportationModeRequest, appointmentGroupingEntityId: string) => axios.request<unknown>({
            method: "put",
            url: `/secure/appointment-groupings/${appointmentGroupingEntityId}/transportation-mode`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findAuditedCommands: (startDate?: string, endDate?: string) => axios.request<AuditedCommandSeriesResponse>({
            method: "get",
            url: `/secure/audit/get-audit-commands`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createAvailabilityParticipants: (payload: CreateAvailabilityParticipantRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/availability-participants/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteAvailabilityParticipants: (payload: DeleteAvailabilityParticipantRequest) => axios.request<unknown>({
            method: "delete",
            url: `/secure/availability-participants/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateAvailabilityParticipants: (payload: UpdateAvailabilityParticipantRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/availability-participants/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        exportCalculatedAppointmentOccurrences: (payload: ExportCalculatedAppointmentOccurrencesRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/calculated-appointment-occurrences/export`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        exportCalculatedAppointmentOccurrencesBySeries: (payload: ExportCalculatedAppointmentOccurrencesBySeriesRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/calculated-appointment-occurrences/export-series`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getCalculatedAppointmentOccurrencesSyncStatuses: (from?: string, until?: string, status?: SynchronizationStatus) => axios.request<SyncStatusResponse[]>({
            method: "get",
            url: `/secure/calculated-appointment-occurrences/status`,
            params: {
                ...(from !== undefined ? { from } : undefined),
                ...(until !== undefined ? { until } : undefined),
                ...(status !== undefined ? { status } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        synchronizeCalculatedAppointmentOccurrences: (payload: SynchronizeCalculatedAppointmentOccurrencesRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/calculated-appointment-occurrences/synchronize`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        synchronizeAndExportCalculatedAppointmentOccurrences: (payload: SynchronizeAndExportCalculatedAppointmentOccurrencesRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/calculated-appointment-occurrences/synchronize-and-export`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        extendAppointmentSeriesHorizon: (payload: ExtendAppointmentSeriesHorizonRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/calculated-appointment-occurrences/extend-horizon`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        importCareReceivers: (careReceiverIds: string[]) => axios.request<unknown>({
            method: "post",
            url: `/secure/care-receivers/import`,
            params: {
                careReceiverIds
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        importChangedCareReceivers: (start?: string, end?: string) => axios.request<unknown>({
            method: "post",
            url: `/secure/care-receivers/import/changes`,
            params: {
                ...(start !== undefined ? { start } : undefined),
                ...(end !== undefined ? { end } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        importAllCareReceivers: () => axios.request<unknown>({
            method: "post",
            url: `/secure/care-receivers/import/full'`
        }).then(res => res.data),
        getClients: (organizationalUnitIds?: number[], searchCriterium?: string, schedulingLabels?: SchedulingLabel[]) => axios.request<ClientResponse[]>({
            method: "get",
            url: `/secure/clients`,
            params: {
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined),
                ...(searchCriterium !== undefined ? { searchCriterium } : undefined),
                ...(schedulingLabels !== undefined ? { schedulingLabels } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findConfigurationSettings: () => axios.request<ConfigurationResponse[]>({
            method: "get",
            url: `/secure/configurations`
        }).then(res => res.data),
        createConfiguration: (payload: CreateConfigurationRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/configurations/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findConfigurationById: (id: string) => axios.request<ConfigurationResponse>({
            method: "get",
            url: `/secure/configurations/find-by-id`,
            params: {
                id
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createConfigurationPlanningEntities: (payload: ConfigurationAddPlanningEntitiesRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/configurations/planning-entities/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createConfigurationPlanningEntityForAiBasicConfiguration: (organizationalUnitEntityId: string) => axios.request<CommandResultResponse>({
            method: "post",
            url: `/secure/configurations/planning-entities/create-for-ai-basic-configuration`,
            params: {
                organizationalUnitEntityId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        deleteConfigurationPlanningEntities: (payload: ConfigurationDeletePlanningEntitiesRequest) => axios.request<unknown>({
            method: "delete",
            url: `/secure/configurations/planning-entities/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteConfigurationPlanningEntityForAiBasicConfiguration: (organizationalUnitEntityId: string) => axios.request<CommandResultResponse>({
            method: "delete",
            url: `/secure/configurations/planning-entities/delete-for-ai-basic-configuration`,
            params: {
                organizationalUnitEntityId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createConfigurationSettings: (payload: ConfigurationAddSettingsRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/configurations/settings/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteConfigurationSettings: (payload: ConfigurationDeleteSettingsRequest) => axios.request<unknown>({
            method: "delete",
            url: `/secure/configurations/settings/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateConfigurationSettings: (payload: ConfigurationAddSettingsRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/configurations/settings/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateConfiguration: (payload: UpdateConfigurationRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/configurations/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDayLimitArrangements: () => axios.request<DayLimitArrangementResponse[]>({
            method: "get",
            url: `/secure/day-limit-arrangements`
        }).then(res => res.data),
        createDayLimitArrangement: (payload: CreateDayLimitArrangementRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/day-limit-arrangements/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateDayLimitArrangement: (payload: UpdateDayLimitArrangementRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/day-limit-arrangements/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteDayLimitArrangement: (payload: DeleteDayLimitArrangementRequest) => axios.request<unknown>({
            method: "delete",
            url: `/secure/day-limit-arrangements/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDayLimitArrangementsForEmployee: (employeeId: number) => axios.request<DayLimitArrangementForEmployeeResponse[]>({
            method: "get",
            url: `/secure/day-limit-arrangements-for-employee`,
            params: {
                employeeId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createDayLimitArrangementForEmployee: (payload: CreateDayLimitArrangementForEmployeeRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/day-limit-arrangements-for-employee/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateDayLimitArrangementForEmployee: (payload: UpdateDayLimitArrangementForEmployeeRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/day-limit-arrangements-for-employee/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteDayLimitArrangementForEmployee: (payload: DeleteDayLimitArrangementForEmployeeRequest) => axios.request<unknown>({
            method: "delete",
            url: `/secure/day-limit-arrangements-for-employee/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findDayLimitViolationsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[]) => axios.request<EmploymentDayViolationResponse[]>({
            method: "get",
            url: `/secure/day-limit-violations/find-by-organizational-units`,
            params: {
                startDate,
                endDate,
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createDayLimitViolationPayOut: (payload: DayLimitViolationPayOutRequestArray) => axios.request<unknown>({
            method: "post",
            url: `/secure/day-limit-violation-payouts`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDeclarationTypes: (startDate?: string, endDate?: string) => axios.request<DeclarationTypeResponse[]>({
            method: "get",
            url: `/secure/declaration-types`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createDeclarationType: (payload: DeclarationTypeRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/declaration-types/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteDeclarationType: (id: string) => axios.request<unknown>({
            method: "delete",
            url: `/secure/declaration-types/delete`,
            params: {
                id
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        updateDeclarationType: (payload: DeclarationTypeRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/declaration-types/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getAskedDuties: (startDate: string, endDate: string) => axios.request<AskedDutyResponse[]>({
            method: "get",
            url: `/secure/asked-duties`,
            params: {
                startDate,
                endDate
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        dummyReference: () => axios.request<DummyResponse>({
            method: "get",
            url: `/secure/dummy`
        }).then(res => res.data),
        getDuties: (startDate: string, endDate: string, employeeNumbers?: number[], includeAssignedDuties?: boolean, includeRequestedDuties?: boolean, includeOpenDuties?: boolean, includePlannedDuties?: boolean, includeRetractedDuties?: boolean, includeTravelDistance?: boolean, includeOptionalEmployeesForPlannedDuties?: boolean) => axios.request<DutyResponse[]>({
            method: "get",
            url: `/secure/duties`,
            params: {
                startDate,
                endDate,
                ...(employeeNumbers !== undefined ? { employeeNumbers } : undefined),
                ...(includeAssignedDuties !== undefined ? { includeAssignedDuties } : undefined),
                ...(includeRequestedDuties !== undefined ? { includeRequestedDuties } : undefined),
                ...(includeOpenDuties !== undefined ? { includeOpenDuties } : undefined),
                ...(includePlannedDuties !== undefined ? { includePlannedDuties } : undefined),
                ...(includeRetractedDuties !== undefined ? { includeRetractedDuties } : undefined),
                ...(includeTravelDistance !== undefined ? { includeTravelDistance } : undefined),
                ...(includeOptionalEmployeesForPlannedDuties !== undefined ? { includeOptionalEmployeesForPlannedDuties } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        askEmployeeForDuty: (payload: AskEmployeeForDutyRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/duties/ask-employee`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        askEmployeesForDuty: (payload: AskEmployeesForDutyRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/duties/ask-employees`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDutyAskRoundSettings: () => axios.request<DutyAskRoundSettingsResponse>({
            method: "get",
            url: `/secure/duty-ask-round-settings`
        }).then(res => res.data),
        updateDutyAskRoundSettings: (payload: DutyAskRoundSettingsResponse) => axios.request<unknown>({
            method: "put",
            url: `/secure/duty-ask-round-settings/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDutyCalculationValueSleepDuties: (validFrom?: string, validTo?: string) => axios.request<DutyCalculationValueSleepDutyType[]>({
            method: "get",
            url: `/secure/duty-calculation-values/sleep-duty`,
            params: {
                ...(validFrom !== undefined ? { validFrom } : undefined),
                ...(validTo !== undefined ? { validTo } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createDutyCalculationValueSleepDuty: (payload: DutyCalculationValueSleepDutyType) => axios.request<unknown>({
            method: "post",
            url: `/secure/duty-calculation-values/sleep-duty/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteDutyCalculationValueSleepDuty: (payload: DutyCalculationValueSleepDutyType) => axios.request<unknown>({
            method: "delete",
            url: `/secure/duty-calculation-values/sleep-duty/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateDutyCalculationValueSleepDuty: (payload: DutyCalculationValueSleepDutyType) => axios.request<unknown>({
            method: "put",
            url: `/secure/duty-calculation-values/sleep-duty/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDutyCodes: (referenceDate?: string, organizationalUnitId?: number, includeRequestExcluded?: boolean) => axios.request<DutyCode[]>({
            method: "get",
            url: `/secure/duty-codes`,
            params: {
                ...(referenceDate !== undefined ? { referenceDate } : undefined),
                ...(organizationalUnitId !== undefined ? { organizationalUnitId } : undefined),
                ...(includeRequestExcluded !== undefined ? { includeRequestExcluded } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getDutyCode: (dutyCodeId?: number) => axios.request<DutyCode>({
            method: "get",
            url: `/secure/duty-codes/find-duty-code`,
            params: {
                ...(dutyCodeId !== undefined ? { dutyCodeId } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createDutyCode: (payload: CreateDutyCodeRequest) => axios.request<number>({
            method: "post",
            url: `/secure/duty-codes/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateDutyCode: (payload: CreateDutyCodeRequest) => axios.request<number>({
            method: "put",
            url: `/secure/duty-codes/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteDutyCode: (payload: DeleteDutyCodeRequest) => axios.request<number>({
            method: "delete",
            url: `/secure/duty-codes/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getDutyHourTypes: (referenceDate?: string, checkRestrictedAuthorization?: boolean) => axios.request<DutyHourType[]>({
            method: "get",
            url: `/secure/duty-hour-types`,
            params: {
                ...(referenceDate !== undefined ? { referenceDate } : undefined),
                ...(checkRestrictedAuthorization !== undefined ? { checkRestrictedAuthorization } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getDutyPeriodsBlocksWithDtrViolations: (employeeId: number, startDate: string, endDate: string) => axios.request<DutyPeriodsBlockWithDtrViolationsResponse[]>({
            method: "get",
            url: `/secure/duty-period-blocks-with-dtr-violations`,
            params: {
                employeeId,
                startDate,
                endDate
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getCostCalculationExternalSurcharges: (employmentId?: number, organizationalUnitId?: number, validFrom?: string, validTo?: string) => axios.request<CostCalculationExternalSurcharge[]>({
            method: "get",
            url: `/secure/cost-calculation-external/surcharges`,
            params: {
                ...(employmentId !== undefined ? { employmentId } : undefined),
                ...(organizationalUnitId !== undefined ? { organizationalUnitId } : undefined),
                ...(validFrom !== undefined ? { validFrom } : undefined),
                ...(validTo !== undefined ? { validTo } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        recalculateCostCalculationExternalSurcharge: (payload: CostCalculationExternalRecalculate) => axios.request<unknown>({
            method: "post",
            url: `/secure/cost-calculation-external/surcharge/recalculate`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createCostCalculationExternalSurcharges: (payload: CostCalculationExternalSurcharge) => axios.request<unknown>({
            method: "post",
            url: `/secure/cost-calculation-external/surcharges/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateCostCalculationExternalSurcharges: (payload: CostCalculationExternalSurcharge) => axios.request<unknown>({
            method: "put",
            url: `/secure/cost-calculation-external/surcharges/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getCostCalculationExternalSurchargeSettings: (employmentId?: number, organizationalUnitId?: number, validFrom?: string, validTo?: string) => axios.request<CostCalculationExternalSurchargeSetting[]>({
            method: "get",
            url: `/secure/cost-calculation-external/surcharge-settings`,
            params: {
                ...(employmentId !== undefined ? { employmentId } : undefined),
                ...(organizationalUnitId !== undefined ? { organizationalUnitId } : undefined),
                ...(validFrom !== undefined ? { validFrom } : undefined),
                ...(validTo !== undefined ? { validTo } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createCostCalculationExternalSurchargeSettings: (payload: CostCalculationExternalSurchargeSetting) => axios.request<unknown>({
            method: "post",
            url: `/secure/cost-calculation-external/surcharge-settings/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateCostCalculationExternalSurchargeSettings: (payload: CostCalculationExternalSurchargeSetting) => axios.request<unknown>({
            method: "put",
            url: `/secure/cost-calculation-external/surcharge-settings/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getEmployeeTypes: () => axios.request<EmployeeTypeResponse[]>({
            method: "get",
            url: `/secure/employee-types`
        }).then(res => res.data),
        getEmployees: (startDate?: string, endDate?: string, searchCriterium?: string) => axios.request<EmployeeResponse[]>({
            method: "get",
            url: `/secure/employees`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined),
                ...(searchCriterium !== undefined ? { searchCriterium } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findColleagues: (startDate?: string, endDate?: string, organizationalUnitIds?: number[], searchCriterium?: string) => axios.request<EmployeeResponse[]>({
            method: "get",
            url: `/secure/employees/find-colleagues`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined),
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined),
                ...(searchCriterium !== undefined ? { searchCriterium } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findEmployeeWithEmployments: (employeeId: number) => axios.request<EmployeeWithEmploymentsResponse>({
            method: "get",
            url: `/secure/employees/find-employee-with-employments`,
            params: {
                employeeId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getEmployeeCostCalculationExternal: (employmentId: number, year: number) => axios.request<CostCalculationExternal[]>({
            method: "get",
            url: `/secure/employee/cost-calculation-external`,
            params: {
                employmentId,
                year
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getEmployeeExpenseClaims: (startDate: string, endDate: string) => axios.request<ExpenseClaimResponse[]>({
            method: "get",
            url: `/secure/employee/expense-claims`,
            params: {
                startDate,
                endDate
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getEmployeeOrganizationalUnits: (referenceDate?: string) => axios.request<EmployeeOrganizationalUnitResponse[]>({
            method: "get",
            url: `/secure/employee/find-organizational-units`,
            params: {
                ...(referenceDate !== undefined ? { referenceDate } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getEmployeeTransportationType: () => axios.request<TransportationTypeResponse>({
            method: "get",
            url: `/secure/employee/transportation-type`
        }).then(res => res.data),
        updateEmployeeTransportationType: (payload: UpdateTransportationTypeRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/employee/transportation-type/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getEmploymentAdditionalProperties: (employmentId: number) => axios.request<EmploymentAdditionalProperty[]>({
            method: "get",
            url: `/secure/employment/additional-properties`,
            params: {
                employmentId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createEmploymentAdditionalProperty: (payload: EmploymentAdditionalProperty) => axios.request<unknown>({
            method: "post",
            url: `/secure/employment/additional-properties/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteEmploymentAdditionalProperty: (payload: DeleteEmploymentAdditionalProperty) => axios.request<unknown>({
            method: "delete",
            url: `/secure/employment/additional-properties/delete`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateEmploymentAdditionalProperty: (payload: EmploymentAdditionalProperty) => axios.request<unknown>({
            method: "put",
            url: `/secure/employment/additional-properties/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getEmploymentClaYearsValid: (employmentId: number) => axios.request<number[]>({
            method: "get",
            url: `/secure/employment/cla-years-valid`,
            params: {
                employmentId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getEndOfEmploymentByEmploymentId: (employmentId: number, endOfEmploymentSettlementDate: string) => axios.request<EndOfEmploymentSettlementResponse>({
            method: "get",
            url: `/secure/employment/end-of-employment-settlement-by-employment-id`,
            params: {
                employmentId,
                endOfEmploymentSettlementDate
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getEndOfEmploymentSettlementOverview: (organizationalUnitIds?: number[]) => axios.request<EndOfEmploymentSettlementResponse[]>({
            method: "get",
            url: `/secure/employment/end-of-employment-settlement-overview`,
            params: {
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        processEndOfEmploymentSettlement: (payload: ProcessEndOfEmploymentSettlementRequest) => axios.request<CommandResultResponse>({
            method: "post",
            url: `/secure/employment/end-of-employment-settlement-process`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findExpenseClaimById: (id: string) => axios.request<ExpenseClaimResponse>({
            method: "get",
            url: `/secure/expense-claims/find-by-id`,
            params: {
                id
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findExpenseClaimsByEmployee: (startDate: string, endDate: string, employeeId: number) => axios.request<ExpenseClaimResponse[]>({
            method: "get",
            url: `/secure/expense-claims/find-by-employee`,
            params: {
                startDate,
                endDate,
                employeeId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findExpenseClaimsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[]) => axios.request<ExpenseClaimResponse[]>({
            method: "get",
            url: `/secure/expense-claims/find-by-organizational-units`,
            params: {
                startDate,
                endDate,
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findExpenseClaimsByEmployments: (startDate: string, endDate: string, employmentId: number) => axios.request<ExpenseClaimResponse[]>({
            method: "get",
            url: `/secure/expense-claims/find-by-employments`,
            params: {
                startDate,
                endDate,
                employmentId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        approveExpenseClaims: (payload: ApproveExpenseClaimsRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/expense-claims/approve`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createExpenseClaim: (payload: CreateExpenseClaimRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/expense-claims/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        revokeExpenseClaim: (id: string) => axios.request<unknown>({
            method: "put",
            url: `/secure/expense-claims/revoke`,
            params: {
                id
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        rejectExpenseClaims: (payload: RejectExpenseClaimsRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/expense-claims/reject`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getSummary: (organizationalUnitIds: number[]) => axios.request<ExpenseClaimSummaryResponse>({
            method: "get",
            url: `/secure/expense-claims/summary`,
            params: {
                organizationalUnitIds
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        updateExpenseClaim: (payload: UpdateExpenseClaimRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/expense-claims/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createExpenseClaimsBasedOnRoutes: (startDate: string, endDate: string) => axios.request<ExpenseClaimCreateSummaryResponse>({
            method: "post",
            url: `/secure/expense-claims/create-based-on-routes`,
            params: {
                startDate,
                endDate
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getAvatar: (employeeNumber?: number) => axios.request<IdentityAvatarResponse>({
            method: "get",
            url: `/secure/identity/avatar`,
            params: {
                ...(employeeNumber !== undefined ? { employeeNumber } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        syncExternalFlexRequests: (payload: SyncExternalFlexRequestsRequest) => axios.request<SyncExternalFlexRequestsResponse>({
            method: "post",
            url: `/secure/flex-requests/sync-external`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getFunctions: (startDate: string, endDate: string, onlyPreferred?: boolean, organizationalUnit?: number, onlySelectable?: boolean) => axios.request<FunctionResponse[]>({
            method: "get",
            url: `/secure/functions`,
            params: {
                startDate,
                endDate,
                ...(onlyPreferred !== undefined ? { onlyPreferred } : undefined),
                ...(organizationalUnit !== undefined ? { organizationalUnit } : undefined),
                ...(onlySelectable !== undefined ? { onlySelectable } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getOrganizationalUnitChildren: (organizationalUnitId: number, referenceDate: string, includeAllChildren: boolean) => axios.request<number[]>({
            method: "get",
            url: `/secure/organizational-unit-children`,
            params: {
                organizationalUnitId,
                referenceDate,
                includeAllChildren
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getPeriodAssignments: (startDate?: string, endDate?: string, organizationalUnitId?: number, statuses?: PeriodAssignmentStatus[]) => axios.request<PeriodAssignmentResponse[]>({
            method: "get",
            url: `/secure/period-assignments`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined),
                ...(organizationalUnitId !== undefined ? { organizationalUnitId } : undefined),
                ...(statuses !== undefined ? { statuses } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createPeriodAssignment: (payload: CreatePeriodAssignmentRequest) => axios.request<string>({
            method: "post",
            url: `/secure/period-assignments/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updatePeriodAssignment: (payload: UpdatePeriodAssignmentRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/period-assignments/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        connectPeriodAssignment: (payload: ConnectPeriodAssignmentRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/period-assignments/connect`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        inProgressPeriodAssignment: (payload: InProgressPeriodAssignmentRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/period-assignments/in-progress`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        returnPeriodAssignment: (payload: ReturnPeriodAssignmentRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/period-assignments/return`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findMatchCandidatesForPeriodAssignment: (periodAssignmentId: string, searchCriterium?: string, includeFlex?: boolean, includeDeployableAsFlex?: boolean, checkEmploymentValidity?: boolean) => axios.request<MatchCandidateResponse[]>({
            method: "get",
            url: `/secure/period-assignments/find-match-candidates`,
            params: {
                periodAssignmentId,
                ...(searchCriterium !== undefined ? { searchCriterium } : undefined),
                ...(includeFlex !== undefined ? { includeFlex } : undefined),
                ...(includeDeployableAsFlex !== undefined ? { includeDeployableAsFlex } : undefined),
                ...(checkEmploymentValidity !== undefined ? { checkEmploymentValidity } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getPlannedSubscribedHours: () => axios.request<PlannedSubscribedHourResponse[]>({
            method: "get",
            url: `/secure/planned-subscribed-hours`
        }).then(res => res.data),
        getPlanning: (payload: GetPlanningRequest) => axios.request<PlanningResponse>({
            method: "post",
            url: `/secure/planning`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findPlanningEntities: (searchCriterium: string, types?: PlanningEntityType[], maxResultSize?: number, schedulingLabels?: SchedulingLabel[]) => axios.request<PlanningEntitiesSearchResponse[]>({
            method: "get",
            url: `/secure/planning-entities/search`,
            params: {
                searchCriterium,
                ...(types !== undefined ? { types } : undefined),
                ...(maxResultSize !== undefined ? { maxResultSize } : undefined),
                ...(schedulingLabels !== undefined ? { schedulingLabels } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getPrescriptions: (careReceiverId: string) => axios.request<PrescriptionResponse[]>({
            method: "get",
            url: `/secure/prescriptions`,
            params: {
                careReceiverId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        getProjects: (startDate?: string, endDate?: string) => axios.request<ProjectResponse[]>({
            method: "get",
            url: `/secure/projects`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createProject: (payload: CreateProjectRequest) => axios.request<string>({
            method: "post",
            url: `/secure/projects/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateProject: (payload: UpdateProjectRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/projects/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getRegionalPoolEmployeeTenantAgreements: (employmentId?: number, tenantId?: string) => axios.request<RegionalPoolEmployeeTenantAgreementResponse[]>({
            method: "get",
            url: `/secure/regional-pool-employees/tenant-agreements`,
            params: {
                ...(employmentId !== undefined ? { employmentId } : undefined),
                ...(tenantId !== undefined ? { tenantId } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        postRegionalPoolEmployeeTenantAgreements: (payload: RegionalPoolEmployeeTenantAgreementRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/regional-pool-employees/tenant-agreements`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        putRegionalPoolEmployeeTenantAgreementsId: (payload: RegionalPoolEmployeeTenantAgreementRequest, employeeTenantAgreementId: number) => axios.request<unknown>({
            method: "put",
            url: `/secure/regional-pool-employees/tenant-agreements/${employeeTenantAgreementId}`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getRegionalPoolTenants: () => axios.request<RegionalPoolTenantResponse[]>({
            method: "get",
            url: `/secure/regional-pool-tenants`
        }).then(res => res.data),
        getRegionalPools: () => axios.request<RegionalPoolTenantResponse[]>({
            method: "get",
            url: `/secure/regional-pools`
        }).then(res => res.data),
        getRegionalPoolFunctions: (regionalPoolTenantName: string) => axios.request<FunctionResponse[]>({
            method: "get",
            url: `/secure/regional-pools/${regionalPoolTenantName}/functions`
        }).then(res => res.data),
        getRegionalPoolFunctionMappings: (regionalPoolTenantName: string) => axios.request<RegionalPoolFunctionMappingResponse[]>({
            method: "get",
            url: `/secure/regional-pools/${regionalPoolTenantName}/function-mappings`
        }).then(res => res.data),
        updateRegionalPoolFunctionMapping: (payload: RegionalPoolFunctionMappingRequest, regionalPoolTenantName: string, tenantFunctionId: number) => axios.request<unknown>({
            method: "put",
            url: `/secure/regional-pools/${regionalPoolTenantName}/function-mappings/${tenantFunctionId}`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getRegionalPoolQualifications: (regionalPoolTenantName: string) => axios.request<QualificationResponse[]>({
            method: "get",
            url: `/secure/regional-pools/${regionalPoolTenantName}/qualifications`
        }).then(res => res.data),
        getRegionalPoolQualificationMappings: (regionalPoolTenantName: string) => axios.request<RegionalPoolQualificationMappingResponse[]>({
            method: "get",
            url: `/secure/regional-pools/${regionalPoolTenantName}/qualification-mappings`
        }).then(res => res.data),
        updateRegionalPoolQualificationMapping: (payload: RegionalPoolQualificationMappingRequest, regionalPoolTenantName: string, tenantQualificationId: string) => axios.request<unknown>({
            method: "put",
            url: `/secure/regional-pools/${regionalPoolTenantName}/qualification-mappings/${tenantQualificationId}`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findResources: () => axios.request<ResourceResponse[]>({
            method: "get",
            url: `/secure/resources`
        }).then(res => res.data),
        createResource: (payload: CreateResourceRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/resources/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findResourceById: (id: string) => axios.request<ResourceResponse>({
            method: "get",
            url: `/secure/resources/find-by-id`,
            params: {
                id
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        updateResource: (payload: UpdateResourceRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/resources/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        connectSchedule: (payload: ConnectScheduleRequest) => axios.request<SchedulingActionResultResponse>({
            method: "put",
            url: `/secure/planning/connect-schedule`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        bulkConnectSchedules: (payload: BulkConnectSchedulesRequest) => axios.request<SchedulingActionResultResponse>({
            method: "post",
            url: `/secure/planning/bulk-connect-schedules`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        connectSelfMatchDuties: (payload: ConnectSelfMatchDutiesRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/self-match/duties/connect`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        respondSelfMatchPeriodAssignments: (payload: RespondSelfMatchPeriodAssignmentRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/self-match-period-assignment/respond`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getSelfMatchPeriodAssignments: (startDate?: string, endDate?: string, organizationalUnitIds?: number[]) => axios.request<SelfMatchPeriodAssignmentResponse[]>({
            method: "get",
            url: `/secure/self-match-period-assignment/stack`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined),
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        undoChanges: (payload: UndoCommandRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/time-travel/undo-changes`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getTransportationTypes: () => axios.request<TransportationTypeResponse[]>({
            method: "get",
            url: `/secure/transportation-types/`
        }).then(res => res.data),
        updateTransportationType: (payload: UpdateTransportationTypeExportCodesRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/transportation-types/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        correctTravelMovementPerDay: (payload: CorrectTravelMovementPerDayRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/travel-movements/correct`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        findTravelMovementsByEmployee: (startDate: string, endDate: string, employeeId: number) => axios.request<TravelMovementPerDayResponse[]>({
            method: "get",
            url: `/secure/travel-movements/find-by-employee`,
            params: {
                startDate,
                endDate,
                employeeId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        findTravelMovementsByOrganizationalUnits: (startDate: string, endDate: string, onlyApprovableTravelMovements?: boolean, organizationalUnitIds?: number[]) => axios.request<TravelMovementPerDayResponse[]>({
            method: "get",
            url: `/secure/travel-movements/find-by-organizational-units`,
            params: {
                startDate,
                endDate,
                ...(onlyApprovableTravelMovements !== undefined ? { onlyApprovableTravelMovements } : undefined),
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createTravelSet: (payload: CreateTravelSetRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/travelsets/create/`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateTravelSet: (payload: UpdateTravelSetRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/travelsets/update/`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createFavorite: (payload: UserFavoriteRequest) => axios.request<string>({
            method: "post",
            url: `/secure/user/favorite/create/`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteFavorite: (payload: UserFavoriteRequest) => axios.request<unknown>({
            method: "delete",
            url: `/secure/user/favorite/delete/`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        getWorkPlaces: (startDate?: string, endDate?: string, organizationalUnitIds?: number[]) => axios.request<WorkPlaceResponse[]>({
            method: "get",
            url: `/secure/work-places`,
            params: {
                ...(startDate !== undefined ? { startDate } : undefined),
                ...(endDate !== undefined ? { endDate } : undefined),
                ...(organizationalUnitIds !== undefined ? { organizationalUnitIds } : undefined)
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        createWorkPlace: (payload: CreateWorkPlaceRequest) => axios.request<string>({
            method: "post",
            url: `/secure/work-places/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateWorkPlace: (payload: UpdateWorkPlaceRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/work-places/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        createWorkPlaceAtOrganizationalUnitAssignment: (payload: CreateWorkPlaceAtOrganizationalUnitAssignmentRequest) => axios.request<string>({
            method: "post",
            url: `/secure/work-places/organizational-unit-assignments/create`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        updateWorkPlaceAtOrganizationalUnitAssignment: (payload: UpdateWorkPlaceAtOrganizationalUnitAssignmentRequest) => axios.request<unknown>({
            method: "put",
            url: `/secure/work-places/organizational-unit-assignments/update`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data),
        deleteWorkPlaceAtOrganizationalUnitAssignment: (entityId: string) => axios.request<unknown>({
            method: "delete",
            url: `/secure/work-places/organizational-unit-assignments/delete`,
            params: {
                entityId
            },
            paramsSerializer: config?.paramsSerializer
        }).then(res => res.data),
        sortWorkPlaceAtOrganizationalUnitAssignment: (payload: SortWorkPlaceAtOrganizationalUnitAssignmentRequest) => axios.request<unknown>({
            method: "post",
            url: `/secure/work-places/organizational-unit-assignments/sort`,
            data: payload,
            headers: {
                "Content-Type": "application/json"
            }
        }).then(res => res.data)
    } as const;
}
export type Requests = ReturnType<typeof makeRequests>;
export type Response<T extends keyof Requests> = Awaited<ReturnType<Requests[T]>>;
function makeQueries(requests: Requests) {
    return {
        useGetAbsenceNotices: (startDate: string, endDate: string, options?: Omit<UseQueryOptions<Response<"getAbsenceNotices">, unknown, Response<"getAbsenceNotices">, ReturnType<QueryKeys["getAbsenceNotices"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getAbsenceNotices">, unknown> => useQuery({ queryKey: queryKeys.getAbsenceNotices(startDate, endDate), queryFn: () => requests.getAbsenceNotices(startDate, endDate), ...options }),
        useGetActivities: (schedulingLabels?: SchedulingLabel[], clientRelatedTypes?: ClientRelatedType[], withRequirements?: boolean, options?: Omit<UseQueryOptions<Response<"getActivities">, unknown, Response<"getActivities">, ReturnType<QueryKeys["getActivities"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getActivities">, unknown> => useQuery({ queryKey: queryKeys.getActivities(schedulingLabels, clientRelatedTypes, withRequirements), queryFn: () => requests.getActivities(schedulingLabels, clientRelatedTypes, withRequirements), ...options }),
        useSecureAdminProceduresClosureYear: (options?: Omit<UseQueryOptions<Response<"secureAdminProceduresClosureYear">, unknown, Response<"secureAdminProceduresClosureYear">, ReturnType<QueryKeys["secureAdminProceduresClosureYear"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"secureAdminProceduresClosureYear">, unknown> => useQuery({ queryKey: queryKeys.secureAdminProceduresClosureYear(), queryFn: () => requests.secureAdminProceduresClosureYear(), ...options }),
        useSecureAdminProceduresLastLockedMonth: (options?: Omit<UseQueryOptions<Response<"secureAdminProceduresLastLockedMonth">, unknown, Response<"secureAdminProceduresLastLockedMonth">, ReturnType<QueryKeys["secureAdminProceduresLastLockedMonth"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"secureAdminProceduresLastLockedMonth">, unknown> => useQuery({ queryKey: queryKeys.secureAdminProceduresLastLockedMonth(), queryFn: () => requests.secureAdminProceduresLastLockedMonth(), ...options }),
        useFindAdditionalCompensationsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"findAdditionalCompensationsByOrganizationalUnits">, unknown, Response<"findAdditionalCompensationsByOrganizationalUnits">, ReturnType<QueryKeys["findAdditionalCompensationsByOrganizationalUnits"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findAdditionalCompensationsByOrganizationalUnits">, unknown> => useQuery({ queryKey: queryKeys.findAdditionalCompensationsByOrganizationalUnits(startDate, endDate, organizationalUnitIds), queryFn: () => requests.findAdditionalCompensationsByOrganizationalUnits(startDate, endDate, organizationalUnitIds), ...options }),
        useGetAppointmentExceptions: (appointmentId: string, start: string, options?: Omit<UseQueryOptions<Response<"getAppointmentExceptions">, unknown, Response<"getAppointmentExceptions">, ReturnType<QueryKeys["getAppointmentExceptions"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getAppointmentExceptions">, unknown> => useQuery({ queryKey: queryKeys.getAppointmentExceptions(appointmentId, start), queryFn: () => requests.getAppointmentExceptions(appointmentId, start), ...options }),
        useImportChangedAppointments: (start?: string, end?: string, options?: Omit<UseQueryOptions<Response<"importChangedAppointments">, unknown, Response<"importChangedAppointments">, ReturnType<QueryKeys["importChangedAppointments"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"importChangedAppointments">, unknown> => useQuery({ queryKey: queryKeys.importChangedAppointments(start, end), queryFn: () => requests.importChangedAppointments(start, end), ...options }),
        useGetImportAppointmentsSyncStatuses: (from?: string, until?: string, status?: SynchronizationStatus, options?: Omit<UseQueryOptions<Response<"getImportAppointmentsSyncStatuses">, unknown, Response<"getImportAppointmentsSyncStatuses">, ReturnType<QueryKeys["getImportAppointmentsSyncStatuses"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getImportAppointmentsSyncStatuses">, unknown> => useQuery({ queryKey: queryKeys.getImportAppointmentsSyncStatuses(from, until, status), queryFn: () => requests.getImportAppointmentsSyncStatuses(from, until, status), ...options }),
        useGetAppointmentSeries: (originalAppointmentId: string, options?: Omit<UseQueryOptions<Response<"getAppointmentSeries">, unknown, Response<"getAppointmentSeries">, ReturnType<QueryKeys["getAppointmentSeries"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getAppointmentSeries">, unknown> => useQuery({ queryKey: queryKeys.getAppointmentSeries(originalAppointmentId), queryFn: () => requests.getAppointmentSeries(originalAppointmentId), ...options }),
        useFindMatchCandidatesForAppointments: (appointmentEntityId: string, date: string, organizationalUnitIds?: number[], searchCriterium?: string, checkEmploymentValidity?: boolean, options?: Omit<UseQueryOptions<Response<"findMatchCandidatesForAppointments">, unknown, Response<"findMatchCandidatesForAppointments">, ReturnType<QueryKeys["findMatchCandidatesForAppointments"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findMatchCandidatesForAppointments">, unknown> => useQuery({ queryKey: queryKeys.findMatchCandidatesForAppointments(appointmentEntityId, date, organizationalUnitIds, searchCriterium, checkEmploymentValidity), queryFn: () => requests.findMatchCandidatesForAppointments(appointmentEntityId, date, organizationalUnitIds, searchCriterium, checkEmploymentValidity), ...options }),
        useGetAppointmentGroupings: (organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"getAppointmentGroupings">, unknown, Response<"getAppointmentGroupings">, ReturnType<QueryKeys["getAppointmentGroupings"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getAppointmentGroupings">, unknown> => useQuery({ queryKey: queryKeys.getAppointmentGroupings(organizationalUnitIds), queryFn: () => requests.getAppointmentGroupings(organizationalUnitIds), ...options }),
        useFindMatchCandidatesForAppointmentGrouping: (appointmentGroupingId: string, date: string, organizationalUnitIds?: number[], searchCriterium?: string, checkEmploymentValidity?: boolean, options?: Omit<UseQueryOptions<Response<"findMatchCandidatesForAppointmentGrouping">, unknown, Response<"findMatchCandidatesForAppointmentGrouping">, ReturnType<QueryKeys["findMatchCandidatesForAppointmentGrouping"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findMatchCandidatesForAppointmentGrouping">, unknown> => useQuery({ queryKey: queryKeys.findMatchCandidatesForAppointmentGrouping(appointmentGroupingId, date, organizationalUnitIds, searchCriterium, checkEmploymentValidity), queryFn: () => requests.findMatchCandidatesForAppointmentGrouping(appointmentGroupingId, date, organizationalUnitIds, searchCriterium, checkEmploymentValidity), ...options }),
        useFindAuditedCommands: (startDate?: string, endDate?: string, options?: Omit<UseQueryOptions<Response<"findAuditedCommands">, unknown, Response<"findAuditedCommands">, ReturnType<QueryKeys["findAuditedCommands"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findAuditedCommands">, unknown> => useQuery({ queryKey: queryKeys.findAuditedCommands(startDate, endDate), queryFn: () => requests.findAuditedCommands(startDate, endDate), ...options }),
        useGetCalculatedAppointmentOccurrencesSyncStatuses: (from?: string, until?: string, status?: SynchronizationStatus, options?: Omit<UseQueryOptions<Response<"getCalculatedAppointmentOccurrencesSyncStatuses">, unknown, Response<"getCalculatedAppointmentOccurrencesSyncStatuses">, ReturnType<QueryKeys["getCalculatedAppointmentOccurrencesSyncStatuses"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getCalculatedAppointmentOccurrencesSyncStatuses">, unknown> => useQuery({ queryKey: queryKeys.getCalculatedAppointmentOccurrencesSyncStatuses(from, until, status), queryFn: () => requests.getCalculatedAppointmentOccurrencesSyncStatuses(from, until, status), ...options }),
        useGetClients: (organizationalUnitIds?: number[], searchCriterium?: string, schedulingLabels?: SchedulingLabel[], options?: Omit<UseQueryOptions<Response<"getClients">, unknown, Response<"getClients">, ReturnType<QueryKeys["getClients"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getClients">, unknown> => useQuery({ queryKey: queryKeys.getClients(organizationalUnitIds, searchCriterium, schedulingLabels), queryFn: () => requests.getClients(organizationalUnitIds, searchCriterium, schedulingLabels), ...options }),
        useFindConfigurationSettings: (options?: Omit<UseQueryOptions<Response<"findConfigurationSettings">, unknown, Response<"findConfigurationSettings">, ReturnType<QueryKeys["findConfigurationSettings"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findConfigurationSettings">, unknown> => useQuery({ queryKey: queryKeys.findConfigurationSettings(), queryFn: () => requests.findConfigurationSettings(), ...options }),
        useFindConfigurationById: (id: string, options?: Omit<UseQueryOptions<Response<"findConfigurationById">, unknown, Response<"findConfigurationById">, ReturnType<QueryKeys["findConfigurationById"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findConfigurationById">, unknown> => useQuery({ queryKey: queryKeys.findConfigurationById(id), queryFn: () => requests.findConfigurationById(id), ...options }),
        useGetDayLimitArrangements: (options?: Omit<UseQueryOptions<Response<"getDayLimitArrangements">, unknown, Response<"getDayLimitArrangements">, ReturnType<QueryKeys["getDayLimitArrangements"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDayLimitArrangements">, unknown> => useQuery({ queryKey: queryKeys.getDayLimitArrangements(), queryFn: () => requests.getDayLimitArrangements(), ...options }),
        useGetDayLimitArrangementsForEmployee: (employeeId: number, options?: Omit<UseQueryOptions<Response<"getDayLimitArrangementsForEmployee">, unknown, Response<"getDayLimitArrangementsForEmployee">, ReturnType<QueryKeys["getDayLimitArrangementsForEmployee"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDayLimitArrangementsForEmployee">, unknown> => useQuery({ queryKey: queryKeys.getDayLimitArrangementsForEmployee(employeeId), queryFn: () => requests.getDayLimitArrangementsForEmployee(employeeId), ...options }),
        useFindDayLimitViolationsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"findDayLimitViolationsByOrganizationalUnits">, unknown, Response<"findDayLimitViolationsByOrganizationalUnits">, ReturnType<QueryKeys["findDayLimitViolationsByOrganizationalUnits"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findDayLimitViolationsByOrganizationalUnits">, unknown> => useQuery({ queryKey: queryKeys.findDayLimitViolationsByOrganizationalUnits(startDate, endDate, organizationalUnitIds), queryFn: () => requests.findDayLimitViolationsByOrganizationalUnits(startDate, endDate, organizationalUnitIds), ...options }),
        useGetDeclarationTypes: (startDate?: string, endDate?: string, options?: Omit<UseQueryOptions<Response<"getDeclarationTypes">, unknown, Response<"getDeclarationTypes">, ReturnType<QueryKeys["getDeclarationTypes"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDeclarationTypes">, unknown> => useQuery({ queryKey: queryKeys.getDeclarationTypes(startDate, endDate), queryFn: () => requests.getDeclarationTypes(startDate, endDate), ...options }),
        useGetAskedDuties: (startDate: string, endDate: string, options?: Omit<UseQueryOptions<Response<"getAskedDuties">, unknown, Response<"getAskedDuties">, ReturnType<QueryKeys["getAskedDuties"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getAskedDuties">, unknown> => useQuery({ queryKey: queryKeys.getAskedDuties(startDate, endDate), queryFn: () => requests.getAskedDuties(startDate, endDate), ...options }),
        useDummyReference: (options?: Omit<UseQueryOptions<Response<"dummyReference">, unknown, Response<"dummyReference">, ReturnType<QueryKeys["dummyReference"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"dummyReference">, unknown> => useQuery({ queryKey: queryKeys.dummyReference(), queryFn: () => requests.dummyReference(), ...options }),
        useGetDuties: (startDate: string, endDate: string, employeeNumbers?: number[], includeAssignedDuties?: boolean, includeRequestedDuties?: boolean, includeOpenDuties?: boolean, includePlannedDuties?: boolean, includeRetractedDuties?: boolean, includeTravelDistance?: boolean, includeOptionalEmployeesForPlannedDuties?: boolean, options?: Omit<UseQueryOptions<Response<"getDuties">, unknown, Response<"getDuties">, ReturnType<QueryKeys["getDuties"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDuties">, unknown> => useQuery({ queryKey: queryKeys.getDuties(startDate, endDate, employeeNumbers, includeAssignedDuties, includeRequestedDuties, includeOpenDuties, includePlannedDuties, includeRetractedDuties, includeTravelDistance, includeOptionalEmployeesForPlannedDuties), queryFn: () => requests.getDuties(startDate, endDate, employeeNumbers, includeAssignedDuties, includeRequestedDuties, includeOpenDuties, includePlannedDuties, includeRetractedDuties, includeTravelDistance, includeOptionalEmployeesForPlannedDuties), ...options }),
        useGetDutyAskRoundSettings: (options?: Omit<UseQueryOptions<Response<"getDutyAskRoundSettings">, unknown, Response<"getDutyAskRoundSettings">, ReturnType<QueryKeys["getDutyAskRoundSettings"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDutyAskRoundSettings">, unknown> => useQuery({ queryKey: queryKeys.getDutyAskRoundSettings(), queryFn: () => requests.getDutyAskRoundSettings(), ...options }),
        useGetDutyCalculationValueSleepDuties: (validFrom?: string, validTo?: string, options?: Omit<UseQueryOptions<Response<"getDutyCalculationValueSleepDuties">, unknown, Response<"getDutyCalculationValueSleepDuties">, ReturnType<QueryKeys["getDutyCalculationValueSleepDuties"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDutyCalculationValueSleepDuties">, unknown> => useQuery({ queryKey: queryKeys.getDutyCalculationValueSleepDuties(validFrom, validTo), queryFn: () => requests.getDutyCalculationValueSleepDuties(validFrom, validTo), ...options }),
        useGetDutyCodes: (referenceDate?: string, organizationalUnitId?: number, includeRequestExcluded?: boolean, options?: Omit<UseQueryOptions<Response<"getDutyCodes">, unknown, Response<"getDutyCodes">, ReturnType<QueryKeys["getDutyCodes"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDutyCodes">, unknown> => useQuery({ queryKey: queryKeys.getDutyCodes(referenceDate, organizationalUnitId, includeRequestExcluded), queryFn: () => requests.getDutyCodes(referenceDate, organizationalUnitId, includeRequestExcluded), ...options }),
        useGetDutyCode: (dutyCodeId?: number, options?: Omit<UseQueryOptions<Response<"getDutyCode">, unknown, Response<"getDutyCode">, ReturnType<QueryKeys["getDutyCode"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDutyCode">, unknown> => useQuery({ queryKey: queryKeys.getDutyCode(dutyCodeId), queryFn: () => requests.getDutyCode(dutyCodeId), ...options }),
        useGetDutyHourTypes: (referenceDate?: string, checkRestrictedAuthorization?: boolean, options?: Omit<UseQueryOptions<Response<"getDutyHourTypes">, unknown, Response<"getDutyHourTypes">, ReturnType<QueryKeys["getDutyHourTypes"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDutyHourTypes">, unknown> => useQuery({ queryKey: queryKeys.getDutyHourTypes(referenceDate, checkRestrictedAuthorization), queryFn: () => requests.getDutyHourTypes(referenceDate, checkRestrictedAuthorization), ...options }),
        useGetDutyPeriodsBlocksWithDtrViolations: (employeeId: number, startDate: string, endDate: string, options?: Omit<UseQueryOptions<Response<"getDutyPeriodsBlocksWithDtrViolations">, unknown, Response<"getDutyPeriodsBlocksWithDtrViolations">, ReturnType<QueryKeys["getDutyPeriodsBlocksWithDtrViolations"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getDutyPeriodsBlocksWithDtrViolations">, unknown> => useQuery({ queryKey: queryKeys.getDutyPeriodsBlocksWithDtrViolations(employeeId, startDate, endDate), queryFn: () => requests.getDutyPeriodsBlocksWithDtrViolations(employeeId, startDate, endDate), ...options }),
        useGetCostCalculationExternalSurcharges: (employmentId?: number, organizationalUnitId?: number, validFrom?: string, validTo?: string, options?: Omit<UseQueryOptions<Response<"getCostCalculationExternalSurcharges">, unknown, Response<"getCostCalculationExternalSurcharges">, ReturnType<QueryKeys["getCostCalculationExternalSurcharges"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getCostCalculationExternalSurcharges">, unknown> => useQuery({ queryKey: queryKeys.getCostCalculationExternalSurcharges(employmentId, organizationalUnitId, validFrom, validTo), queryFn: () => requests.getCostCalculationExternalSurcharges(employmentId, organizationalUnitId, validFrom, validTo), ...options }),
        useGetCostCalculationExternalSurchargeSettings: (employmentId?: number, organizationalUnitId?: number, validFrom?: string, validTo?: string, options?: Omit<UseQueryOptions<Response<"getCostCalculationExternalSurchargeSettings">, unknown, Response<"getCostCalculationExternalSurchargeSettings">, ReturnType<QueryKeys["getCostCalculationExternalSurchargeSettings"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getCostCalculationExternalSurchargeSettings">, unknown> => useQuery({ queryKey: queryKeys.getCostCalculationExternalSurchargeSettings(employmentId, organizationalUnitId, validFrom, validTo), queryFn: () => requests.getCostCalculationExternalSurchargeSettings(employmentId, organizationalUnitId, validFrom, validTo), ...options }),
        useGetEmployeeTypes: (options?: Omit<UseQueryOptions<Response<"getEmployeeTypes">, unknown, Response<"getEmployeeTypes">, ReturnType<QueryKeys["getEmployeeTypes"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmployeeTypes">, unknown> => useQuery({ queryKey: queryKeys.getEmployeeTypes(), queryFn: () => requests.getEmployeeTypes(), ...options }),
        useGetEmployees: (startDate?: string, endDate?: string, searchCriterium?: string, options?: Omit<UseQueryOptions<Response<"getEmployees">, unknown, Response<"getEmployees">, ReturnType<QueryKeys["getEmployees"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmployees">, unknown> => useQuery({ queryKey: queryKeys.getEmployees(startDate, endDate, searchCriterium), queryFn: () => requests.getEmployees(startDate, endDate, searchCriterium), ...options }),
        useFindColleagues: (startDate?: string, endDate?: string, organizationalUnitIds?: number[], searchCriterium?: string, options?: Omit<UseQueryOptions<Response<"findColleagues">, unknown, Response<"findColleagues">, ReturnType<QueryKeys["findColleagues"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findColleagues">, unknown> => useQuery({ queryKey: queryKeys.findColleagues(startDate, endDate, organizationalUnitIds, searchCriterium), queryFn: () => requests.findColleagues(startDate, endDate, organizationalUnitIds, searchCriterium), ...options }),
        useFindEmployeeWithEmployments: (employeeId: number, options?: Omit<UseQueryOptions<Response<"findEmployeeWithEmployments">, unknown, Response<"findEmployeeWithEmployments">, ReturnType<QueryKeys["findEmployeeWithEmployments"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findEmployeeWithEmployments">, unknown> => useQuery({ queryKey: queryKeys.findEmployeeWithEmployments(employeeId), queryFn: () => requests.findEmployeeWithEmployments(employeeId), ...options }),
        useGetEmployeeCostCalculationExternal: (employmentId: number, year: number, options?: Omit<UseQueryOptions<Response<"getEmployeeCostCalculationExternal">, unknown, Response<"getEmployeeCostCalculationExternal">, ReturnType<QueryKeys["getEmployeeCostCalculationExternal"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmployeeCostCalculationExternal">, unknown> => useQuery({ queryKey: queryKeys.getEmployeeCostCalculationExternal(employmentId, year), queryFn: () => requests.getEmployeeCostCalculationExternal(employmentId, year), ...options }),
        useGetEmployeeExpenseClaims: (startDate: string, endDate: string, options?: Omit<UseQueryOptions<Response<"getEmployeeExpenseClaims">, unknown, Response<"getEmployeeExpenseClaims">, ReturnType<QueryKeys["getEmployeeExpenseClaims"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmployeeExpenseClaims">, unknown> => useQuery({ queryKey: queryKeys.getEmployeeExpenseClaims(startDate, endDate), queryFn: () => requests.getEmployeeExpenseClaims(startDate, endDate), ...options }),
        useGetEmployeeOrganizationalUnits: (referenceDate?: string, options?: Omit<UseQueryOptions<Response<"getEmployeeOrganizationalUnits">, unknown, Response<"getEmployeeOrganizationalUnits">, ReturnType<QueryKeys["getEmployeeOrganizationalUnits"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmployeeOrganizationalUnits">, unknown> => useQuery({ queryKey: queryKeys.getEmployeeOrganizationalUnits(referenceDate), queryFn: () => requests.getEmployeeOrganizationalUnits(referenceDate), ...options }),
        useGetEmployeeTransportationType: (options?: Omit<UseQueryOptions<Response<"getEmployeeTransportationType">, unknown, Response<"getEmployeeTransportationType">, ReturnType<QueryKeys["getEmployeeTransportationType"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmployeeTransportationType">, unknown> => useQuery({ queryKey: queryKeys.getEmployeeTransportationType(), queryFn: () => requests.getEmployeeTransportationType(), ...options }),
        useGetEmploymentAdditionalProperties: (employmentId: number, options?: Omit<UseQueryOptions<Response<"getEmploymentAdditionalProperties">, unknown, Response<"getEmploymentAdditionalProperties">, ReturnType<QueryKeys["getEmploymentAdditionalProperties"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmploymentAdditionalProperties">, unknown> => useQuery({ queryKey: queryKeys.getEmploymentAdditionalProperties(employmentId), queryFn: () => requests.getEmploymentAdditionalProperties(employmentId), ...options }),
        useGetEmploymentClaYearsValid: (employmentId: number, options?: Omit<UseQueryOptions<Response<"getEmploymentClaYearsValid">, unknown, Response<"getEmploymentClaYearsValid">, ReturnType<QueryKeys["getEmploymentClaYearsValid"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEmploymentClaYearsValid">, unknown> => useQuery({ queryKey: queryKeys.getEmploymentClaYearsValid(employmentId), queryFn: () => requests.getEmploymentClaYearsValid(employmentId), ...options }),
        useGetEndOfEmploymentByEmploymentId: (employmentId: number, endOfEmploymentSettlementDate: string, options?: Omit<UseQueryOptions<Response<"getEndOfEmploymentByEmploymentId">, unknown, Response<"getEndOfEmploymentByEmploymentId">, ReturnType<QueryKeys["getEndOfEmploymentByEmploymentId"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEndOfEmploymentByEmploymentId">, unknown> => useQuery({ queryKey: queryKeys.getEndOfEmploymentByEmploymentId(employmentId, endOfEmploymentSettlementDate), queryFn: () => requests.getEndOfEmploymentByEmploymentId(employmentId, endOfEmploymentSettlementDate), ...options }),
        useGetEndOfEmploymentSettlementOverview: (organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"getEndOfEmploymentSettlementOverview">, unknown, Response<"getEndOfEmploymentSettlementOverview">, ReturnType<QueryKeys["getEndOfEmploymentSettlementOverview"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getEndOfEmploymentSettlementOverview">, unknown> => useQuery({ queryKey: queryKeys.getEndOfEmploymentSettlementOverview(organizationalUnitIds), queryFn: () => requests.getEndOfEmploymentSettlementOverview(organizationalUnitIds), ...options }),
        useFindExpenseClaimById: (id: string, options?: Omit<UseQueryOptions<Response<"findExpenseClaimById">, unknown, Response<"findExpenseClaimById">, ReturnType<QueryKeys["findExpenseClaimById"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findExpenseClaimById">, unknown> => useQuery({ queryKey: queryKeys.findExpenseClaimById(id), queryFn: () => requests.findExpenseClaimById(id), ...options }),
        useFindExpenseClaimsByEmployee: (startDate: string, endDate: string, employeeId: number, options?: Omit<UseQueryOptions<Response<"findExpenseClaimsByEmployee">, unknown, Response<"findExpenseClaimsByEmployee">, ReturnType<QueryKeys["findExpenseClaimsByEmployee"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findExpenseClaimsByEmployee">, unknown> => useQuery({ queryKey: queryKeys.findExpenseClaimsByEmployee(startDate, endDate, employeeId), queryFn: () => requests.findExpenseClaimsByEmployee(startDate, endDate, employeeId), ...options }),
        useFindExpenseClaimsByOrganizationalUnits: (startDate: string, endDate: string, organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"findExpenseClaimsByOrganizationalUnits">, unknown, Response<"findExpenseClaimsByOrganizationalUnits">, ReturnType<QueryKeys["findExpenseClaimsByOrganizationalUnits"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findExpenseClaimsByOrganizationalUnits">, unknown> => useQuery({ queryKey: queryKeys.findExpenseClaimsByOrganizationalUnits(startDate, endDate, organizationalUnitIds), queryFn: () => requests.findExpenseClaimsByOrganizationalUnits(startDate, endDate, organizationalUnitIds), ...options }),
        useFindExpenseClaimsByEmployments: (startDate: string, endDate: string, employmentId: number, options?: Omit<UseQueryOptions<Response<"findExpenseClaimsByEmployments">, unknown, Response<"findExpenseClaimsByEmployments">, ReturnType<QueryKeys["findExpenseClaimsByEmployments"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findExpenseClaimsByEmployments">, unknown> => useQuery({ queryKey: queryKeys.findExpenseClaimsByEmployments(startDate, endDate, employmentId), queryFn: () => requests.findExpenseClaimsByEmployments(startDate, endDate, employmentId), ...options }),
        useGetSummary: (organizationalUnitIds: number[], options?: Omit<UseQueryOptions<Response<"getSummary">, unknown, Response<"getSummary">, ReturnType<QueryKeys["getSummary"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getSummary">, unknown> => useQuery({ queryKey: queryKeys.getSummary(organizationalUnitIds), queryFn: () => requests.getSummary(organizationalUnitIds), ...options }),
        useGetAvatar: (employeeNumber?: number, options?: Omit<UseQueryOptions<Response<"getAvatar">, unknown, Response<"getAvatar">, ReturnType<QueryKeys["getAvatar"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getAvatar">, unknown> => useQuery({ queryKey: queryKeys.getAvatar(employeeNumber), queryFn: () => requests.getAvatar(employeeNumber), ...options }),
        useGetFunctions: (startDate: string, endDate: string, onlyPreferred?: boolean, organizationalUnit?: number, onlySelectable?: boolean, options?: Omit<UseQueryOptions<Response<"getFunctions">, unknown, Response<"getFunctions">, ReturnType<QueryKeys["getFunctions"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getFunctions">, unknown> => useQuery({ queryKey: queryKeys.getFunctions(startDate, endDate, onlyPreferred, organizationalUnit, onlySelectable), queryFn: () => requests.getFunctions(startDate, endDate, onlyPreferred, organizationalUnit, onlySelectable), ...options }),
        useGetOrganizationalUnitChildren: (organizationalUnitId: number, referenceDate: string, includeAllChildren: boolean, options?: Omit<UseQueryOptions<Response<"getOrganizationalUnitChildren">, unknown, Response<"getOrganizationalUnitChildren">, ReturnType<QueryKeys["getOrganizationalUnitChildren"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getOrganizationalUnitChildren">, unknown> => useQuery({ queryKey: queryKeys.getOrganizationalUnitChildren(organizationalUnitId, referenceDate, includeAllChildren), queryFn: () => requests.getOrganizationalUnitChildren(organizationalUnitId, referenceDate, includeAllChildren), ...options }),
        useGetPeriodAssignments: (startDate?: string, endDate?: string, organizationalUnitId?: number, statuses?: PeriodAssignmentStatus[], options?: Omit<UseQueryOptions<Response<"getPeriodAssignments">, unknown, Response<"getPeriodAssignments">, ReturnType<QueryKeys["getPeriodAssignments"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getPeriodAssignments">, unknown> => useQuery({ queryKey: queryKeys.getPeriodAssignments(startDate, endDate, organizationalUnitId, statuses), queryFn: () => requests.getPeriodAssignments(startDate, endDate, organizationalUnitId, statuses), ...options }),
        useFindMatchCandidatesForPeriodAssignment: (periodAssignmentId: string, searchCriterium?: string, includeFlex?: boolean, includeDeployableAsFlex?: boolean, checkEmploymentValidity?: boolean, options?: Omit<UseQueryOptions<Response<"findMatchCandidatesForPeriodAssignment">, unknown, Response<"findMatchCandidatesForPeriodAssignment">, ReturnType<QueryKeys["findMatchCandidatesForPeriodAssignment"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findMatchCandidatesForPeriodAssignment">, unknown> => useQuery({ queryKey: queryKeys.findMatchCandidatesForPeriodAssignment(periodAssignmentId, searchCriterium, includeFlex, includeDeployableAsFlex, checkEmploymentValidity), queryFn: () => requests.findMatchCandidatesForPeriodAssignment(periodAssignmentId, searchCriterium, includeFlex, includeDeployableAsFlex, checkEmploymentValidity), ...options }),
        useGetPlannedSubscribedHours: (options?: Omit<UseQueryOptions<Response<"getPlannedSubscribedHours">, unknown, Response<"getPlannedSubscribedHours">, ReturnType<QueryKeys["getPlannedSubscribedHours"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getPlannedSubscribedHours">, unknown> => useQuery({ queryKey: queryKeys.getPlannedSubscribedHours(), queryFn: () => requests.getPlannedSubscribedHours(), ...options }),
        useFindPlanningEntities: (searchCriterium: string, types?: PlanningEntityType[], maxResultSize?: number, schedulingLabels?: SchedulingLabel[], options?: Omit<UseQueryOptions<Response<"findPlanningEntities">, unknown, Response<"findPlanningEntities">, ReturnType<QueryKeys["findPlanningEntities"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findPlanningEntities">, unknown> => useQuery({ queryKey: queryKeys.findPlanningEntities(searchCriterium, types, maxResultSize, schedulingLabels), queryFn: () => requests.findPlanningEntities(searchCriterium, types, maxResultSize, schedulingLabels), ...options }),
        useGetPrescriptions: (careReceiverId: string, options?: Omit<UseQueryOptions<Response<"getPrescriptions">, unknown, Response<"getPrescriptions">, ReturnType<QueryKeys["getPrescriptions"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getPrescriptions">, unknown> => useQuery({ queryKey: queryKeys.getPrescriptions(careReceiverId), queryFn: () => requests.getPrescriptions(careReceiverId), ...options }),
        useGetProjects: (startDate?: string, endDate?: string, options?: Omit<UseQueryOptions<Response<"getProjects">, unknown, Response<"getProjects">, ReturnType<QueryKeys["getProjects"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getProjects">, unknown> => useQuery({ queryKey: queryKeys.getProjects(startDate, endDate), queryFn: () => requests.getProjects(startDate, endDate), ...options }),
        useGetRegionalPoolEmployeeTenantAgreements: (employmentId?: number, tenantId?: string, options?: Omit<UseQueryOptions<Response<"getRegionalPoolEmployeeTenantAgreements">, unknown, Response<"getRegionalPoolEmployeeTenantAgreements">, ReturnType<QueryKeys["getRegionalPoolEmployeeTenantAgreements"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPoolEmployeeTenantAgreements">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPoolEmployeeTenantAgreements(employmentId, tenantId), queryFn: () => requests.getRegionalPoolEmployeeTenantAgreements(employmentId, tenantId), ...options }),
        useGetRegionalPoolTenants: (options?: Omit<UseQueryOptions<Response<"getRegionalPoolTenants">, unknown, Response<"getRegionalPoolTenants">, ReturnType<QueryKeys["getRegionalPoolTenants"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPoolTenants">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPoolTenants(), queryFn: () => requests.getRegionalPoolTenants(), ...options }),
        useGetRegionalPools: (options?: Omit<UseQueryOptions<Response<"getRegionalPools">, unknown, Response<"getRegionalPools">, ReturnType<QueryKeys["getRegionalPools"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPools">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPools(), queryFn: () => requests.getRegionalPools(), ...options }),
        useGetRegionalPoolFunctions: (regionalPoolTenantName: string, options?: Omit<UseQueryOptions<Response<"getRegionalPoolFunctions">, unknown, Response<"getRegionalPoolFunctions">, ReturnType<QueryKeys["getRegionalPoolFunctions"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPoolFunctions">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPoolFunctions(regionalPoolTenantName), queryFn: () => requests.getRegionalPoolFunctions(regionalPoolTenantName), ...options }),
        useGetRegionalPoolFunctionMappings: (regionalPoolTenantName: string, options?: Omit<UseQueryOptions<Response<"getRegionalPoolFunctionMappings">, unknown, Response<"getRegionalPoolFunctionMappings">, ReturnType<QueryKeys["getRegionalPoolFunctionMappings"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPoolFunctionMappings">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPoolFunctionMappings(regionalPoolTenantName), queryFn: () => requests.getRegionalPoolFunctionMappings(regionalPoolTenantName), ...options }),
        useGetRegionalPoolQualifications: (regionalPoolTenantName: string, options?: Omit<UseQueryOptions<Response<"getRegionalPoolQualifications">, unknown, Response<"getRegionalPoolQualifications">, ReturnType<QueryKeys["getRegionalPoolQualifications"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPoolQualifications">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPoolQualifications(regionalPoolTenantName), queryFn: () => requests.getRegionalPoolQualifications(regionalPoolTenantName), ...options }),
        useGetRegionalPoolQualificationMappings: (regionalPoolTenantName: string, options?: Omit<UseQueryOptions<Response<"getRegionalPoolQualificationMappings">, unknown, Response<"getRegionalPoolQualificationMappings">, ReturnType<QueryKeys["getRegionalPoolQualificationMappings"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getRegionalPoolQualificationMappings">, unknown> => useQuery({ queryKey: queryKeys.getRegionalPoolQualificationMappings(regionalPoolTenantName), queryFn: () => requests.getRegionalPoolQualificationMappings(regionalPoolTenantName), ...options }),
        useFindResources: (options?: Omit<UseQueryOptions<Response<"findResources">, unknown, Response<"findResources">, ReturnType<QueryKeys["findResources"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findResources">, unknown> => useQuery({ queryKey: queryKeys.findResources(), queryFn: () => requests.findResources(), ...options }),
        useFindResourceById: (id: string, options?: Omit<UseQueryOptions<Response<"findResourceById">, unknown, Response<"findResourceById">, ReturnType<QueryKeys["findResourceById"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findResourceById">, unknown> => useQuery({ queryKey: queryKeys.findResourceById(id), queryFn: () => requests.findResourceById(id), ...options }),
        useGetSelfMatchPeriodAssignments: (startDate?: string, endDate?: string, organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"getSelfMatchPeriodAssignments">, unknown, Response<"getSelfMatchPeriodAssignments">, ReturnType<QueryKeys["getSelfMatchPeriodAssignments"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getSelfMatchPeriodAssignments">, unknown> => useQuery({ queryKey: queryKeys.getSelfMatchPeriodAssignments(startDate, endDate, organizationalUnitIds), queryFn: () => requests.getSelfMatchPeriodAssignments(startDate, endDate, organizationalUnitIds), ...options }),
        useGetTransportationTypes: (options?: Omit<UseQueryOptions<Response<"getTransportationTypes">, unknown, Response<"getTransportationTypes">, ReturnType<QueryKeys["getTransportationTypes"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getTransportationTypes">, unknown> => useQuery({ queryKey: queryKeys.getTransportationTypes(), queryFn: () => requests.getTransportationTypes(), ...options }),
        useFindTravelMovementsByEmployee: (startDate: string, endDate: string, employeeId: number, options?: Omit<UseQueryOptions<Response<"findTravelMovementsByEmployee">, unknown, Response<"findTravelMovementsByEmployee">, ReturnType<QueryKeys["findTravelMovementsByEmployee"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findTravelMovementsByEmployee">, unknown> => useQuery({ queryKey: queryKeys.findTravelMovementsByEmployee(startDate, endDate, employeeId), queryFn: () => requests.findTravelMovementsByEmployee(startDate, endDate, employeeId), ...options }),
        useFindTravelMovementsByOrganizationalUnits: (startDate: string, endDate: string, onlyApprovableTravelMovements?: boolean, organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"findTravelMovementsByOrganizationalUnits">, unknown, Response<"findTravelMovementsByOrganizationalUnits">, ReturnType<QueryKeys["findTravelMovementsByOrganizationalUnits"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"findTravelMovementsByOrganizationalUnits">, unknown> => useQuery({ queryKey: queryKeys.findTravelMovementsByOrganizationalUnits(startDate, endDate, onlyApprovableTravelMovements, organizationalUnitIds), queryFn: () => requests.findTravelMovementsByOrganizationalUnits(startDate, endDate, onlyApprovableTravelMovements, organizationalUnitIds), ...options }),
        useGetWorkPlaces: (startDate?: string, endDate?: string, organizationalUnitIds?: number[], options?: Omit<UseQueryOptions<Response<"getWorkPlaces">, unknown, Response<"getWorkPlaces">, ReturnType<QueryKeys["getWorkPlaces"]>>, "queryKey" | "queryFn">): UseQueryResult<Response<"getWorkPlaces">, unknown> => useQuery({ queryKey: queryKeys.getWorkPlaces(startDate, endDate, organizationalUnitIds), queryFn: () => requests.getWorkPlaces(startDate, endDate, organizationalUnitIds), ...options })
    } as const;
}
type MutationConfigs = {
    useCreateAbsenceNotice?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createAbsenceNotice">, unknown, Parameters<Requests["createAbsenceNotice"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useProcessAbsenceNotice?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"processAbsenceNotice">, unknown, Parameters<Requests["processAbsenceNotice"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSuggestedActivities?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"suggestedActivities">, unknown, Parameters<Requests["suggestedActivities"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateActivity?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createActivity">, unknown, Parameters<Requests["createActivity"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateActivity?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateActivity">, unknown, Parameters<Requests["updateActivity"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSecureAdminProceduresAbsenceReasonsSynchronize?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"secureAdminProceduresAbsenceReasonsSynchronize">, unknown, Parameters<Requests["secureAdminProceduresAbsenceReasonsSynchronize"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSecureAdminProceduresOrganizationalUnitsSynchronize?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"secureAdminProceduresOrganizationalUnitsSynchronize">, unknown, Parameters<Requests["secureAdminProceduresOrganizationalUnitsSynchronize"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSecureAdminProceduresBalanceBudgetGift?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"secureAdminProceduresBalanceBudgetGift">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useApproveAppointments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"approveAppointments">, unknown, Parameters<Requests["approveAppointments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreditAppointments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"creditAppointments">, unknown, Parameters<Requests["creditAppointments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useEndClientAbsenceForAppointment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"endClientAbsenceForAppointment">, unknown, Parameters<Requests["endClientAbsenceForAppointment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSubmitAppointments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"submitAppointments">, unknown, Parameters<Requests["submitAppointments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCopyAppointmentsInPeriod?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"copyAppointmentsInPeriod">, unknown, Parameters<Requests["copyAppointmentsInPeriod"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateAppointment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createAppointment">, unknown, Parameters<Requests["createAppointment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useAppointmentBulkDeleteAction?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"appointmentBulkDeleteAction">, unknown, Parameters<Requests["appointmentBulkDeleteAction"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useAppointmentBulkEditParticipantsAction?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"appointmentBulkEditParticipantsAction">, unknown, Parameters<Requests["appointmentBulkEditParticipantsAction"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useImportAppointments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"importAppointments">, unknown, Parameters<Requests["importAppointments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateAppointment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateAppointment">, unknown, Parameters<Requests["updateAppointment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteAppointment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteAppointment">, unknown, Parameters<Requests["deleteAppointment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    usePlanAppointments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"planAppointments">, unknown, Parameters<Requests["planAppointments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useAssignEmploymentToAppointment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"assignEmploymentToAppointment">, unknown, Parameters<Requests["assignEmploymentToAppointment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useGetAppointmentFiltered?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"getAppointmentFiltered">, unknown, Parameters<Requests["getAppointmentFiltered"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useRemoveAssignmentFromAppointments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"removeAssignmentFromAppointments">, unknown, Parameters<Requests["removeAssignmentFromAppointments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateAppointmentGrouping?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createAppointmentGrouping">, unknown, Parameters<Requests["createAppointmentGrouping"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateAppointmentGrouping?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateAppointmentGrouping">, unknown, Parameters<Requests["updateAppointmentGrouping"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSyncAppointmentGroupingsWithDuties?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"syncAppointmentGroupingsWithDuties">, unknown, Parameters<Requests["syncAppointmentGroupingsWithDuties"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useAssignEmploymentToAppointmentGrouping?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"assignEmploymentToAppointmentGrouping">, unknown, Parameters<Requests["assignEmploymentToAppointmentGrouping"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useRemoveAssignmentEmploymentToAppointmentGrouping?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"removeAssignmentEmploymentToAppointmentGrouping">, unknown, Parameters<Requests["removeAssignmentEmploymentToAppointmentGrouping"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateAppointmentGroupingTransportationMode?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateAppointmentGroupingTransportationMode">, unknown, Parameters<Requests["updateAppointmentGroupingTransportationMode"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateAvailabilityParticipants?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createAvailabilityParticipants">, unknown, Parameters<Requests["createAvailabilityParticipants"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteAvailabilityParticipants?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteAvailabilityParticipants">, unknown, Parameters<Requests["deleteAvailabilityParticipants"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateAvailabilityParticipants?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateAvailabilityParticipants">, unknown, Parameters<Requests["updateAvailabilityParticipants"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useExportCalculatedAppointmentOccurrences?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"exportCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["exportCalculatedAppointmentOccurrences"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useExportCalculatedAppointmentOccurrencesBySeries?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"exportCalculatedAppointmentOccurrencesBySeries">, unknown, Parameters<Requests["exportCalculatedAppointmentOccurrencesBySeries"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSynchronizeCalculatedAppointmentOccurrences?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"synchronizeCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["synchronizeCalculatedAppointmentOccurrences"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useSynchronizeAndExportCalculatedAppointmentOccurrences?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"synchronizeAndExportCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["synchronizeAndExportCalculatedAppointmentOccurrences"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useExtendAppointmentSeriesHorizon?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"extendAppointmentSeriesHorizon">, unknown, Parameters<Requests["extendAppointmentSeriesHorizon"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useImportCareReceivers?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"importCareReceivers">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useImportChangedCareReceivers?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"importChangedCareReceivers">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useImportAllCareReceivers?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"importAllCareReceivers">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateConfiguration?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createConfiguration">, unknown, Parameters<Requests["createConfiguration"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateConfigurationPlanningEntities?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createConfigurationPlanningEntities">, unknown, Parameters<Requests["createConfigurationPlanningEntities"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateConfigurationPlanningEntityForAiBasicConfiguration?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createConfigurationPlanningEntityForAiBasicConfiguration">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteConfigurationPlanningEntities?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteConfigurationPlanningEntities">, unknown, Parameters<Requests["deleteConfigurationPlanningEntities"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteConfigurationPlanningEntityForAiBasicConfiguration?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteConfigurationPlanningEntityForAiBasicConfiguration">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateConfigurationSettings?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createConfigurationSettings">, unknown, Parameters<Requests["createConfigurationSettings"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteConfigurationSettings?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteConfigurationSettings">, unknown, Parameters<Requests["deleteConfigurationSettings"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateConfigurationSettings?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateConfigurationSettings">, unknown, Parameters<Requests["updateConfigurationSettings"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateConfiguration?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateConfiguration">, unknown, Parameters<Requests["updateConfiguration"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateDayLimitArrangement?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createDayLimitArrangement">, unknown, Parameters<Requests["createDayLimitArrangement"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateDayLimitArrangement?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateDayLimitArrangement">, unknown, Parameters<Requests["updateDayLimitArrangement"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteDayLimitArrangement?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteDayLimitArrangement">, unknown, Parameters<Requests["deleteDayLimitArrangement"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateDayLimitArrangementForEmployee?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createDayLimitArrangementForEmployee">, unknown, Parameters<Requests["createDayLimitArrangementForEmployee"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateDayLimitArrangementForEmployee?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateDayLimitArrangementForEmployee">, unknown, Parameters<Requests["updateDayLimitArrangementForEmployee"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteDayLimitArrangementForEmployee?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteDayLimitArrangementForEmployee">, unknown, Parameters<Requests["deleteDayLimitArrangementForEmployee"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateDayLimitViolationPayOut?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createDayLimitViolationPayOut">, unknown, Parameters<Requests["createDayLimitViolationPayOut"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateDeclarationType?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createDeclarationType">, unknown, Parameters<Requests["createDeclarationType"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteDeclarationType?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteDeclarationType">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateDeclarationType?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateDeclarationType">, unknown, Parameters<Requests["updateDeclarationType"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useAskEmployeeForDuty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"askEmployeeForDuty">, unknown, Parameters<Requests["askEmployeeForDuty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useAskEmployeesForDuty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"askEmployeesForDuty">, unknown, Parameters<Requests["askEmployeesForDuty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateDutyAskRoundSettings?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateDutyAskRoundSettings">, unknown, Parameters<Requests["updateDutyAskRoundSettings"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateDutyCalculationValueSleepDuty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["createDutyCalculationValueSleepDuty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteDutyCalculationValueSleepDuty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["deleteDutyCalculationValueSleepDuty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateDutyCalculationValueSleepDuty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["updateDutyCalculationValueSleepDuty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateDutyCode?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createDutyCode">, unknown, Parameters<Requests["createDutyCode"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateDutyCode?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateDutyCode">, unknown, Parameters<Requests["updateDutyCode"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteDutyCode?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteDutyCode">, unknown, Parameters<Requests["deleteDutyCode"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useRecalculateCostCalculationExternalSurcharge?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"recalculateCostCalculationExternalSurcharge">, unknown, Parameters<Requests["recalculateCostCalculationExternalSurcharge"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateCostCalculationExternalSurcharges?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createCostCalculationExternalSurcharges">, unknown, Parameters<Requests["createCostCalculationExternalSurcharges"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateCostCalculationExternalSurcharges?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateCostCalculationExternalSurcharges">, unknown, Parameters<Requests["updateCostCalculationExternalSurcharges"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateCostCalculationExternalSurchargeSettings?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createCostCalculationExternalSurchargeSettings">, unknown, Parameters<Requests["createCostCalculationExternalSurchargeSettings"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateCostCalculationExternalSurchargeSettings?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateCostCalculationExternalSurchargeSettings">, unknown, Parameters<Requests["updateCostCalculationExternalSurchargeSettings"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateEmployeeTransportationType?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateEmployeeTransportationType">, unknown, Parameters<Requests["updateEmployeeTransportationType"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateEmploymentAdditionalProperty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createEmploymentAdditionalProperty">, unknown, Parameters<Requests["createEmploymentAdditionalProperty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteEmploymentAdditionalProperty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteEmploymentAdditionalProperty">, unknown, Parameters<Requests["deleteEmploymentAdditionalProperty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateEmploymentAdditionalProperty?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateEmploymentAdditionalProperty">, unknown, Parameters<Requests["updateEmploymentAdditionalProperty"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useProcessEndOfEmploymentSettlement?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"processEndOfEmploymentSettlement">, unknown, Parameters<Requests["processEndOfEmploymentSettlement"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useApproveExpenseClaims?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"approveExpenseClaims">, unknown, Parameters<Requests["approveExpenseClaims"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateExpenseClaim?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createExpenseClaim">, unknown, Parameters<Requests["createExpenseClaim"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useRevokeExpenseClaim?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"revokeExpenseClaim">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useRejectExpenseClaims?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"rejectExpenseClaims">, unknown, Parameters<Requests["rejectExpenseClaims"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateExpenseClaim?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateExpenseClaim">, unknown, Parameters<Requests["updateExpenseClaim"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateExpenseClaimsBasedOnRoutes?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createExpenseClaimsBasedOnRoutes">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useSyncExternalFlexRequests?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"syncExternalFlexRequests">, unknown, Parameters<Requests["syncExternalFlexRequests"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreatePeriodAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createPeriodAssignment">, unknown, Parameters<Requests["createPeriodAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdatePeriodAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updatePeriodAssignment">, unknown, Parameters<Requests["updatePeriodAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useConnectPeriodAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"connectPeriodAssignment">, unknown, Parameters<Requests["connectPeriodAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useInProgressPeriodAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"inProgressPeriodAssignment">, unknown, Parameters<Requests["inProgressPeriodAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useReturnPeriodAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"returnPeriodAssignment">, unknown, Parameters<Requests["returnPeriodAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useGetPlanning?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"getPlanning">, unknown, Parameters<Requests["getPlanning"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateProject?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createProject">, unknown, Parameters<Requests["createProject"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateProject?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateProject">, unknown, Parameters<Requests["updateProject"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    usePostRegionalPoolEmployeeTenantAgreements?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"postRegionalPoolEmployeeTenantAgreements">, unknown, Parameters<Requests["postRegionalPoolEmployeeTenantAgreements"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    usePutRegionalPoolEmployeeTenantAgreementsId?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"putRegionalPoolEmployeeTenantAgreementsId">, unknown, Parameters<Requests["putRegionalPoolEmployeeTenantAgreementsId"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateRegionalPoolFunctionMapping?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateRegionalPoolFunctionMapping">, unknown, Parameters<Requests["updateRegionalPoolFunctionMapping"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateRegionalPoolQualificationMapping?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateRegionalPoolQualificationMapping">, unknown, Parameters<Requests["updateRegionalPoolQualificationMapping"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateResource?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createResource">, unknown, Parameters<Requests["createResource"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateResource?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateResource">, unknown, Parameters<Requests["updateResource"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useConnectSchedule?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"connectSchedule">, unknown, Parameters<Requests["connectSchedule"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useBulkConnectSchedules?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"bulkConnectSchedules">, unknown, Parameters<Requests["bulkConnectSchedules"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useConnectSelfMatchDuties?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"connectSelfMatchDuties">, unknown, Parameters<Requests["connectSelfMatchDuties"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useRespondSelfMatchPeriodAssignments?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"respondSelfMatchPeriodAssignments">, unknown, Parameters<Requests["respondSelfMatchPeriodAssignments"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUndoChanges?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"undoChanges">, unknown, Parameters<Requests["undoChanges"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateTransportationType?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateTransportationType">, unknown, Parameters<Requests["updateTransportationType"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCorrectTravelMovementPerDay?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"correctTravelMovementPerDay">, unknown, Parameters<Requests["correctTravelMovementPerDay"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateTravelSet?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createTravelSet">, unknown, Parameters<Requests["createTravelSet"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateTravelSet?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateTravelSet">, unknown, Parameters<Requests["updateTravelSet"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateFavorite?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createFavorite">, unknown, Parameters<Requests["createFavorite"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteFavorite?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteFavorite">, unknown, Parameters<Requests["deleteFavorite"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateWorkPlace?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createWorkPlace">, unknown, Parameters<Requests["createWorkPlace"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateWorkPlace?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateWorkPlace">, unknown, Parameters<Requests["updateWorkPlace"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useCreateWorkPlaceAtOrganizationalUnitAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"createWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["createWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useUpdateWorkPlaceAtOrganizationalUnitAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"updateWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["updateWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
    useDeleteWorkPlaceAtOrganizationalUnitAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"deleteWorkPlaceAtOrganizationalUnitAssignment">, unknown, unknown, unknown>, "onSuccess" | "onSettled" | "onError">;
    useSortWorkPlaceAtOrganizationalUnitAssignment?: (queryClient: QueryClient) => Pick<UseMutationOptions<Response<"sortWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["sortWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>, "onSuccess" | "onSettled" | "onError">;
};
function makeMutations(requests: Requests, config?: Config["mutations"]) {
    return {
        useCreateAbsenceNotice: (options?: Omit<UseMutationOptions<Response<"createAbsenceNotice">, unknown, Parameters<Requests["createAbsenceNotice"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createAbsenceNotice">, unknown, Parameters<Requests["createAbsenceNotice"]>[0], unknown>(payload => requests.createAbsenceNotice(payload), config?.useCreateAbsenceNotice, options),
        useProcessAbsenceNotice: (options?: Omit<UseMutationOptions<Response<"processAbsenceNotice">, unknown, Parameters<Requests["processAbsenceNotice"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"processAbsenceNotice">, unknown, Parameters<Requests["processAbsenceNotice"]>[0], unknown>(payload => requests.processAbsenceNotice(payload), config?.useProcessAbsenceNotice, options),
        useSuggestedActivities: (options?: Omit<UseMutationOptions<Response<"suggestedActivities">, unknown, Parameters<Requests["suggestedActivities"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"suggestedActivities">, unknown, Parameters<Requests["suggestedActivities"]>[0], unknown>(payload => requests.suggestedActivities(payload), config?.useSuggestedActivities, options),
        useCreateActivity: (options?: Omit<UseMutationOptions<Response<"createActivity">, unknown, Parameters<Requests["createActivity"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createActivity">, unknown, Parameters<Requests["createActivity"]>[0], unknown>(payload => requests.createActivity(payload), config?.useCreateActivity, options),
        useUpdateActivity: (options?: Omit<UseMutationOptions<Response<"updateActivity">, unknown, Parameters<Requests["updateActivity"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateActivity">, unknown, Parameters<Requests["updateActivity"]>[0], unknown>(payload => requests.updateActivity(payload), config?.useUpdateActivity, options),
        useSecureAdminProceduresAbsenceReasonsSynchronize: (options?: Omit<UseMutationOptions<Response<"secureAdminProceduresAbsenceReasonsSynchronize">, unknown, Parameters<Requests["secureAdminProceduresAbsenceReasonsSynchronize"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"secureAdminProceduresAbsenceReasonsSynchronize">, unknown, Parameters<Requests["secureAdminProceduresAbsenceReasonsSynchronize"]>[0], unknown>(payload => requests.secureAdminProceduresAbsenceReasonsSynchronize(payload), config?.useSecureAdminProceduresAbsenceReasonsSynchronize, options),
        useSecureAdminProceduresOrganizationalUnitsSynchronize: (options?: Omit<UseMutationOptions<Response<"secureAdminProceduresOrganizationalUnitsSynchronize">, unknown, Parameters<Requests["secureAdminProceduresOrganizationalUnitsSynchronize"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"secureAdminProceduresOrganizationalUnitsSynchronize">, unknown, Parameters<Requests["secureAdminProceduresOrganizationalUnitsSynchronize"]>[0], unknown>(payload => requests.secureAdminProceduresOrganizationalUnitsSynchronize(payload), config?.useSecureAdminProceduresOrganizationalUnitsSynchronize, options),
        useSecureAdminProceduresBalanceBudgetGift: (options?: Omit<UseMutationOptions<Response<"secureAdminProceduresBalanceBudgetGift">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"secureAdminProceduresBalanceBudgetGift">, unknown, unknown, unknown>(() => requests.secureAdminProceduresBalanceBudgetGift(), config?.useSecureAdminProceduresBalanceBudgetGift, options),
        useApproveAppointments: (options?: Omit<UseMutationOptions<Response<"approveAppointments">, unknown, Parameters<Requests["approveAppointments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"approveAppointments">, unknown, Parameters<Requests["approveAppointments"]>[0], unknown>(payload => requests.approveAppointments(payload), config?.useApproveAppointments, options),
        useCreditAppointments: (options?: Omit<UseMutationOptions<Response<"creditAppointments">, unknown, Parameters<Requests["creditAppointments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"creditAppointments">, unknown, Parameters<Requests["creditAppointments"]>[0], unknown>(payload => requests.creditAppointments(payload), config?.useCreditAppointments, options),
        useEndClientAbsenceForAppointment: (options?: Omit<UseMutationOptions<Response<"endClientAbsenceForAppointment">, unknown, Parameters<Requests["endClientAbsenceForAppointment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"endClientAbsenceForAppointment">, unknown, Parameters<Requests["endClientAbsenceForAppointment"]>[0], unknown>(payload => requests.endClientAbsenceForAppointment(payload), config?.useEndClientAbsenceForAppointment, options),
        useSubmitAppointments: (options?: Omit<UseMutationOptions<Response<"submitAppointments">, unknown, Parameters<Requests["submitAppointments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"submitAppointments">, unknown, Parameters<Requests["submitAppointments"]>[0], unknown>(payload => requests.submitAppointments(payload), config?.useSubmitAppointments, options),
        useCopyAppointmentsInPeriod: (options?: Omit<UseMutationOptions<Response<"copyAppointmentsInPeriod">, unknown, Parameters<Requests["copyAppointmentsInPeriod"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"copyAppointmentsInPeriod">, unknown, Parameters<Requests["copyAppointmentsInPeriod"]>[0], unknown>(payload => requests.copyAppointmentsInPeriod(payload), config?.useCopyAppointmentsInPeriod, options),
        useCreateAppointment: (options?: Omit<UseMutationOptions<Response<"createAppointment">, unknown, Parameters<Requests["createAppointment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createAppointment">, unknown, Parameters<Requests["createAppointment"]>[0], unknown>(payload => requests.createAppointment(payload), config?.useCreateAppointment, options),
        useAppointmentBulkDeleteAction: (options?: Omit<UseMutationOptions<Response<"appointmentBulkDeleteAction">, unknown, Parameters<Requests["appointmentBulkDeleteAction"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"appointmentBulkDeleteAction">, unknown, Parameters<Requests["appointmentBulkDeleteAction"]>[0], unknown>(payload => requests.appointmentBulkDeleteAction(payload), config?.useAppointmentBulkDeleteAction, options),
        useAppointmentBulkEditParticipantsAction: (options?: Omit<UseMutationOptions<Response<"appointmentBulkEditParticipantsAction">, unknown, Parameters<Requests["appointmentBulkEditParticipantsAction"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"appointmentBulkEditParticipantsAction">, unknown, Parameters<Requests["appointmentBulkEditParticipantsAction"]>[0], unknown>(payload => requests.appointmentBulkEditParticipantsAction(payload), config?.useAppointmentBulkEditParticipantsAction, options),
        useImportAppointments: (options?: Omit<UseMutationOptions<Response<"importAppointments">, unknown, Parameters<Requests["importAppointments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"importAppointments">, unknown, Parameters<Requests["importAppointments"]>[0], unknown>(payload => requests.importAppointments(payload), config?.useImportAppointments, options),
        useUpdateAppointment: (options?: Omit<UseMutationOptions<Response<"updateAppointment">, unknown, Parameters<Requests["updateAppointment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateAppointment">, unknown, Parameters<Requests["updateAppointment"]>[0], unknown>(payload => requests.updateAppointment(payload), config?.useUpdateAppointment, options),
        useDeleteAppointment: (options?: Omit<UseMutationOptions<Response<"deleteAppointment">, unknown, Parameters<Requests["deleteAppointment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteAppointment">, unknown, Parameters<Requests["deleteAppointment"]>[0], unknown>(payload => requests.deleteAppointment(payload), config?.useDeleteAppointment, options),
        usePlanAppointments: (options?: Omit<UseMutationOptions<Response<"planAppointments">, unknown, Parameters<Requests["planAppointments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"planAppointments">, unknown, Parameters<Requests["planAppointments"]>[0], unknown>(payload => requests.planAppointments(payload), config?.usePlanAppointments, options),
        useAssignEmploymentToAppointment: (options?: Omit<UseMutationOptions<Response<"assignEmploymentToAppointment">, unknown, Parameters<Requests["assignEmploymentToAppointment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"assignEmploymentToAppointment">, unknown, Parameters<Requests["assignEmploymentToAppointment"]>[0], unknown>(payload => requests.assignEmploymentToAppointment(payload), config?.useAssignEmploymentToAppointment, options),
        useGetAppointmentFiltered: (options?: Omit<UseMutationOptions<Response<"getAppointmentFiltered">, unknown, Parameters<Requests["getAppointmentFiltered"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"getAppointmentFiltered">, unknown, Parameters<Requests["getAppointmentFiltered"]>[0], unknown>(payload => requests.getAppointmentFiltered(payload), config?.useGetAppointmentFiltered, options),
        useRemoveAssignmentFromAppointments: (options?: Omit<UseMutationOptions<Response<"removeAssignmentFromAppointments">, unknown, Parameters<Requests["removeAssignmentFromAppointments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"removeAssignmentFromAppointments">, unknown, Parameters<Requests["removeAssignmentFromAppointments"]>[0], unknown>(payload => requests.removeAssignmentFromAppointments(payload), config?.useRemoveAssignmentFromAppointments, options),
        useCreateAppointmentGrouping: (options?: Omit<UseMutationOptions<Response<"createAppointmentGrouping">, unknown, Parameters<Requests["createAppointmentGrouping"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createAppointmentGrouping">, unknown, Parameters<Requests["createAppointmentGrouping"]>[0], unknown>(payload => requests.createAppointmentGrouping(payload), config?.useCreateAppointmentGrouping, options),
        useUpdateAppointmentGrouping: (options?: Omit<UseMutationOptions<Response<"updateAppointmentGrouping">, unknown, Parameters<Requests["updateAppointmentGrouping"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateAppointmentGrouping">, unknown, Parameters<Requests["updateAppointmentGrouping"]>[0], unknown>(payload => requests.updateAppointmentGrouping(payload), config?.useUpdateAppointmentGrouping, options),
        useSyncAppointmentGroupingsWithDuties: (options?: Omit<UseMutationOptions<Response<"syncAppointmentGroupingsWithDuties">, unknown, Parameters<Requests["syncAppointmentGroupingsWithDuties"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"syncAppointmentGroupingsWithDuties">, unknown, Parameters<Requests["syncAppointmentGroupingsWithDuties"]>[0], unknown>(payload => requests.syncAppointmentGroupingsWithDuties(payload), config?.useSyncAppointmentGroupingsWithDuties, options),
        useAssignEmploymentToAppointmentGrouping: (appointmentGroupingEntityId: string, options?: Omit<UseMutationOptions<Response<"assignEmploymentToAppointmentGrouping">, unknown, Parameters<Requests["assignEmploymentToAppointmentGrouping"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"assignEmploymentToAppointmentGrouping">, unknown, Parameters<Requests["assignEmploymentToAppointmentGrouping"]>[0], unknown>(payload => requests.assignEmploymentToAppointmentGrouping(payload, appointmentGroupingEntityId), config?.useAssignEmploymentToAppointmentGrouping, options),
        useRemoveAssignmentEmploymentToAppointmentGrouping: (appointmentGroupingEntityId: string, options?: Omit<UseMutationOptions<Response<"removeAssignmentEmploymentToAppointmentGrouping">, unknown, Parameters<Requests["removeAssignmentEmploymentToAppointmentGrouping"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"removeAssignmentEmploymentToAppointmentGrouping">, unknown, Parameters<Requests["removeAssignmentEmploymentToAppointmentGrouping"]>[0], unknown>(payload => requests.removeAssignmentEmploymentToAppointmentGrouping(payload, appointmentGroupingEntityId), config?.useRemoveAssignmentEmploymentToAppointmentGrouping, options),
        useUpdateAppointmentGroupingTransportationMode: (appointmentGroupingEntityId: string, options?: Omit<UseMutationOptions<Response<"updateAppointmentGroupingTransportationMode">, unknown, Parameters<Requests["updateAppointmentGroupingTransportationMode"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateAppointmentGroupingTransportationMode">, unknown, Parameters<Requests["updateAppointmentGroupingTransportationMode"]>[0], unknown>(payload => requests.updateAppointmentGroupingTransportationMode(payload, appointmentGroupingEntityId), config?.useUpdateAppointmentGroupingTransportationMode, options),
        useCreateAvailabilityParticipants: (options?: Omit<UseMutationOptions<Response<"createAvailabilityParticipants">, unknown, Parameters<Requests["createAvailabilityParticipants"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createAvailabilityParticipants">, unknown, Parameters<Requests["createAvailabilityParticipants"]>[0], unknown>(payload => requests.createAvailabilityParticipants(payload), config?.useCreateAvailabilityParticipants, options),
        useDeleteAvailabilityParticipants: (options?: Omit<UseMutationOptions<Response<"deleteAvailabilityParticipants">, unknown, Parameters<Requests["deleteAvailabilityParticipants"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteAvailabilityParticipants">, unknown, Parameters<Requests["deleteAvailabilityParticipants"]>[0], unknown>(payload => requests.deleteAvailabilityParticipants(payload), config?.useDeleteAvailabilityParticipants, options),
        useUpdateAvailabilityParticipants: (options?: Omit<UseMutationOptions<Response<"updateAvailabilityParticipants">, unknown, Parameters<Requests["updateAvailabilityParticipants"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateAvailabilityParticipants">, unknown, Parameters<Requests["updateAvailabilityParticipants"]>[0], unknown>(payload => requests.updateAvailabilityParticipants(payload), config?.useUpdateAvailabilityParticipants, options),
        useExportCalculatedAppointmentOccurrences: (options?: Omit<UseMutationOptions<Response<"exportCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["exportCalculatedAppointmentOccurrences"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"exportCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["exportCalculatedAppointmentOccurrences"]>[0], unknown>(payload => requests.exportCalculatedAppointmentOccurrences(payload), config?.useExportCalculatedAppointmentOccurrences, options),
        useExportCalculatedAppointmentOccurrencesBySeries: (options?: Omit<UseMutationOptions<Response<"exportCalculatedAppointmentOccurrencesBySeries">, unknown, Parameters<Requests["exportCalculatedAppointmentOccurrencesBySeries"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"exportCalculatedAppointmentOccurrencesBySeries">, unknown, Parameters<Requests["exportCalculatedAppointmentOccurrencesBySeries"]>[0], unknown>(payload => requests.exportCalculatedAppointmentOccurrencesBySeries(payload), config?.useExportCalculatedAppointmentOccurrencesBySeries, options),
        useSynchronizeCalculatedAppointmentOccurrences: (options?: Omit<UseMutationOptions<Response<"synchronizeCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["synchronizeCalculatedAppointmentOccurrences"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"synchronizeCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["synchronizeCalculatedAppointmentOccurrences"]>[0], unknown>(payload => requests.synchronizeCalculatedAppointmentOccurrences(payload), config?.useSynchronizeCalculatedAppointmentOccurrences, options),
        useSynchronizeAndExportCalculatedAppointmentOccurrences: (options?: Omit<UseMutationOptions<Response<"synchronizeAndExportCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["synchronizeAndExportCalculatedAppointmentOccurrences"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"synchronizeAndExportCalculatedAppointmentOccurrences">, unknown, Parameters<Requests["synchronizeAndExportCalculatedAppointmentOccurrences"]>[0], unknown>(payload => requests.synchronizeAndExportCalculatedAppointmentOccurrences(payload), config?.useSynchronizeAndExportCalculatedAppointmentOccurrences, options),
        useExtendAppointmentSeriesHorizon: (options?: Omit<UseMutationOptions<Response<"extendAppointmentSeriesHorizon">, unknown, Parameters<Requests["extendAppointmentSeriesHorizon"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"extendAppointmentSeriesHorizon">, unknown, Parameters<Requests["extendAppointmentSeriesHorizon"]>[0], unknown>(payload => requests.extendAppointmentSeriesHorizon(payload), config?.useExtendAppointmentSeriesHorizon, options),
        useImportCareReceivers: (careReceiverIds: string[], options?: Omit<UseMutationOptions<Response<"importCareReceivers">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"importCareReceivers">, unknown, unknown, unknown>(() => requests.importCareReceivers(careReceiverIds), config?.useImportCareReceivers, options),
        useImportChangedCareReceivers: (start?: string, end?: string, options?: Omit<UseMutationOptions<Response<"importChangedCareReceivers">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"importChangedCareReceivers">, unknown, unknown, unknown>(() => requests.importChangedCareReceivers(start, end), config?.useImportChangedCareReceivers, options),
        useImportAllCareReceivers: (options?: Omit<UseMutationOptions<Response<"importAllCareReceivers">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"importAllCareReceivers">, unknown, unknown, unknown>(() => requests.importAllCareReceivers(), config?.useImportAllCareReceivers, options),
        useCreateConfiguration: (options?: Omit<UseMutationOptions<Response<"createConfiguration">, unknown, Parameters<Requests["createConfiguration"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createConfiguration">, unknown, Parameters<Requests["createConfiguration"]>[0], unknown>(payload => requests.createConfiguration(payload), config?.useCreateConfiguration, options),
        useCreateConfigurationPlanningEntities: (options?: Omit<UseMutationOptions<Response<"createConfigurationPlanningEntities">, unknown, Parameters<Requests["createConfigurationPlanningEntities"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createConfigurationPlanningEntities">, unknown, Parameters<Requests["createConfigurationPlanningEntities"]>[0], unknown>(payload => requests.createConfigurationPlanningEntities(payload), config?.useCreateConfigurationPlanningEntities, options),
        useCreateConfigurationPlanningEntityForAiBasicConfiguration: (organizationalUnitEntityId: string, options?: Omit<UseMutationOptions<Response<"createConfigurationPlanningEntityForAiBasicConfiguration">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"createConfigurationPlanningEntityForAiBasicConfiguration">, unknown, unknown, unknown>(() => requests.createConfigurationPlanningEntityForAiBasicConfiguration(organizationalUnitEntityId), config?.useCreateConfigurationPlanningEntityForAiBasicConfiguration, options),
        useDeleteConfigurationPlanningEntities: (options?: Omit<UseMutationOptions<Response<"deleteConfigurationPlanningEntities">, unknown, Parameters<Requests["deleteConfigurationPlanningEntities"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteConfigurationPlanningEntities">, unknown, Parameters<Requests["deleteConfigurationPlanningEntities"]>[0], unknown>(payload => requests.deleteConfigurationPlanningEntities(payload), config?.useDeleteConfigurationPlanningEntities, options),
        useDeleteConfigurationPlanningEntityForAiBasicConfiguration: (organizationalUnitEntityId: string, options?: Omit<UseMutationOptions<Response<"deleteConfigurationPlanningEntityForAiBasicConfiguration">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteConfigurationPlanningEntityForAiBasicConfiguration">, unknown, unknown, unknown>(() => requests.deleteConfigurationPlanningEntityForAiBasicConfiguration(organizationalUnitEntityId), config?.useDeleteConfigurationPlanningEntityForAiBasicConfiguration, options),
        useCreateConfigurationSettings: (options?: Omit<UseMutationOptions<Response<"createConfigurationSettings">, unknown, Parameters<Requests["createConfigurationSettings"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createConfigurationSettings">, unknown, Parameters<Requests["createConfigurationSettings"]>[0], unknown>(payload => requests.createConfigurationSettings(payload), config?.useCreateConfigurationSettings, options),
        useDeleteConfigurationSettings: (options?: Omit<UseMutationOptions<Response<"deleteConfigurationSettings">, unknown, Parameters<Requests["deleteConfigurationSettings"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteConfigurationSettings">, unknown, Parameters<Requests["deleteConfigurationSettings"]>[0], unknown>(payload => requests.deleteConfigurationSettings(payload), config?.useDeleteConfigurationSettings, options),
        useUpdateConfigurationSettings: (options?: Omit<UseMutationOptions<Response<"updateConfigurationSettings">, unknown, Parameters<Requests["updateConfigurationSettings"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateConfigurationSettings">, unknown, Parameters<Requests["updateConfigurationSettings"]>[0], unknown>(payload => requests.updateConfigurationSettings(payload), config?.useUpdateConfigurationSettings, options),
        useUpdateConfiguration: (options?: Omit<UseMutationOptions<Response<"updateConfiguration">, unknown, Parameters<Requests["updateConfiguration"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateConfiguration">, unknown, Parameters<Requests["updateConfiguration"]>[0], unknown>(payload => requests.updateConfiguration(payload), config?.useUpdateConfiguration, options),
        useCreateDayLimitArrangement: (options?: Omit<UseMutationOptions<Response<"createDayLimitArrangement">, unknown, Parameters<Requests["createDayLimitArrangement"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createDayLimitArrangement">, unknown, Parameters<Requests["createDayLimitArrangement"]>[0], unknown>(payload => requests.createDayLimitArrangement(payload), config?.useCreateDayLimitArrangement, options),
        useUpdateDayLimitArrangement: (options?: Omit<UseMutationOptions<Response<"updateDayLimitArrangement">, unknown, Parameters<Requests["updateDayLimitArrangement"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateDayLimitArrangement">, unknown, Parameters<Requests["updateDayLimitArrangement"]>[0], unknown>(payload => requests.updateDayLimitArrangement(payload), config?.useUpdateDayLimitArrangement, options),
        useDeleteDayLimitArrangement: (options?: Omit<UseMutationOptions<Response<"deleteDayLimitArrangement">, unknown, Parameters<Requests["deleteDayLimitArrangement"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteDayLimitArrangement">, unknown, Parameters<Requests["deleteDayLimitArrangement"]>[0], unknown>(payload => requests.deleteDayLimitArrangement(payload), config?.useDeleteDayLimitArrangement, options),
        useCreateDayLimitArrangementForEmployee: (options?: Omit<UseMutationOptions<Response<"createDayLimitArrangementForEmployee">, unknown, Parameters<Requests["createDayLimitArrangementForEmployee"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createDayLimitArrangementForEmployee">, unknown, Parameters<Requests["createDayLimitArrangementForEmployee"]>[0], unknown>(payload => requests.createDayLimitArrangementForEmployee(payload), config?.useCreateDayLimitArrangementForEmployee, options),
        useUpdateDayLimitArrangementForEmployee: (options?: Omit<UseMutationOptions<Response<"updateDayLimitArrangementForEmployee">, unknown, Parameters<Requests["updateDayLimitArrangementForEmployee"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateDayLimitArrangementForEmployee">, unknown, Parameters<Requests["updateDayLimitArrangementForEmployee"]>[0], unknown>(payload => requests.updateDayLimitArrangementForEmployee(payload), config?.useUpdateDayLimitArrangementForEmployee, options),
        useDeleteDayLimitArrangementForEmployee: (options?: Omit<UseMutationOptions<Response<"deleteDayLimitArrangementForEmployee">, unknown, Parameters<Requests["deleteDayLimitArrangementForEmployee"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteDayLimitArrangementForEmployee">, unknown, Parameters<Requests["deleteDayLimitArrangementForEmployee"]>[0], unknown>(payload => requests.deleteDayLimitArrangementForEmployee(payload), config?.useDeleteDayLimitArrangementForEmployee, options),
        useCreateDayLimitViolationPayOut: (options?: Omit<UseMutationOptions<Response<"createDayLimitViolationPayOut">, unknown, Parameters<Requests["createDayLimitViolationPayOut"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createDayLimitViolationPayOut">, unknown, Parameters<Requests["createDayLimitViolationPayOut"]>[0], unknown>(payload => requests.createDayLimitViolationPayOut(payload), config?.useCreateDayLimitViolationPayOut, options),
        useCreateDeclarationType: (options?: Omit<UseMutationOptions<Response<"createDeclarationType">, unknown, Parameters<Requests["createDeclarationType"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createDeclarationType">, unknown, Parameters<Requests["createDeclarationType"]>[0], unknown>(payload => requests.createDeclarationType(payload), config?.useCreateDeclarationType, options),
        useDeleteDeclarationType: (id: string, options?: Omit<UseMutationOptions<Response<"deleteDeclarationType">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteDeclarationType">, unknown, unknown, unknown>(() => requests.deleteDeclarationType(id), config?.useDeleteDeclarationType, options),
        useUpdateDeclarationType: (options?: Omit<UseMutationOptions<Response<"updateDeclarationType">, unknown, Parameters<Requests["updateDeclarationType"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateDeclarationType">, unknown, Parameters<Requests["updateDeclarationType"]>[0], unknown>(payload => requests.updateDeclarationType(payload), config?.useUpdateDeclarationType, options),
        useAskEmployeeForDuty: (options?: Omit<UseMutationOptions<Response<"askEmployeeForDuty">, unknown, Parameters<Requests["askEmployeeForDuty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"askEmployeeForDuty">, unknown, Parameters<Requests["askEmployeeForDuty"]>[0], unknown>(payload => requests.askEmployeeForDuty(payload), config?.useAskEmployeeForDuty, options),
        useAskEmployeesForDuty: (options?: Omit<UseMutationOptions<Response<"askEmployeesForDuty">, unknown, Parameters<Requests["askEmployeesForDuty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"askEmployeesForDuty">, unknown, Parameters<Requests["askEmployeesForDuty"]>[0], unknown>(payload => requests.askEmployeesForDuty(payload), config?.useAskEmployeesForDuty, options),
        useUpdateDutyAskRoundSettings: (options?: Omit<UseMutationOptions<Response<"updateDutyAskRoundSettings">, unknown, Parameters<Requests["updateDutyAskRoundSettings"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateDutyAskRoundSettings">, unknown, Parameters<Requests["updateDutyAskRoundSettings"]>[0], unknown>(payload => requests.updateDutyAskRoundSettings(payload), config?.useUpdateDutyAskRoundSettings, options),
        useCreateDutyCalculationValueSleepDuty: (options?: Omit<UseMutationOptions<Response<"createDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["createDutyCalculationValueSleepDuty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["createDutyCalculationValueSleepDuty"]>[0], unknown>(payload => requests.createDutyCalculationValueSleepDuty(payload), config?.useCreateDutyCalculationValueSleepDuty, options),
        useDeleteDutyCalculationValueSleepDuty: (options?: Omit<UseMutationOptions<Response<"deleteDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["deleteDutyCalculationValueSleepDuty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["deleteDutyCalculationValueSleepDuty"]>[0], unknown>(payload => requests.deleteDutyCalculationValueSleepDuty(payload), config?.useDeleteDutyCalculationValueSleepDuty, options),
        useUpdateDutyCalculationValueSleepDuty: (options?: Omit<UseMutationOptions<Response<"updateDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["updateDutyCalculationValueSleepDuty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateDutyCalculationValueSleepDuty">, unknown, Parameters<Requests["updateDutyCalculationValueSleepDuty"]>[0], unknown>(payload => requests.updateDutyCalculationValueSleepDuty(payload), config?.useUpdateDutyCalculationValueSleepDuty, options),
        useCreateDutyCode: (options?: Omit<UseMutationOptions<Response<"createDutyCode">, unknown, Parameters<Requests["createDutyCode"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createDutyCode">, unknown, Parameters<Requests["createDutyCode"]>[0], unknown>(payload => requests.createDutyCode(payload), config?.useCreateDutyCode, options),
        useUpdateDutyCode: (options?: Omit<UseMutationOptions<Response<"updateDutyCode">, unknown, Parameters<Requests["updateDutyCode"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateDutyCode">, unknown, Parameters<Requests["updateDutyCode"]>[0], unknown>(payload => requests.updateDutyCode(payload), config?.useUpdateDutyCode, options),
        useDeleteDutyCode: (options?: Omit<UseMutationOptions<Response<"deleteDutyCode">, unknown, Parameters<Requests["deleteDutyCode"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteDutyCode">, unknown, Parameters<Requests["deleteDutyCode"]>[0], unknown>(payload => requests.deleteDutyCode(payload), config?.useDeleteDutyCode, options),
        useRecalculateCostCalculationExternalSurcharge: (options?: Omit<UseMutationOptions<Response<"recalculateCostCalculationExternalSurcharge">, unknown, Parameters<Requests["recalculateCostCalculationExternalSurcharge"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"recalculateCostCalculationExternalSurcharge">, unknown, Parameters<Requests["recalculateCostCalculationExternalSurcharge"]>[0], unknown>(payload => requests.recalculateCostCalculationExternalSurcharge(payload), config?.useRecalculateCostCalculationExternalSurcharge, options),
        useCreateCostCalculationExternalSurcharges: (options?: Omit<UseMutationOptions<Response<"createCostCalculationExternalSurcharges">, unknown, Parameters<Requests["createCostCalculationExternalSurcharges"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createCostCalculationExternalSurcharges">, unknown, Parameters<Requests["createCostCalculationExternalSurcharges"]>[0], unknown>(payload => requests.createCostCalculationExternalSurcharges(payload), config?.useCreateCostCalculationExternalSurcharges, options),
        useUpdateCostCalculationExternalSurcharges: (options?: Omit<UseMutationOptions<Response<"updateCostCalculationExternalSurcharges">, unknown, Parameters<Requests["updateCostCalculationExternalSurcharges"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateCostCalculationExternalSurcharges">, unknown, Parameters<Requests["updateCostCalculationExternalSurcharges"]>[0], unknown>(payload => requests.updateCostCalculationExternalSurcharges(payload), config?.useUpdateCostCalculationExternalSurcharges, options),
        useCreateCostCalculationExternalSurchargeSettings: (options?: Omit<UseMutationOptions<Response<"createCostCalculationExternalSurchargeSettings">, unknown, Parameters<Requests["createCostCalculationExternalSurchargeSettings"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createCostCalculationExternalSurchargeSettings">, unknown, Parameters<Requests["createCostCalculationExternalSurchargeSettings"]>[0], unknown>(payload => requests.createCostCalculationExternalSurchargeSettings(payload), config?.useCreateCostCalculationExternalSurchargeSettings, options),
        useUpdateCostCalculationExternalSurchargeSettings: (options?: Omit<UseMutationOptions<Response<"updateCostCalculationExternalSurchargeSettings">, unknown, Parameters<Requests["updateCostCalculationExternalSurchargeSettings"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateCostCalculationExternalSurchargeSettings">, unknown, Parameters<Requests["updateCostCalculationExternalSurchargeSettings"]>[0], unknown>(payload => requests.updateCostCalculationExternalSurchargeSettings(payload), config?.useUpdateCostCalculationExternalSurchargeSettings, options),
        useUpdateEmployeeTransportationType: (options?: Omit<UseMutationOptions<Response<"updateEmployeeTransportationType">, unknown, Parameters<Requests["updateEmployeeTransportationType"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateEmployeeTransportationType">, unknown, Parameters<Requests["updateEmployeeTransportationType"]>[0], unknown>(payload => requests.updateEmployeeTransportationType(payload), config?.useUpdateEmployeeTransportationType, options),
        useCreateEmploymentAdditionalProperty: (options?: Omit<UseMutationOptions<Response<"createEmploymentAdditionalProperty">, unknown, Parameters<Requests["createEmploymentAdditionalProperty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createEmploymentAdditionalProperty">, unknown, Parameters<Requests["createEmploymentAdditionalProperty"]>[0], unknown>(payload => requests.createEmploymentAdditionalProperty(payload), config?.useCreateEmploymentAdditionalProperty, options),
        useDeleteEmploymentAdditionalProperty: (options?: Omit<UseMutationOptions<Response<"deleteEmploymentAdditionalProperty">, unknown, Parameters<Requests["deleteEmploymentAdditionalProperty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteEmploymentAdditionalProperty">, unknown, Parameters<Requests["deleteEmploymentAdditionalProperty"]>[0], unknown>(payload => requests.deleteEmploymentAdditionalProperty(payload), config?.useDeleteEmploymentAdditionalProperty, options),
        useUpdateEmploymentAdditionalProperty: (options?: Omit<UseMutationOptions<Response<"updateEmploymentAdditionalProperty">, unknown, Parameters<Requests["updateEmploymentAdditionalProperty"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateEmploymentAdditionalProperty">, unknown, Parameters<Requests["updateEmploymentAdditionalProperty"]>[0], unknown>(payload => requests.updateEmploymentAdditionalProperty(payload), config?.useUpdateEmploymentAdditionalProperty, options),
        useProcessEndOfEmploymentSettlement: (options?: Omit<UseMutationOptions<Response<"processEndOfEmploymentSettlement">, unknown, Parameters<Requests["processEndOfEmploymentSettlement"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"processEndOfEmploymentSettlement">, unknown, Parameters<Requests["processEndOfEmploymentSettlement"]>[0], unknown>(payload => requests.processEndOfEmploymentSettlement(payload), config?.useProcessEndOfEmploymentSettlement, options),
        useApproveExpenseClaims: (options?: Omit<UseMutationOptions<Response<"approveExpenseClaims">, unknown, Parameters<Requests["approveExpenseClaims"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"approveExpenseClaims">, unknown, Parameters<Requests["approveExpenseClaims"]>[0], unknown>(payload => requests.approveExpenseClaims(payload), config?.useApproveExpenseClaims, options),
        useCreateExpenseClaim: (options?: Omit<UseMutationOptions<Response<"createExpenseClaim">, unknown, Parameters<Requests["createExpenseClaim"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createExpenseClaim">, unknown, Parameters<Requests["createExpenseClaim"]>[0], unknown>(payload => requests.createExpenseClaim(payload), config?.useCreateExpenseClaim, options),
        useRevokeExpenseClaim: (id: string, options?: Omit<UseMutationOptions<Response<"revokeExpenseClaim">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"revokeExpenseClaim">, unknown, unknown, unknown>(() => requests.revokeExpenseClaim(id), config?.useRevokeExpenseClaim, options),
        useRejectExpenseClaims: (options?: Omit<UseMutationOptions<Response<"rejectExpenseClaims">, unknown, Parameters<Requests["rejectExpenseClaims"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"rejectExpenseClaims">, unknown, Parameters<Requests["rejectExpenseClaims"]>[0], unknown>(payload => requests.rejectExpenseClaims(payload), config?.useRejectExpenseClaims, options),
        useUpdateExpenseClaim: (options?: Omit<UseMutationOptions<Response<"updateExpenseClaim">, unknown, Parameters<Requests["updateExpenseClaim"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateExpenseClaim">, unknown, Parameters<Requests["updateExpenseClaim"]>[0], unknown>(payload => requests.updateExpenseClaim(payload), config?.useUpdateExpenseClaim, options),
        useCreateExpenseClaimsBasedOnRoutes: (startDate: string, endDate: string, options?: Omit<UseMutationOptions<Response<"createExpenseClaimsBasedOnRoutes">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"createExpenseClaimsBasedOnRoutes">, unknown, unknown, unknown>(() => requests.createExpenseClaimsBasedOnRoutes(startDate, endDate), config?.useCreateExpenseClaimsBasedOnRoutes, options),
        useSyncExternalFlexRequests: (options?: Omit<UseMutationOptions<Response<"syncExternalFlexRequests">, unknown, Parameters<Requests["syncExternalFlexRequests"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"syncExternalFlexRequests">, unknown, Parameters<Requests["syncExternalFlexRequests"]>[0], unknown>(payload => requests.syncExternalFlexRequests(payload), config?.useSyncExternalFlexRequests, options),
        useCreatePeriodAssignment: (options?: Omit<UseMutationOptions<Response<"createPeriodAssignment">, unknown, Parameters<Requests["createPeriodAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createPeriodAssignment">, unknown, Parameters<Requests["createPeriodAssignment"]>[0], unknown>(payload => requests.createPeriodAssignment(payload), config?.useCreatePeriodAssignment, options),
        useUpdatePeriodAssignment: (options?: Omit<UseMutationOptions<Response<"updatePeriodAssignment">, unknown, Parameters<Requests["updatePeriodAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updatePeriodAssignment">, unknown, Parameters<Requests["updatePeriodAssignment"]>[0], unknown>(payload => requests.updatePeriodAssignment(payload), config?.useUpdatePeriodAssignment, options),
        useConnectPeriodAssignment: (options?: Omit<UseMutationOptions<Response<"connectPeriodAssignment">, unknown, Parameters<Requests["connectPeriodAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"connectPeriodAssignment">, unknown, Parameters<Requests["connectPeriodAssignment"]>[0], unknown>(payload => requests.connectPeriodAssignment(payload), config?.useConnectPeriodAssignment, options),
        useInProgressPeriodAssignment: (options?: Omit<UseMutationOptions<Response<"inProgressPeriodAssignment">, unknown, Parameters<Requests["inProgressPeriodAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"inProgressPeriodAssignment">, unknown, Parameters<Requests["inProgressPeriodAssignment"]>[0], unknown>(payload => requests.inProgressPeriodAssignment(payload), config?.useInProgressPeriodAssignment, options),
        useReturnPeriodAssignment: (options?: Omit<UseMutationOptions<Response<"returnPeriodAssignment">, unknown, Parameters<Requests["returnPeriodAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"returnPeriodAssignment">, unknown, Parameters<Requests["returnPeriodAssignment"]>[0], unknown>(payload => requests.returnPeriodAssignment(payload), config?.useReturnPeriodAssignment, options),
        useGetPlanning: (options?: Omit<UseMutationOptions<Response<"getPlanning">, unknown, Parameters<Requests["getPlanning"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"getPlanning">, unknown, Parameters<Requests["getPlanning"]>[0], unknown>(payload => requests.getPlanning(payload), config?.useGetPlanning, options),
        useCreateProject: (options?: Omit<UseMutationOptions<Response<"createProject">, unknown, Parameters<Requests["createProject"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createProject">, unknown, Parameters<Requests["createProject"]>[0], unknown>(payload => requests.createProject(payload), config?.useCreateProject, options),
        useUpdateProject: (options?: Omit<UseMutationOptions<Response<"updateProject">, unknown, Parameters<Requests["updateProject"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateProject">, unknown, Parameters<Requests["updateProject"]>[0], unknown>(payload => requests.updateProject(payload), config?.useUpdateProject, options),
        usePostRegionalPoolEmployeeTenantAgreements: (options?: Omit<UseMutationOptions<Response<"postRegionalPoolEmployeeTenantAgreements">, unknown, Parameters<Requests["postRegionalPoolEmployeeTenantAgreements"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"postRegionalPoolEmployeeTenantAgreements">, unknown, Parameters<Requests["postRegionalPoolEmployeeTenantAgreements"]>[0], unknown>(payload => requests.postRegionalPoolEmployeeTenantAgreements(payload), config?.usePostRegionalPoolEmployeeTenantAgreements, options),
        usePutRegionalPoolEmployeeTenantAgreementsId: (employeeTenantAgreementId: number, options?: Omit<UseMutationOptions<Response<"putRegionalPoolEmployeeTenantAgreementsId">, unknown, Parameters<Requests["putRegionalPoolEmployeeTenantAgreementsId"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"putRegionalPoolEmployeeTenantAgreementsId">, unknown, Parameters<Requests["putRegionalPoolEmployeeTenantAgreementsId"]>[0], unknown>(payload => requests.putRegionalPoolEmployeeTenantAgreementsId(payload, employeeTenantAgreementId), config?.usePutRegionalPoolEmployeeTenantAgreementsId, options),
        useUpdateRegionalPoolFunctionMapping: (regionalPoolTenantName: string, tenantFunctionId: number, options?: Omit<UseMutationOptions<Response<"updateRegionalPoolFunctionMapping">, unknown, Parameters<Requests["updateRegionalPoolFunctionMapping"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateRegionalPoolFunctionMapping">, unknown, Parameters<Requests["updateRegionalPoolFunctionMapping"]>[0], unknown>(payload => requests.updateRegionalPoolFunctionMapping(payload, regionalPoolTenantName, tenantFunctionId), config?.useUpdateRegionalPoolFunctionMapping, options),
        useUpdateRegionalPoolQualificationMapping: (regionalPoolTenantName: string, tenantQualificationId: string, options?: Omit<UseMutationOptions<Response<"updateRegionalPoolQualificationMapping">, unknown, Parameters<Requests["updateRegionalPoolQualificationMapping"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateRegionalPoolQualificationMapping">, unknown, Parameters<Requests["updateRegionalPoolQualificationMapping"]>[0], unknown>(payload => requests.updateRegionalPoolQualificationMapping(payload, regionalPoolTenantName, tenantQualificationId), config?.useUpdateRegionalPoolQualificationMapping, options),
        useCreateResource: (options?: Omit<UseMutationOptions<Response<"createResource">, unknown, Parameters<Requests["createResource"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createResource">, unknown, Parameters<Requests["createResource"]>[0], unknown>(payload => requests.createResource(payload), config?.useCreateResource, options),
        useUpdateResource: (options?: Omit<UseMutationOptions<Response<"updateResource">, unknown, Parameters<Requests["updateResource"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateResource">, unknown, Parameters<Requests["updateResource"]>[0], unknown>(payload => requests.updateResource(payload), config?.useUpdateResource, options),
        useConnectSchedule: (options?: Omit<UseMutationOptions<Response<"connectSchedule">, unknown, Parameters<Requests["connectSchedule"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"connectSchedule">, unknown, Parameters<Requests["connectSchedule"]>[0], unknown>(payload => requests.connectSchedule(payload), config?.useConnectSchedule, options),
        useBulkConnectSchedules: (options?: Omit<UseMutationOptions<Response<"bulkConnectSchedules">, unknown, Parameters<Requests["bulkConnectSchedules"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"bulkConnectSchedules">, unknown, Parameters<Requests["bulkConnectSchedules"]>[0], unknown>(payload => requests.bulkConnectSchedules(payload), config?.useBulkConnectSchedules, options),
        useConnectSelfMatchDuties: (options?: Omit<UseMutationOptions<Response<"connectSelfMatchDuties">, unknown, Parameters<Requests["connectSelfMatchDuties"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"connectSelfMatchDuties">, unknown, Parameters<Requests["connectSelfMatchDuties"]>[0], unknown>(payload => requests.connectSelfMatchDuties(payload), config?.useConnectSelfMatchDuties, options),
        useRespondSelfMatchPeriodAssignments: (options?: Omit<UseMutationOptions<Response<"respondSelfMatchPeriodAssignments">, unknown, Parameters<Requests["respondSelfMatchPeriodAssignments"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"respondSelfMatchPeriodAssignments">, unknown, Parameters<Requests["respondSelfMatchPeriodAssignments"]>[0], unknown>(payload => requests.respondSelfMatchPeriodAssignments(payload), config?.useRespondSelfMatchPeriodAssignments, options),
        useUndoChanges: (options?: Omit<UseMutationOptions<Response<"undoChanges">, unknown, Parameters<Requests["undoChanges"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"undoChanges">, unknown, Parameters<Requests["undoChanges"]>[0], unknown>(payload => requests.undoChanges(payload), config?.useUndoChanges, options),
        useUpdateTransportationType: (options?: Omit<UseMutationOptions<Response<"updateTransportationType">, unknown, Parameters<Requests["updateTransportationType"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateTransportationType">, unknown, Parameters<Requests["updateTransportationType"]>[0], unknown>(payload => requests.updateTransportationType(payload), config?.useUpdateTransportationType, options),
        useCorrectTravelMovementPerDay: (options?: Omit<UseMutationOptions<Response<"correctTravelMovementPerDay">, unknown, Parameters<Requests["correctTravelMovementPerDay"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"correctTravelMovementPerDay">, unknown, Parameters<Requests["correctTravelMovementPerDay"]>[0], unknown>(payload => requests.correctTravelMovementPerDay(payload), config?.useCorrectTravelMovementPerDay, options),
        useCreateTravelSet: (options?: Omit<UseMutationOptions<Response<"createTravelSet">, unknown, Parameters<Requests["createTravelSet"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createTravelSet">, unknown, Parameters<Requests["createTravelSet"]>[0], unknown>(payload => requests.createTravelSet(payload), config?.useCreateTravelSet, options),
        useUpdateTravelSet: (options?: Omit<UseMutationOptions<Response<"updateTravelSet">, unknown, Parameters<Requests["updateTravelSet"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateTravelSet">, unknown, Parameters<Requests["updateTravelSet"]>[0], unknown>(payload => requests.updateTravelSet(payload), config?.useUpdateTravelSet, options),
        useCreateFavorite: (options?: Omit<UseMutationOptions<Response<"createFavorite">, unknown, Parameters<Requests["createFavorite"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createFavorite">, unknown, Parameters<Requests["createFavorite"]>[0], unknown>(payload => requests.createFavorite(payload), config?.useCreateFavorite, options),
        useDeleteFavorite: (options?: Omit<UseMutationOptions<Response<"deleteFavorite">, unknown, Parameters<Requests["deleteFavorite"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteFavorite">, unknown, Parameters<Requests["deleteFavorite"]>[0], unknown>(payload => requests.deleteFavorite(payload), config?.useDeleteFavorite, options),
        useCreateWorkPlace: (options?: Omit<UseMutationOptions<Response<"createWorkPlace">, unknown, Parameters<Requests["createWorkPlace"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createWorkPlace">, unknown, Parameters<Requests["createWorkPlace"]>[0], unknown>(payload => requests.createWorkPlace(payload), config?.useCreateWorkPlace, options),
        useUpdateWorkPlace: (options?: Omit<UseMutationOptions<Response<"updateWorkPlace">, unknown, Parameters<Requests["updateWorkPlace"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateWorkPlace">, unknown, Parameters<Requests["updateWorkPlace"]>[0], unknown>(payload => requests.updateWorkPlace(payload), config?.useUpdateWorkPlace, options),
        useCreateWorkPlaceAtOrganizationalUnitAssignment: (options?: Omit<UseMutationOptions<Response<"createWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["createWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"createWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["createWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>(payload => requests.createWorkPlaceAtOrganizationalUnitAssignment(payload), config?.useCreateWorkPlaceAtOrganizationalUnitAssignment, options),
        useUpdateWorkPlaceAtOrganizationalUnitAssignment: (options?: Omit<UseMutationOptions<Response<"updateWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["updateWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"updateWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["updateWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>(payload => requests.updateWorkPlaceAtOrganizationalUnitAssignment(payload), config?.useUpdateWorkPlaceAtOrganizationalUnitAssignment, options),
        useDeleteWorkPlaceAtOrganizationalUnitAssignment: (entityId: string, options?: Omit<UseMutationOptions<Response<"deleteWorkPlaceAtOrganizationalUnitAssignment">, unknown, unknown, unknown>, "mutationFn">) => useRapiniMutation<Response<"deleteWorkPlaceAtOrganizationalUnitAssignment">, unknown, unknown, unknown>(() => requests.deleteWorkPlaceAtOrganizationalUnitAssignment(entityId), config?.useDeleteWorkPlaceAtOrganizationalUnitAssignment, options),
        useSortWorkPlaceAtOrganizationalUnitAssignment: (options?: Omit<UseMutationOptions<Response<"sortWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["sortWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>, "mutationFn">) => useRapiniMutation<Response<"sortWorkPlaceAtOrganizationalUnitAssignment">, unknown, Parameters<Requests["sortWorkPlaceAtOrganizationalUnitAssignment"]>[0], unknown>(payload => requests.sortWorkPlaceAtOrganizationalUnitAssignment(payload), config?.useSortWorkPlaceAtOrganizationalUnitAssignment, options)
    } as const;
}
